import { type Translation } from "../hooks/useTranslations";
import type { NoticeParams } from "../pages/engines/lockCalculator/model/noticeParams";
import { type UILanguageCode } from "../types";

const de: Translation = {
  languageCode: "de" as UILanguageCode,
  projects: {
    all: "Alle Projekte",
    new: "Neues Projekt",
    singular: "Projekt",
    plural: "Projekte",
    my: "Meine Projekte",
    createNew: "Neues Projekt erstellen",
    delete: "Projekt löschen",
    search: "Projekte suchen",
    shareWithUser: "Projekt mit Benutzer teilen",
    editPermissions: "Berechtigungen bearbeiten",
    editSharings: "Freigaben bearbeiten",
    invitations: "Projekteinladungen",

    invitationStatus: {
      pending: "Ausstehend",
      accepted: "Akzeptiert",
      rejected: "Abgelehnt",
      cancelled: "Abgebrochen",
      statusChanges: {
        accept: "Einladung erfolgreich akzeptiert.",
        rejected: "Einladung abgelehnt.",
      },
    },

    invitePage: {
      badge: "Projekteinladung zum Zugriff",
      fallbackAuthorName: "ein Teammitglied",
      collaborationLead:
        "hat Sie eingeladen, an diesem Projekt mitzuarbeiten und die freigegebenen Inhalte einzusehen.",
      invitedBy: "Eingeladen von",
      invitationSentTo: "Einladung gesendet an",
      projectName: "Projektname",
      description: "Beschreibung",
      inviteNoLongerAvailable:
        "Diese Einladung ist nicht mehr verfügbar, weil sie bereits angenommen, abgelehnt oder abgebrochen wurde.",
      acceptSignedIn: "Wenn Sie diese Einladung annehmen, wird das Projekt sofort geöffnet.",
      acceptSignedOut:
        "Wenn Sie diese Einladung annehmen, müssen Sie sich zuerst anmelden, bevor Sie Zugriff auf das Projekt erhalten.",
      acceptAction: "Einladung annehmen",
      rejectAction: "Einladung ablehnen",
    },

    questions: {
      delete: "Möchten Sie dieses Projekt wirklich löschen?",
      unshare: "Möchten Sie das Projekt wirklich für diesen Benutzer nicht mehr teilen?",
    },

    errors: {
      deleteProjectWithConfigurationsNotPossible:
        "Es ist nicht möglich, ein Projekt zu löschen, wenn Konfigurationen vorhanden sind.",
      deleteProjectWithDocumentsNotPossible:
        "Es ist nicht möglich, ein Projekt zu löschen, wenn Dokumente vorhanden sind.",
      notFound: "Projekt nicht gefunden.",
      sharingNotFound: "Freigabe nicht gefunden.",
      sharingAlreadyExists: "Das Projekt ist bereits für diesen Benutzer freigegeben.",
      documentNotFound: "Dokument nicht gefunden.",
      missingDocumentName: "Dokumentenname fehlt.",
      documentAlreadyPresent: "Dokument bereits vorhanden.",
      missingProjectStatus: "Projektstatus fehlt.",
      inviteReadNotAllowed: "Sie sind nicht berechtigt, diese Einladung anzusehen.",
      inviteInsertNotAllowed: "Sie sind nicht berechtigt, eine Einladung für dieses Projekt zu senden.",
      inviteUpdateNotAllowed: "Sie sind nicht berechtigt, diese Einladung anzunehmen oder abzulehnen.",
      inviteDeleteNotAllowed: "Sie sind nicht berechtigt, diese Einladung zu löschen.",
      inviteAlreadyExists: "Für diesen Benutzer existiert bereits eine Einladung.",
      inviteNotFound: "Einladung nicht gefunden.",
      inviteAlreadyCancelled: "Diese Einladung wurde bereits abgebrochen.",
      inviteAlreadyAccepted: "Diese Einladung wurde bereits akzeptiert.",
      inviteAlreadyRejected: "Diese Einladung wurde bereits abgelehnt.",
    },
  },

  calculations: {
    all: "Alle Berechnungen",
    new: "Neue Berechnung",
    plural: "Berechnungen",
    create: "Berechnung erstellen",
    createNew: "Neue Berechnung erstellen",
    delete: "Berechnung löschen",
    duplicate: "Berechnung duplizieren",
    my: "Meine Berechnungen",
    search: "Berechnungen suchen",
    reset: "Berechnung zurücksetzen",
    newCalculationName: "Name der neuen Berechnung",
    shareWithUser: "Berechnungen mit Benutzer teilen",
    editSharings: "Freigaben bearbeiten",
    invitations: "Berechnungseinladungen",
    invitePage: {
      badge: "Einladung zum Zugriff auf eine Berechnung",
      collaborationLead: "hat Sie eingeladen, an dieser Berechnung mitzuwirken und die geteilten Inhalte einzusehen.",
      calculationName: "Name der Berechnung",
      inviteNoLongerAvailable:
        "Diese Einladung ist nicht mehr verfügbar, da sie bereits angenommen, abgelehnt oder storniert wurde.",
      acceptSignedIn: "Durch Annahme dieser Einladung wird die Berechnung sofort geöffnet.",
      acceptSignedOut:
        "Durch Annahme dieser Einladung werden Sie aufgefordert, sich anzumelden, bevor der Zugriff auf die Berechnung gewährt wird.",
    },

    questions: {
      delete: "Möchten Sie diese Berechnung wirklich löschen?",
      unshare: "Möchten Sie die Berechnung wirklich für diesen Benutzer nicht mehr teilen?",
      reset: "Möchten Sie wirklich alle Daten der aktuellen Berechnung zurücksetzen?",
    },

    errors: {
      notFound: "Berechnung nicht gefunden.",
      sharingNotFound: "Freigabe nicht gefunden.",
      sharingAlreadyExists: "Die Berechnung ist bereits für diesen Benutzer freigegeben.",
      moduleNotSupported: "Berechnungsmodul wird nicht unterstützt.",
      moduleRestricted: "Dieses Modul erlaubt kein Erstellen, Bearbeiten oder Duplizieren von Berechnungen.",
      inviteInsertNotAllowed: "Sie dürfen für diese Berechnung keine Einladung senden.",
      inviteUpdateNotAllowed: "Sie dürfen diese Einladung nicht annehmen oder ablehnen.",
      inviteDeleteNotAllowed: "Sie dürfen diese Einladung nicht löschen.",
      inviteAlreadyExists: "Für diesen Benutzer existiert bereits eine Einladung.",
      inviteNotFound: "Einladung nicht gefunden.",
      inviteAlreadyCancelled: "Diese Einladung wurde bereits storniert.",
      inviteAlreadyAccepted: "Diese Einladung wurde bereits angenommen.",
      inviteAlreadyRejected: "Diese Einladung wurde bereits abgelehnt.",
    },
  },

  sharingRequests: {
    title: "Zugriffsanfragen",
    requestedAt: "Angefragt am",
    message: "Nachricht",
    accept: "Annehmen",
    reject: "Ablehnen",
    acceptTitle: "Zugriffsanfrage annehmen",
    rejectQuestion: "Möchten Sie diese Zugriffsanfrage wirklich ablehnen?",
    accepted: "Zugriffsanfrage angenommen.",
    rejected: "Zugriffsanfrage abgelehnt.",
    notShared: {
      projectTitle: "Projekt nicht geteilt",
      calculationTitle: "Berechnung nicht geteilt",
      projectText: "Dieses Projekt wurde nicht mit Ihnen geteilt, daher können Sie nicht darauf zugreifen.",
      calculationText: "Diese Berechnung wurde nicht mit Ihnen geteilt, daher können Sie nicht darauf zugreifen.",
      requestText: "Sie können den Eigentümer bitten, sie mit Ihnen zu teilen: Er erhält Ihre Anfrage per E-Mail.",
      messageLabel: "Nachricht an den Eigentümer",
      request: "Zugriff anfragen",
      sent: "Ihre Zugriffsanfrage wurde gesendet.",
      pending: (date: string) =>
        `Sie haben am ${date} Zugriff angefragt: Sie erhalten eine E-Mail, sobald der Eigentümer antwortet.`,
    },
    managePage: {
      projectTitle: "Zugriffsanfrage für ein Projekt",
      calculationTitle: "Zugriffsanfrage für eine Berechnung",
      requestedBy: "Angefragt von",
      project: "Projekt",
      calculation: "Berechnung",
      projectLead: "bittet Sie um Zugriff auf dieses Projekt.",
      calculationLead: "bittet Sie um Zugriff auf diese Berechnung.",
      alreadyAccepted: "Diese Anfrage wurde bereits angenommen.",
      alreadyRejected: "Diese Anfrage wurde bereits abgelehnt.",
    },
    errors: {
      notFound: "Zugriffsanfrage nicht gefunden.",
      readNotAllowed: "Sie sind nicht berechtigt, diese Zugriffsanfrage anzuzeigen.",
      alreadyExists: "Sie haben bereits Zugriff angefragt: Der Eigentümer hat noch nicht geantwortet.",
      alreadyShared: "Es wurde bereits mit Ihnen geteilt: Laden Sie die Seite neu.",
      alreadyAccepted: "Diese Zugriffsanfrage wurde bereits angenommen.",
      alreadyRejected: "Diese Zugriffsanfrage wurde bereits abgelehnt.",
    },
  },

  notifications: {
    title: "Benachrichtigungen",
    projectSharingRequest: (user: string, project: string) =>
      `${user} bittet um Zugriff auf das Projekt ${project}`,
    calculationSharingRequest: (user: string, calculation: string) =>
      `${user} bittet um Zugriff auf die Berechnung ${calculation}`,
    projectSharingInvite: (user: string, project: string) =>
      `${user} hat Sie zum Projekt ${project} eingeladen`,
    calculationSharingInvite: (user: string, calculation: string) =>
      `${user} hat Sie zur Berechnung ${calculation} eingeladen`,
  },

  users: {
    add: "Benutzer hinzufügen",
    all: "Alle Benutzer",
    plural: "Benutzer",
    remove: "Benutzer entfernen",
    invite: "Benutzer einladen",
    search: "Benutzer suchen",
    usersEmail: "Benutzer-E-Mail",
    usersName: "Benutzername",

    questions: {},

    pending: {
      plural: "Ausstehende Benutzer",
      edit: "Ausstehenden Benutzer bearbeiten",
      delete: "Ausstehenden Benutzer entfernen",
      createdAt: "Hinzugefügt",
      info: "Hat diese E-Mail noch kein Konto, bleibt der Benutzer bis zu seiner ersten Anmeldung ausstehend und erhält dann diese Rollen und Berechtigungen.",
    },

    errors: {
      notFound: "Benutzer nicht gefunden.",
      alreadyExists: "Ein Benutzer mit dieser E-Mail existiert bereits.",
      pendingAlreadyExists: "Ein ausstehender Benutzer mit dieser E-Mail existiert bereits.",
    },
  },

  items: {
    all: "Alle Elemente",
    singular: "Element",
    plural: "Elemente",
    createNew: "Neues Element erstellen",
    delete: "Element löschen",
    search: "Elemente suchen",
    list: "Elementliste",

    questions: {
      delete: "Möchten Sie dieses Element wirklich löschen?",
    },

    errors: {
      notFound: "Element nicht gefunden.",
      doesNotExist: "Artikel mit diesem Code existiert nicht.",
      missingCode: "Elementcode fehlt.",
    },
  },

  clients: {
    all: "Alle Clients",
    plural: "Clients",
    createNew: "Neuen Client erstellen",
    delete: "Client löschen",
    search: "Client suchen",

    questions: {
      delete: "Möchten Sie diesen Client wirklich löschen?",
    },

    errors: {
      notFound: "Client nicht gefunden.",
      insertNotAllowed: "Sie sind nicht berechtigt, einen neuen Client einzufügen.",
      readNotAllowed: "Sie sind nicht berechtigt, diesen Client zu lesen.",
      updateNotAllowed: "Sie sind nicht berechtigt, diesen Client zu aktualisieren.",
      deleteNotAllowed: "Sie sind nicht berechtigt, diesen Client zu löschen.",
    },
  },

  accessControl: {
    title: "Zugriffskontrolle",
    plural: "Zugriffskontrolle",
    disclaimer:
      "Die Zugriffskontrolle legt fest, welche Benutzer jede Anwendung nutzen können. Sie hebt das Geoblocking nicht auf: Ein erlaubter Benutzer wird in den Ländern, in denen die Anwendung oder eines ihrer Module per Geoblocking gesperrt ist, trotzdem abgewiesen. Um einem Benutzer den Zugriff von überall zu ermöglichen, weisen Sie ihm die Berechtigung geo_blocking:bypass zu (z. B. mit der Rolle geo-blocking-bypass): Sie hebt jedes Geoblocking auf, für alle Anwendungen und Module.",
    applications: "Anwendungen",
    modules: "Module",
    modulesDisclaimer:
      "Die Zugriffskontrolle eines Moduls gilt nur in der Hauptanwendung: Die eigene Anwendung des Moduls, falls vorhanden, folgt ihrem eigenen Tab. Ein vom Modul ausgeschlossener Benutzer sieht weder das Modul noch seine Berechnungen und kann sie nicht öffnen.",

    settings: {
      lockSwitchLabel: "Anwendung auf autorisierte Benutzer beschränken",
      lockSwitchDescription:
        "Wenn aktiviert, können nur Benutzer mit einer 'erlaubt'-Regel oder Administratorzugriff die Anwendung nutzen.",
      confirmEnable: "Möchten Sie die Anwendung wirklich auf autorisierte Benutzer beschränken?",
      confirmDisable: "Möchten Sie wirklich wieder allen authentifizierten Benutzern Zugriff gewähren?",
    },

    // the modules' tab: a module's lock switch, in the main application only
    moduleSettings: {
      lockSwitchLabel: "Modul auf autorisierte Benutzer beschränken",
      lockSwitchDescription:
        "Wenn aktiviert, können nur Benutzer mit einer 'erlaubt'-Regel oder Administratorzugriff das Modul in der Hauptanwendung nutzen.",
      confirmEnable: "Möchten Sie das Modul wirklich auf autorisierte Benutzer beschränken?",
      confirmDisable: "Möchten Sie wirklich wieder allen authentifizierten Benutzern Zugriff gewähren?",
    },

    userAccess: {
      notAllowed: "Nicht erlaubt",
      causes: {
        rule: "Regel",
        default: "Standard",
        admin: "Administrator",
        locked: "App eingeschränkt",
      },
    },

    rules: {
      plural: "Zugriffsregeln",
      app: "Anwendung",
      createNew: "Regel hinzufügen",
      search: "Nach E-Mail oder Grund suchen",
      delete: "Regel löschen",
      email: "E-Mail",
      user: "Benutzer",
      status: "Status",
      statusAllowed: "Erlaubt",
      statusBlocked: "Gesperrt",
      reason: "Grund",
      createdAt: "Erstellt am",
      import: "Importieren",
      importTitle: "Regeln importieren",
      importFrom: "Importieren aus",
      currentStatus: "Aktueller Status",
      selectAll: "Alle auswählen",
      importHint:
        "Bereits vorhandene Regeln mit einem anderen Status sind nicht ausgewählt: Wählen Sie sie aus, um den aktuellen Status und Grund zu überschreiben.",

      questions: {
        delete: "Möchten Sie diese Zugriffsregel wirklich löschen?",
      },
    },

    forbiddenTitle: "Nicht erlaubt",
    forbiddenText: "Sie sind nicht berechtigt, auf diese Anwendung zuzugreifen.",
    signedInAs: "Angemeldet als",

    errors: {
      forbidden: "Sie sind nicht berechtigt, auf diese Anwendung zuzugreifen.",
      ruleNotFound: "Zugriffsregel nicht gefunden.",
      ruleMissingEmail: "E-Mail ist erforderlich.",
      ruleInvalidStatus: "Der Status muss entweder erlaubt oder gesperrt sein.",
      ruleCannotTargetSelf: "Sie können keine Zugriffsregel für sich selbst hinzufügen.",
      settingsReadNotAllowed: "Sie sind nicht berechtigt, die Zugriffseinstellungen zu lesen.",
      settingsUpdateNotAllowed: "Sie sind nicht berechtigt, die Zugriffseinstellungen zu aktualisieren.",
      ruleReadNotAllowed: "Sie sind nicht berechtigt, Zugriffsregeln zu lesen.",
      ruleInsertNotAllowed: "Sie sind nicht berechtigt, eine Zugriffsregel zu erstellen.",
      ruleDeleteNotAllowed: "Sie sind nicht berechtigt, eine Zugriffsregel zu löschen.",
      lastAdmin:
        "Sie können nicht den letzten Benutzer entfernen, der die Zugriffskontrolle der Anwendung verwalten kann.",
      invalidScope: "Unbekannter Zugriffsbereich.",
      invalidModule: "Unbekanntes Modul.",
    },
  },

  geoBlocking: {
    plural: "Geoblocking",
    disclaimer:
      "Das Geoblocking legt fest, aus welchen Ländern jede Anwendung oder ein Modul genutzt werden kann. Die Regeln eines Moduls gelten überall, wo das Modul genutzt wird, in der Hauptanwendung und in seiner eigenen; die Regeln einer Anwendung nur für diese Anwendung. Ohne Regeln sind alle Länder erlaubt; sobald eine Regel 'Erlaubt' ist, nur die erlaubten Länder. Die Zugriffskontrolle hebt es nicht auf: Um einem Benutzer den Zugriff von überall zu ermöglichen, weisen Sie ihm die Berechtigung geo_blocking:bypass zu (z. B. mit der Rolle geo-blocking-bypass).",
    settings: "Regionen und Länder",
    modules: "Module",
    rules: {
      singular: "Regel",
      target: "Region oder Land",
      type: "Typ",
      region: "Region",
      country: "Land",
      status: "Status",
      statusAllowed: "Erlaubt",
      statusBlocked: "Gesperrt",
      createdAt: "Erstellt",
    },
    regions: {
      plural: "Regionen",
      singular: "Region",
      countries: "Länder",
    },
    countries: {
      plural: "Länder",
      code: "Code",
    },
    errors: {
      moduleBlocked: "Sie haben keinen Zugriff auf dieses Modul.",
      ruleAlreadyExists: "Für diese Region oder dieses Land gibt es bereits eine Regel.",
      regionNameAlreadyExists: "Es gibt bereits eine Region mit diesem Namen.",
      regionInUse: "Die Region wird von einer Geoblocking-Regel verwendet.",
      blocksSelf: "Diese Änderung würde Ihren eigenen Zugriff auf die Anwendung sperren.",
    },
  },

  usageStatistics: {
    plural: "Nutzungsstatistiken",
    from: "Von",
    to: "Bis",
    apps: "Anwendungen",
    visits: "Besuche",
    description: "Anzahl der verschiedenen Benutzer pro Tag (UTC).",
    errors: {
      readNotAllowed: "Sie sind nicht berechtigt, die Nutzungsstatistiken anzuzeigen.",
      invalidPeriod: "Wählen Sie einen gültigen Zeitraum von höchstens einem Jahr.",
    },
  },

  helpRequests: {
    title: "Hilfe",
    dialogTitle: "Hilfe anfordern",
    intro: "Beschreiben Sie Ihr Problem: Unser technisches Beratungsteam antwortet Ihnen per E-Mail.",
    type: "Typ",
    subject: "Betreff",
    message: "Nachricht",
    screenshot: "Screenshot",
    attachScreenshot: "Screenshot der aktuellen Seite anhängen",
    screenshotUnavailable: "Der Screenshot der Seite konnte nicht erstellt werden.",
    attachments: "Anhänge",
    attachmentsHint: (maxFiles: number, maxMegabytes: number) =>
      `Bis zu ${maxFiles} Dateien, insgesamt ${maxMegabytes} MB.`,
    removeAttachment: "Anhang entfernen",
    diagnosticsInfo:
      "Technische Informationen zu Ihrem Browser und die letzten Fehler der Seite werden angehängt, damit wir das Problem analysieren können.",
    send: "Senden",
    success: "Ihre Anfrage wurde gesendet. Sie erhalten eine Bestätigung per E-Mail.",

    errors: {
      invalidForm: "Die Anfrage konnte nicht gelesen werden oder überschreitet die zulässige Größe.",
      missingSubject: "Der Betreff ist erforderlich.",
      subjectTooLong: "Der Betreff ist zu lang.",
      missingMessage: "Die Nachricht ist erforderlich.",
      messageTooLong: "Die Nachricht ist zu lang.",
      tooManyAttachments: (maxFiles: number) => `Sie können bis zu ${maxFiles} Dateien anhängen.`,
      attachmentsTooLarge: (maxMegabytes: number) => `Die Anhänge überschreiten insgesamt ${maxMegabytes} MB.`,
      invalidScreenshot: "Der Screenshot ist ungültig.",
      invalidDiagnostics: "Die technischen Informationen sind ungültig.",
    },
  },

  feedbacks: {
    title: "An Entwickler melden",
    dialogTitle: "Feedback senden",
    intro: "Melden Sie einen Fehler oder teilen Sie einen Vorschlag: Das Entwicklungsteam erhält ihn.",
    types: {
      bug: "Fehler",
      suggestion: "Vorschlag",
      other: "Sonstiges",
    },
    success: "Vielen Dank, Ihr Feedback wurde gesendet.",

    errors: {
      sendNotAllowed: "Sie dürfen kein Feedback senden.",
      invalidType: "Wählen Sie einen gültigen Feedback-Typ.",
    },
  },

  home: {
    greeting: (name: string) => `Hallo, ${name}`,
    subtitle: "Wählen Sie einen Rechner, um eine neue Berechnung zu starten.",
    availableCalculators: "Verfügbare Rechner",
  },

  modules: {
    all: "Alle Module",
    plural: "Module",
    createNew: "Neues Modul erstellen",
    delete: "Modul löschen",
    search: "Module suchen",
    singular: "Modul",

    questions: {
      delete: "Möchten Sie dieses Modul wirklich löschen?",
    },

    errors: {
      notFound: "Modul nicht gefunden.",
      insertNotAllowed: "Sie haben keine Berechtigung, un ein neues Modul zu erstellen.",
      readNotAllowed: "Sie haben keine Berechtigung, dieses Modul zu lesen.",
      updateNotAllowed: "Sie haben keine Berechtigung, dieses Modul zu bearbeiten.",
      deleteNotAllowed: "Sie haben keine Berechtigung, dieses Modul zu löschen.",
    },
  },

  categories: {
    all: "Alle Kategorien",
    plural: "Kategorien",
    createNew: "Neue Kategorie erstellen",
    delete: "Kategorie löschen",
    search: "Kategorien suchen",
    view: "Kategorien anzeigen",
    edit: "Kategorie bearbeiten",

    questions: {
      delete: "Möchten Sie diese Kategorie wirklich löschen?",
    },

    errors: {
      notFound: "Kategorie nicht gefunden.",
      insertNotAllowed: "Sie haben keine Berechtigung, um eine neue Kategorie zu erstellen.",
      readNotAllowed: "Sie haben keine Berechtigung, diese Kategorie zu lesen.",
      updateNotAllowed: "Sie haben keine Berechtigung, diese Kategorie zu bearbeiten.",
      deleteNotAllowed: "Sie haben keine Berechtigung, diese Kategorie zu löschen.",
    },
  },

  roles: {
    plural: "Rollen",
  },

  permissions: {
    plural: "Berechtigungen",
  },

  secrets: {
    plural: "Secrets",

    errors: {
      missingValue: "Secretwert fehlt.",
    },
  },

  adminDashboard: {
    projects: "Projekte aller Benutzer anzeigen und verwalten",
    calculations: "Berechnungen aller Benutzer anzeigen und verwalten",
    modules: "Berechnungsmodule und deren Einstellungen konfigurieren",
    categories: "Kategorien zur Gruppierung von Modulen verwalten",
    items: "Artikelkatalog verwalten",
    clients: "API-Clients, deren Secrets und Berechtigungen verwalten",
    users: "Benutzer und deren Rollen verwalten",
    accessControl: "Anwendungen einschränken und zugelassene oder gesperrte Benutzer verwalten",
    usageStatistics: "Sehen Sie, wie viele Benutzer täglich auf die Anwendungen und Module zugreifen",
    geoBlocking: "Anwendungen und Module nach Land erlauben oder sperren",
  },

  common: {
    date: "Datum",
    active: "Aktiv",
    add: "Hinzufügen",
    errorInvalidVisibility: "Ungültige Sichtbarkeit: Wählen Sie öffentlich, intern oder versteckt.",
    administration: "Administration",
    all: "Alle",
    alreadyPresent: "Bereits vorhanden",
    angle: "Winkel",
    angles: "Winkel",
    archived: "Archiviert",
    author: "Autor",
    automaticSync: "Automatische Synchronisierung",
    back: "Zurück",
    baseHeight: "Grundhöhe",
    cancel: "Abbrechen",
    categories: "Kategorien",
    category: "Kategorie",
    clamped: "Geklemmt",
    thickness: "Dicke",
    close: "Schließen",
    code: "Code",
    clearPoints: "Punkte löschen",
    undo: "Rückgängig",
    components: "Komponenten",
    confirmed: "Bestätigt",
    coordinates: "Koordinaten",
    copy: "Kopieren",
    country: "Land",
    create: "Erstellen",
    created: "Erstellt",
    createNew: "Neu erstellen",
    curve: "Kurve",
    customer: "Kunde",
    dataHasBeenSavedSuccessfully: "Daten wurden erfolgreich gespeichert.",
    default: "Standard",
    degrees: "Grad",
    delete: "Löschen",
    description: "Beschreibung",
    designer: "Planer",
    details: "Details",
    download: "Herunterladen",
    dragAndDropOrUploadAnImage: "Bild ziehen und ablegen oder klicken um eines hochladen",
    draw: "Zeichnen",
    dropIt: "Hochladen!",
    duplicate: "Duplizieren",
    edit: "Bearbeiten",
    editor: "Editor",
    email: "E-Mail",
    error: "Fehler",
    errorLoadingData: "Fehler beim Laden der Daten",
    extensions: "Erweiterungen",
    factor: "Faktor",
    gap: "Fuge",
    failed: "Fehlgeschlagen",
    fieldContainsInvalidValueInsertBiggerOne: "Das Feld enthält einen ungültigen Wert, geben Sie einen größeren ein.",
    fieldInvalidValue: "Das Feld enthält einen ungültigen Wert.",
    colorHexInvalid: "Bitte geben Sie eine gültige HEX-Farbe im Format #RRGGBB ein.",
    finish: "Fertig",
    fixed: "Fest",
    forbidden: "Nicht erlaubt",
    from: "Von",
    generalData: "Allgemeine Daten",
    genericError: "Ein Fehler ist aufgetreten, bitte versuchen Sie es erneut.",
    groovingMinHeight: "Min. Nuthöhe",
    groovingMaxHeight: "Max. Nuthöhe",
    groovingThickness: "Nutstärke",
    height: "Höhe",
    help: "Hilfe",
    hidden: "Hidden",
    hide: "Ausblenden",
    home: "Startseite",
    installed: "Installiert",
    intermediate: "Zwischenstück",
    isVisible: "Sichtbar",
    languages: {
      arabic: "Arabisch",
      chinese: "Chinesisch",
      croatian: "Kroatisch",
      czech: "Tschechisch",
      danish: "Dänisch",
      dutch: "Niederländisch",
      english: "Englisch",
      estonian: "Estnisch",
      finnish: "Finnisch",
      french: "Französisch",
      german: "Deutsch",
      greek: "Griechisch",
      hungarian: "Ungarisch",
      icelandic: "Isländisch",
      italian: "Italienisch",
      japanese: "Japanisch",
      korean: "Koreanisch",
      latvian: "Lettisch",
      lithuanian: "Litauisch",
      norwegian: "Norwegisch",
      plural: "Sprachen",
      polish: "Polnisch",
      portugese: "Portugiesisch",
      russian: "Russisch",
      singular: "Sprache",
      slovak: "Slowakisch",
      slovenian: "Slowenisch",
      spanish: "Spanisch",
      swedish: "Schwedisch",
      turkish: "Türkisch",
    },
    translations: "Übersetzungen",
    lastAccess: "Letzter Zugriff",
    lastModified: "Zuletzt geändert",
    lastName: "Nachname",
    lastSync: "Letzte Synchronisierung",
    length: "Länge",
    screwDiameter: "Schraubendurchmesser",
    screwHeadHeight: "Schraubenkopfhöhe",
    loading: "Lädt...",
    location: "Ort",
    login: "Anmelden",
    logout: "Abmelden",
    loop: "Schleife",
    mappings: "Zuordnungen",
    max: "Max",
    maxHeight: "Max. Höhe",
    meters: "Meter",
    min: "Min",
    minHeight: "Min. Höhe",
    minimumOrderQuantity: "Mindestbestellmenge",
    minOrderQuantity: "Min. Bestellm.",
    minValue: "Min. Wert",
    minWidth: "Min. Breite",
    myProfile: "Mein Profil",
    name: "Name",
    never: "nie",
    next: "Weiter",
    no: "Nein",
    noItemsFound: "Keine Elemente gefunden",
    notFound: "Nicht gefunden",
    offer: "Angebot",
    ok: "Ok",
    onlyImagesAreAllowed: "Nur Bilder sind erlaubt.",
    open: "Öffnen",
    optional: "optional",
    order: "Reihenfolge",
    overwrite: "Überschreiben",
    owner: "Eigentümer",
    pitch: "Neigung",
    print: "Drucken",
    public: "Öffentlich",
    everyOne: "Alle",
    remove: "Entfernen",
    removeImage: "Bild entfernen",
    reset: "Zurücksetzen",
    resetToDefault: "Auf Standard zurücksetzen",
    resetView: "Ansicht zurücksetzen",
    resourceNotFound: "Die angeforderte Ressource wurde nicht gefunden.",
    roof: "Dach",
    rothoblaas: "Rothoblaas",
    rothoblaasUsers: "Rothoblaas-Benutzer",
    save: "Speichern",
    search: "Suchen",
    select: "Auswählen",
    selectProjectInwhichCreateCopy: "Wählen Sie das Projekt aus, in dem die Kopie erstellt werden soll",
    sentAt: "Gesendet am",
    sessionExpired: "Sitzung abgelaufen",
    settings: "Einstellungen",
    shared: "Geteilt",
    sharedByMe: "Von mir geteilt",
    sharedWithMe: "Mit mir geteilt",
    canEdit: "Kann bearbeiten",
    viewOnly: "Nur ansehen",
    sharedViaProject: "Über das Projekt geteilt",
    shape: "Form",
    show: "Anzeigen",
    span: "Spannweite",
    spans: "Spannweiten",
    status: "Status",
    stripeLength: "Streifenlänge",
    substructureHeight: "Unterkonstruktionshöhe",
    substructures: "Unterkonstruktionen",
    support: "Halterung",
    supportHeight: "Halterungshöhe",
    supports: "Halterungen",
    sync: "Synchronisieren",
    testEnvironment: "Testumgebung",
    to: "Bis",
    toBeInstalled: "Zu installieren",
    toInspect: "Zu prüfen",
    type: "Typ",
    unlock: "Entsperren",
    update: "Aktualisieren",
    value: "Wert",
    visible: "Sichtbar",
    visibility: "Sichtbarkeit",
    unitSystem: "Einheitensystem",
    unitSystemMetric: "Metrisch",
    unitSystemImperial: "Imperial",
    width: "Breite",
    quantity: "Menge",
    yes: "Ja",
    chooseSecondPointDifferentXY: "Wählen Sie einen zweiten Punkt mit anderer x- und y-Koordinate.",
    zoomIn: "Vergrößern",
    zoomOut: "Verkleinern",
    youAreNotAllowed: "Sie sind nicht berechtigt auf diese Ressource zuzugreifen.",
    area: "Fläche",
    units: "Einheiten",
    volume: "Volumen",
    preview: "Vorschau",
    summary: "Zusammenfassung",

    unitM: "m",
    unitCm: "cm",
    unitGPa: "GPa",
    unitMm: "mm",
    unitInch: "in",
    unitN: "N",
    unitNMm: "N·mm",
    unitDeg: "°",
    unitPcs: "Stk",
    unitM2: "m²",
    unitM4: "m^4",
    unitKn: "kN",
    unitKnM2: "kN/m²",
    unitKnM: "kN/m",
    unitMpa: "MPa",
    titlePreview: "Vorschau",
    wasteFactor: "Abfallfaktor",

    cookiePolicy: "Cookie-Richtlinie",
    privacyPolicy: "Datenschutzrichtlinie",
    termsAndConditions: "Geschäftsbedingungen",
    writePermissions: "Schreibberechtigungen",
    invitationHasBeenSentSuccessfully: "Einladung erfolgreich gesendet.",
    resendInvite: "Einladung erneut senden",
    cancelInviteQuestion: "Möchten Sie diese Einladung wirklich stornieren?",
    deleteInviteQuestion: "Möchten Sie diese Einladung wirklich löschen?",
    invite: "Einladen",
    singleOptionAvailable:
      "Feld deaktiviert: Für die gewählte Konfiguration ist nur eine auswählbare Option verfügbar.",
  },

  concreteResinModule: {
    addCalculation: "Berechnung speichern",
    anchorMapping: "Ankerzuordnung",
    anchorMappings: "Ankerzuordnungen",
    cartridges: "Kartuschen",
    chemicalAnchor: "Chemischer Anker",
    chemicalAnchors: "Chemische Anker",
    downloadPdf: "PDF herunterladen",
    downloadPdfNoCalculationsSavedHint: "Speichern Sie mindestens eine Berechnung, um das PDF herunterzuladen.",
    holeDepth: "Bohrlochtiefe ( h₁ )",
    holeDiameter: "Bohrlochdurchmesser ( d₀ )",
    holeDiameterImperial: "US-Bohrer [in]",
    holeDiameterImperialInfo:
      'Aus dem Bohrlochdurchmesser berechnet: der kleinste 1/64"-Bohrer, der nicht kleiner ist.',
    holeNumbers: "Anzahl Bohrlöcher",
    holes: "Löcher",
    instruction2:
      "Diese Berechnung schätzt die Harzmenge basierend auf dem gewählten Übermaßfaktor. Das Übermaß hängt häufig von Anwendung und Erfahrung des Installateurs ab.",
    instructionsSummaryPrefix:
      "Die Harzmenge wird anhand der in der Produkt-ETA angegebenen Bohrlochfüllung geschätzt: vom Grund des gereinigten Bohrlochs ausgehend das Bohrloch zu etwa zwei Dritteln mit Klebstoff füllen und die statische Mischdüse beim Füllen langsam zurückziehen, um Lufteinschlüsse zu vermeiden; erreicht die Düse den Bohrlochgrund nicht, eine passende Verlängerungsdüse verwenden. Produkt gemäß der Rothoblaas-Dokumentation für den vorgesehenen Verwendungszweck anwenden und injizieren. Rothoblaas behält sich das Recht vor, Berechnungsmethoden, Designs oder Modelle ohne Vorankündigung oder Haftung zu ändern; die aktuellsten Bemessungsressourcen finden Sie auf",
    instructionsSummarySuffix: ".",
    liters: "Liter",
    mlQuantity: "ml-Menge",
    noCalculationsSaved: "Keine Berechnungen gespeichert",
    overageFactor: "Verschnittfaktor",
    rodDiameter: "Stabdurchmesser ( d )",
    titleCalculationsSaved: "Berechnungen gespeichert",
    titleHoleGeometry: "Bohrlochgeometrie",
    titleInstructionsAndNotes: "Anweisungen und Hinweise",
    titleSummary: "Zusammenfassung",
    totalVolumeRequired: "Gesamtvolumen erforderlich",
    unitOverageFactor: "%",
    unitSystemImperial: "Imperial",
    unitSystemMetric: "Metrisch",
    waste: "Verschnitt",
    cartridgesShort: "Kart.",
    unitLiters: "L",
    errors: {
      anchorInUseByMapping: "Der Anker wird von einer Zuordnung verwendet: Löschen Sie zuerst seine Zuordnungen",
      anchorInUseByEntry:
        "Der Anker wird in gespeicherten Berechnungen verwendet: Setzen Sie ihn auf Hidden, statt ihn zu löschen",
      anchorNotFound: "Der chemische Anker wurde nicht gefunden.",
      anchorNotAvailable: "Der gewählte chemische Anker ist nicht mehr verfügbar: Wählen Sie einen anderen.",
      anchorAlreadyPresent: "Der Artikel ist bereits ein chemischer Anker.",
      mappingAlreadyPresent: "Der chemische Anker hat bereits eine Zuordnung mit diesen Durchmessern.",
      invalidRodDiameter: "Der Stangendurchmesser ist für den gewählten chemischen Anker nicht verfügbar.",
      invalidHoleDiameter: "Der Bohrlochdurchmesser passt nicht zum gewählten Stangendurchmesser.",
      mappingNotFound: "Die Ankerzuordnung wurde nicht gefunden.",
      entryMissingChemicalAnchor: "Wählen Sie vor dem Speichern der Berechnung einen chemischen Anker",
      entryIncompleteCalculation: "Füllen Sie vor dem Speichern der Berechnung alle Felder aus",
    },
  },

  usLiftingCalculatorModule: {
    calculator: {
      aboveTarget: (target: number) =>
        `Der Hub liegt innerhalb der Tragfähigkeit, überschreitet aber das Ziel von ${target} %.`,
      allSavedMembersCleared: "Alle gespeicherten Bauteile wurden gelöscht.",
      allowAnotherDevice:
        "Ein anderes Gerät zulassen, wenn mit dem gewählten Gerät keine Konfiguration nachgewiesen wird.",
      alternateDevice: "Keine nachgewiesene Konfiguration mit dem gewählten Hebemittel verfügbar.",
      anchorPointsDetail: "Anzahl Anschlagpunkte",
      angleDiagramAlt: "Hebewinkelbereiche des gewählten Geräts und der gewählten Hubart",
      angleDiagramTitle: "Hebewinkelbereiche",
      angleFromHorizontal: "Winkel zur Horizontalen",
      angleLegendTitle: "Legende der Winkelbereiche",
      anglePhotoTitle: "Hebewinkel",
      applicableLifts: "Zugeordnete Hübe",
      automatic: "Automatisch",
      backToModeSelection: "Zurück zur Modusauswahl",
      beamDepth: "Trägerhöhe",
      beamDepthDetail: "Trägerhöhe",
      beamOrientationAlt: (orientation: string) => `RAPTOR am Träger: ${orientation}`,
      beamSpecifications: (depth: string, width: string) => `Höhe ${depth} × Breite ${width}`,
      beamWidth: "Trägerbreite",
      beamWidthDetail: "Trägerbreite",
      buildingPdf: "PDF wird erstellt…",
      capacityTablePreview: "Vorschau der Tragfähigkeit eines Hebegeräts",
      capacityUtilization: "Tragfähigkeitsausnutzung",
      clearSavedMembers: "Gespeicherte Bauteile löschen",
      clearSavedMembersConfirm:
        "Möchten Sie wirklich alle gespeicherten Bauteile löschen? Dadurch werden gespeicherte Hübe in allen Modi entfernt; dies kann nicht rückgängig gemacht werden.",
      clearSavedMembersTitle: "Gespeicherte Bauteile löschen?",
      cltPanelDepthDetail: "BSP-Plattendicke",
      cltThickness: "BSP-Dicke",
      columnSpecifications: (width: string) => `Breite ${width}`,
      columnWidth: "Stützenbreite",
      columnWidthDetail: "Stützenbreite",
      completedExcelTemplate: "Ausgefüllte Excel-Vorlage",
      contactTechSupport: "Technischen Support kontaktieren",
      contactUs: "Kontakt",
      copied: "Kopiert",
      copyFailed: "Kopieren fehlgeschlagen",
      copyTable: "Tabelle kopieren",
      customLiftDesign: "Benutzerdefinierte Hebeplanung",
      datasheetNoticeText:
        "Die Einsatzbedingungen, Montageanweisungen, Tragfähigkeiten der Verbindungsmittel, zugelassenen Hebeausrichtungen sowie alle geltenden Anforderungen und Einschränkungen sind dem technischen Datenblatt des jeweiligen Hebemittels zu entnehmen.",
      datasheetNoticeTitle: "Dieses Tool ersetzt nicht die geltenden technischen Datenblätter.",
      decreaseByEighth: "Um 1/8 Zoll verringern",
      deviceFamilyUnavailable: (name: string) => `${name} — Nicht verfügbar`,
      deviceImageAlt: "Gewähltes Hebegerät",
      devices: "Hebegeräte",
      dimensionFormatError: "Maß in Zoll eingeben, z. B. 4 1/8.",
      downloadExcel: "Excel herunterladen",
      downloadTemplate: "Vorlage herunterladen",
      downloaded: "Heruntergeladen",
      draftOpened: "Entwurf geöffnet",
      eServiceTemplateRequired: "Freigegebene eService-Vorlage erforderlich",
      eServiceXlsx: "eService XLSX",
      editingMember: (name: string) => `${name} bearbeiten`,
      engineeringReviewRequired: "Technische Prüfung erforderlich",
      enlargeImage: "Bild vergrößern",
      enterLiftName: "Geben Sie einen Hubnamen ein.",
      enterNameOrSave: "Bauteilnamen eingeben oder Hub speichern wählen, um ihn im Dialog zu benennen.",
      enterWeightBeforeSaving: "Vor dem Speichern das Gewicht des gehobenen Bauteils eingeben.",
      enterWeightForRecommendation: "Das Gewicht des gehobenen Bauteils eingeben, um eine Empfehlung zu erhalten.",
      export: "Exportieren",
      exportFailed: "Export fehlgeschlagen",
      exportFilledExcel: "Ausgefüllte Excel-Datei exportieren",
      exportHelp: "Wählen Sie, was in den PDF-Bericht aufgenommen wird.",
      exportMemberExcel: "Bauteil-Excel exportieren",
      exportReport: "Bericht exportieren",
      fastenerTakeoff: "Befestiger-Mengenermittlung",
      guidanceApplication: "Anwendung",
      guidanceInstallation: (device: string) => `${device} Montage`,
      guidanceLabel: "Montagehinweise",
      guidanceLoadDirections: "Zulässige Lastrichtungen",
      guidanceMinimumDistances: "Mindestabstände",
      guidanceMinimumUnavailable: "Gültige Trägerbreite für die Mindestabstände eingeben.",
      howRecommendationsSelected: "So werden Empfehlungen ausgewählt",
      increaseByEighth: "Um 1/8 Zoll erhöhen",
      installation: "Montageart",
      installationIllustrations: "Montageabbildungen",
      installationIllustrationsTitle: "WASP / WASPL - Montageabbildungen",
      installationLine: (installation: string) => `Montageart: ${installation}`,
      installationType: "Montageart",
      installationTypeColumn: "Montageart",
      installationTypes: {
        inclined: "Geneigt",
        perpendicular: "Senkrecht mit Fräsung",
        standard: "Standard",
      },
      issues: {
        angleUnsupported: "Einen unterstützten Hebewinkel wählen. Der eingegebene Winkel wurde nicht ersetzt.",
        beamDepthMaximum: (maximum: string) => `Die Trägerhöhe darf ${maximum} nicht überschreiten.`,
        beamDepthMinimum: (minimum: string) =>
          `Die Trägerhöhe muss mindestens ${minimum} betragen. Das eingegebene Maß wurde nicht geändert.`,
        beamDepthPositive: "Die Trägerhöhe muss eine positive Zahl sein. Das eingegebene Maß wurde nicht geändert.",
        beamOrientationRestricted:
          "Die gewählte RAPTOR-Ausrichtung ist durch Trägerbreite und Schraubenanzahl eingeschränkt.",
        beamWidthMaximum: (maximum: string) => `Die Trägerbreite darf ${maximum} nicht überschreiten.`,
        beamWidthMinimum: (minimum: string) =>
          `Die Trägerbreite muss mindestens ${minimum} betragen. Die eingegebene Breite wurde nicht geändert.`,
        beamWidthPositive:
          "Die Trägerbreite muss eine positive Zahl sein. Die eingegebene Breite wurde nicht geändert.",
        cltEdgeGrainMinimum: (minimum: string) =>
          `Die BSP-Dicke muss bei Hebungen an der Plattenkante und beim Aufrichten mindestens ${minimum} betragen.`,
        cltFaceGrainMinimum: (minimum: string, device: string) =>
          `Die BSP-Dicke muss bei ${device}-Hebungen an der Plattenfläche mindestens ${minimum} betragen.`,
        cltSlabMaximum: (maximum: string) => `Die Dicke der BSP-Deckenplatte darf ${maximum} nicht überschreiten.`,
        cltThicknessPositive: "Die BSP-Dicke muss eine positive Zahl sein. Das eingegebene Maß wurde nicht geändert.",
        columnWidthMinimum: (minimum: string) =>
          `Die Stützenbreite muss mindestens ${minimum} betragen. Das eingegebene Maß wurde nicht geändert.`,
        columnWidthPositive:
          "Die Stützenbreite muss eine positive Zahl sein. Das eingegebene Maß wurde nicht geändert.",
        memberDepthMaximum: (maximum: string) => `Das Bauteilmaß darf ${maximum} nicht überschreiten.`,
      },
      liftName: "Hubname",
      liftNamePlaceholder: "Hubnamen eingeben",
      liftNotOverwritten: "Der Hub konnte nicht überschrieben werden.",
      liftNotSaved: "Der Hub konnte nicht gespeichert werden.",
      liftOverwritten: (name: string) =>
        `${name} wurde mit den aktuellen Eigenschaften und der Empfehlung überschrieben.`,
      liftRecommendation: "Hebeempfehlung",
      liftSaved: (name: string) => `${name} wurde mit den aktuellen Eigenschaften und der Empfehlung gespeichert.`,
      liftType: "Hubart",
      liftTypeFallbackNote:
        "Für das Aufrichten ist keine nachgewiesene Konfiguration verfügbar. Als Alternative wird die vertikale Hebung an der Plattenfläche gezeigt; damit ist kein Aufrichten nachgewiesen.",
      liftedMemberWeight: "Gewicht des gehobenen Bauteils",
      liftingDevice: "Hebegerät",
      liftingPoints: "Hebepunkte",
      liftingSetup: "Hebeanordnung",
      liftingSetupValue: (screws: number | string, devices: number | string) =>
        `${screws} Schrauben × ${devices} Geräte`,
      marginLine: (margin: string) => `Reserve: ${margin}`,
      memberLoaded: (name: string) => `${name} wurde geladen.`,
      memberName: "Bauteilname",
      memberNamePlaceholder: "Bauteilnamen eingeben",
      memberSpecifications: "Bauteilangaben",
      memberType: "Bauteiltyp",
      memberTypes: {
        beam: "Träger",
        "clt-slab": "CLT Slab",
        "clt-wall": "CLT Wall",
        column: "Stütze",
      },
      memberWeight: "Bauteilgewicht",
      memberWorkspace: "Bauteil-Arbeitsbereich",
      minimumDistancesCaption: (mounting: string, screwCount: number | string) =>
        `${mounting} · Schrauben je Hebegerät: ${screwCount}`,
      mode1Description: "Einen bestimmten Hebevorgang konfigurieren und prüfen.",
      mode1Pill: "Modus 1",
      mode2Description: "Bauteil eingeben und geeignete Hebeoptionen finden.",
      mode2Eyebrow: "Modus 2: Hebeempfehlung",
      mode2Pill: "Modus 2",
      mode3Description: "Mehrere Bauteile hochladen und Hebeempfehlungen erhalten.",
      mode3Pill: "Modus 3",
      modeSelection: "Modusauswahl",
      moduleTitle: "US Lifting Calculator",
      nameInUse: (name: string) =>
        `Ein gespeicherter Hub heißt bereits „${name}“. Überschreiben Sie ihn oder geben Sie einen anderen Namen ein.`,
      newMember: "Neues Bauteil",
      noCapacitySubtitle: "Die ausgewählte Kombination ist in den eingebetteten Übersichtstabellen nicht aufgeführt.",
      noCapacityTable: "Für diese Konfiguration ist keine Tragfähigkeitstabelle angegeben.",
      noSavedLifts: "Keine gespeicherten Hübe verfügbar.",
      noSavedMembers: "Keine gespeicherten Bauteile",
      noSavedMembersToProcess: "Zuerst ein Bauteil in Modus 1 oder 2 speichern.",
      noSavedRecommendationLifts: "Es sind keine Hübe aus Modus 2 gespeichert.",
      noScrews: (filters: string[]) => `Keine Schrauben ${filters.join(" und ")}`,
      noScrewsListed: "Keine Schrauben angegeben",
      notAvailableYet: "In einer späteren Version verfügbar.",
      numberOfAnchorPoints: "Anzahl Anschlagpunkte",
      numberOfLiftingDevices: "Anzahl Hebegeräte",
      numberOfRaptors: "Anzahl RAPTOR",
      openEmailEnquiry: "E-Mail-Anfrage öffnen",
      openInMode1: "In Modus 1 öffnen",
      openInMode2: "In Modus 2 öffnen",
      openOrCreateMember: "Bauteil öffnen oder anlegen",
      openSavedSelect:
        "Öffnen Sie jeden gespeicherten Hub, wählen Sie eine tragfähigkeitsverifizierte Option für die Mengenermittlung und speichern Sie erneut.",
      openSupportMenu: "Support-Menü öffnen",
      optionAlternativeTitle: (slot: number, label: string) => `Option ${slot} - ${label} Alternative`,
      optionTitle: (slot: number, label: string) => `Option ${slot} - ${label}`,
      orientation: "Ausrichtung",
      orientationUnavailable: (orientation: string) => `${orientation} - Bei dieser Balkenbreite nicht verfügbar`,
      overwriteExisting: "Vorhandenen überschreiben",
      overwritten: "Überschrieben",
      parallel: "Parallel",
      parallelMounting: "Parallele Montage",
      perpendicular: "Senkrecht",
      perpendicularMounting: "Senkrechte Montage",
      perpendicularWithMillingShort: "Senkr. mit Fräsung",
      printCurrentScreen: "Aktuelle Ansicht drucken",
      printCurrentScreenHelp: "Der angezeigte Hebevorgang mit Ergebnis und Hinweisbildern.",
      printPdf: "PDF drucken",
      printSavedMembers: "Gespeicherte Bauteile drucken",
      printSavedMembersHelp:
        "Jeder gespeicherte Hub wird auf einer eigenen Seite gedruckt, gefolgt von einer gemeinsamen Befestiger-Mengenermittlung, wenn Hübe aus Modus 2 vorhanden sind.",
      processList: "Liste verarbeiten",
      processSavedMembers: "Liste gespeicherter Bauteile verarbeiten",
      productCode: "Produktcode",
      productPage: "Produktseite",
      properties: "Eigenschaften",
      quantityRequired: "Erforderliche Menge",
      raptors: "RAPTOR",
      readyToSave: "Die aktuelle Empfehlung kann gespeichert werden.",
      reasons: {
        noAdditionalVgspl:
          "Mit diesen Einstellungen ist keine weitere nachgewiesene VGSPL-Schraubenanzahl oder -Länge verfügbar.",
        noVgsplAlternative: "Mit diesen Einstellungen ist keine nachgewiesene VGSPL-Alternative verfügbar.",
        notEligible: (family: string) =>
          `Mit diesen Einstellungen ist keine zulässige ${family}-Konfiguration verfügbar.`,
        notWithinCapacity: (family: string) =>
          `Mit diesen Einstellungen liegt keine ${family}-Konfiguration innerhalb der Tragfähigkeit.`,
        waspNoAlternative: (series: string) =>
          `Mit diesen Einstellungen ist keine nachgewiesene ${series}-Alternative mit WASP verfügbar.`,
        waspNotWithinCapacity: (series: string) =>
          `Mit diesen Einstellungen liegt keine ${series}-Konfiguration innerhalb der Tragfähigkeit.`,
      },
      recommendedConfigurations: "Empfohlene Konfigurationen",
      recommendedLiftConfiguration: "Empfohlene Hubkonfiguration",
      requiredFields: (fields: string) => `erforderlich: ${fields}`,
      reset: "Zurücksetzen",
      result: "Ergebnis",
      review: {
        allSupportedDevices: "Alle unterstützten Geräte",
        determinism:
          "Dies ist eine deterministische Tabellensuche, keine laufende KI. Die Auswahl zeigt einzelne Tabellenwinkel; Bereichsdiagramme bleiben Referenzen. Diese Änderung ist keine technische Freigabe.",
        deviceScope: "Geräteumfang dieser Prüfung",
        methodChecks:
          "Vor der Rangfolge Abmessungen, Ausrichtung, Tabellenwinkel, Schraubenlänge und Anzahl prüfen. Fehlende Pflichtangaben verhindern einen Nachweis.",
        methodRaptor:
          "RAPTOR-Hebungen von Trägern und an der Plattenfläche vergleichen VGSPL, eine VGSPL-Alternative mit anderer Schraubenanzahl oder -länge und HBSPL. RAPTOR MINI sowie RAPTOR-Hebungen an der Plattenkante oder beim Aufrichten vergleichen nur VGSPL und HBSPL.",
        methodWasp: "Wenn erlaubt: WASP → WASPL → RAPTOR MINI → RAPTOR.",
        raptorNote: "RAPTOR-Anwendungsbilder und Mindestabstände werden bei den Ergebnissen angezeigt.",
        raptorOnly: "Nur RAPTOR",
        scopeNote: "Der RAPTOR-Umfang begrenzt Auswahl und Suche; er ist keine Validierung.",
      },
      reviewSettings: "Empfehlungen und Prüfeinstellungen",
      riggingBasis:
        "Gesamttragfähigkeit der Anschlagmittel = Anzahl Anschlagpunkte x Tragfähigkeit eines Geräts an der unteren, konservativsten Grenze des gewählten Winkelbereichs (oder genau 90 Grad). Eine gleichmäßige Lastverteilung wird angenommen, aber von diesem Tool nicht bestätigt.",
      riggingMemo:
        "Diese Ausrichtung ist im technischen Datenblatt nicht zugelassen. Anwendungen mit mehr Anschlagpunkten als in den veröffentlichten Konfigurationen sind möglich, wenn sie von einem externen qualifizierten Anschlag-Ingenieur geplant werden.",
      saveLift: "Hub speichern",
      saveMember: "Bauteil speichern",
      saveMode1Lift: "Hub aus Modus 1 speichern",
      saveMode2Lift: "Hub aus Modus 2 speichern",
      saved: "Gespeichert",
      savedLiftConfigurations: "Gespeicherte Hubkonfigurationen",
      savedLiftCount: (count: number) => (count === 1 ? "1 gespeicherter Hub" : `${count} gespeicherte Hübe`),
      savedLiftLoaded:
        "Gespeicherter Hub geladen. Eine Eigenschaft ändern und Hub speichern wählen, um ihn zu aktualisieren.",
      savedLifts: "Gespeicherte Hübe",
      savedLiftsEyebrow: "Gespeicherte Hübe",
      savedLiftsFileSuffix: "gespeicherte-huebe",
      savedMembers: "Gespeicherte Bauteile",
      savedMembersAvailable: (count: number) => `Verfügbare gespeicherte Bauteile: ${count}`,
      savedMembersDescription:
        "Empfehlungen aus gespeicherten Bauteildaten ohne Excel-Upload erstellen. Gespeicherte Hebevorgänge werden nicht geändert.",
      savedOn: (mode: string, date: string) => `${mode} · Gespeichert am ${date}`,
      savedPill: "Gespeichert",
      savedRecommendations: "Gespeicherte Empfehlungen",
      savedResult: "Gespeichertes Ergebnis",
      screw: "Schraube",
      screwCountBeamWidth: (count: number) => `${count} - Bei dieser Balkenbreite nicht verfügbar`,
      screwCountNotListed: (count: number) => `${count} — nicht angegeben`,
      screwLength: "Schraubenlänge (in)",
      screwLengthOption: (length: string, code: string) => `${length} — ${code}`,
      screwLengthPhotoAlt: "Schraubenlänge im Verhältnis zur Bauteildicke",
      screwLengthReferenceText:
        "Bei Hebeanwendungen mit einer Lastkomponente senkrecht zur Holzfaser wird empfohlen, dass die im Holz eingebundene Schraubenlänge ohne die 10 mm RAPTOR-Platte 0,7·d überschreitet (L - 10 mm > 0,7·d; d = Dicke des Holzbauteils), um die maximale Tragfähigkeit des Hebesystems zu erreichen und Risse zu vermeiden. Ist das Verhältnis nicht erfüllt, können die maßgebenden Schub- und Verformungsnachweise nach AWC-NDS, Kapitel 3 und 5, geführt werden.",
      screwLengthReferenceTitle: "Einfluss des Verhältnisses von Schraubenlänge zu Bauteildicke",
      screwLengthViewerTitle: "Referenz zur Schraubenlänge",
      screwType: "Schraubentyp",
      screwsAtLeast: (minimum: string) => `ab ${minimum}`,
      screwsColumn: "Anz. Schrauben",
      screwsLongerThan: (minimum: string) => `länger als ${minimum}`,
      screwsPerDevice: "Schrauben je Hebegerät",
      screwsPerDeviceDetail: "Schrauben je Gerät",
      screwsPerRaptor: "Schrauben je RAPTOR",
      screwsPerRaptorFigure: (count: number) => `${count} Schrauben je RAPTOR`,
      screwsPerRaptorFigureAlt: (count: number) => `RAPTOR mit ${count} Schrauben montiert`,
      screwsRow: (count: number) => `${count} Schrauben`,
      screwsShorterThan: (maximum: string) => `kürzer als ${maximum}`,
      selectCompletedTemplate: "Wählen Sie zum Start eine ausgefüllte Vorlage aus.",
      selectListedScrew: "Vor dem Speichern eine aufgeführte Schraubenkonfiguration wählen.",
      selectNewMember:
        "Wählen Sie Neues Bauteil, um einen Hub zu speichern, oder öffnen Sie ein gespeichertes Bauteil.",
      selectedConfiguration: "Ausgewählte Konfiguration",
      shortScrewEmbedment: "Das aktuelle Verbindungsmittel dringt nicht 0,7*d ein",
      shortScrewRatio: (length: string) =>
        `Die gewählte Schraube ist kürzer als die empfohlene Länge von 0,7d (${length}). Die maßgebenden Schub- und Verformungsnachweise nach AWC-NDS, Kapitel 3 und 5, prüfen. Diese Nachweise werden von diesem Tool nicht geführt.`,
      specificGravity: "Spezifisches Gewicht",
      statusAboveTarget: "Verifiziert - über der Zielausnutzung",
      statusAlternativeLift: "Tragfähigkeit nachgewiesen - alternative Hebung",
      statusDimension: "Maß außerhalb des Bereichs",
      statusEngineeringReview: "Verifiziert — Technische Prüfung erforderlich",
      statusNoCapacity: "Keine Tragfähigkeit angegeben",
      statusNotVerified: "Nicht verifiziert",
      statusOrientation: "Ausrichtung nicht verfügbar",
      statusPending: "Ausstehend",
      statusReview: "Prüfung",
      statusUnavailable: "Nicht verfügbar",
      statusVerified: "Verifiziert",
      submitFeedback: "Feedback senden",
      takeoffEmailBasis: "Berechnungsgrundlage: USA - NDS",
      takeoffEmailIncluded: (count: number) => `Enthaltene gespeicherte Hübe aus Modus 2: ${count}`,
      takeoffEmailRequest: "Bitte prüfen Sie Verfügbarkeit und Preise für die aufgeführten Mengen.",
      takeoffEmailSubject: "US Lifting Calculator - Befestiger-Mengenermittlung",
      takeoffEmailTotal: (total: string) => `Befestiger gesamt: ${total}`,
      takeoffEmailUnits: "Einheiten: imperial (lb / in)",
      takeoffLength: "Länge",
      takeoffTotal: "GESAMT",
      targetUtilization: "Zielausnutzung festlegen (%)",
      targetUtilizationShort: "Zielausnutzung",
      techSupportSubject: "US Lifting Calculator - technischer Support",
      template: "Vorlage",
      templateDownloaded: (device: string) => `${device}-Vorlage in imperialen Einheiten (lb / in) heruntergeladen.`,
      totalCapacity: "Gesamttragfähigkeit",
      totalFasteners: "Befestiger gesamt",
      totalRiggingCapacity: "Gesamttragfähigkeit der Anschlagmittel",
      totalScrews: "Schrauben gesamt",
      unitIn: "in",
      unitLb: "lb",
      upload: {
        columns: {
          anchors: "Anz. Anschlagpunkte",
          angle: "Winkel",
          liftingDevice: "Hebegerät",
          memberName: "Bauteilname",
          memberType: "Bauteiltyp",
          screw: "Schraube",
          screwCount: "Anz. Schrauben",
          status: "Status",
          totalCapacity: "Gesamttragfähigkeit",
          totalScrews: "Schrauben gesamt",
          utilization: "Ausnutzung",
          weight: "Gewicht",
        },
        defaultAngle: "(Standard)",
        enteredTableReference: "Eingegebener Winkel → Tabellenwert",
        externalRigging: "Externe Anschlagplanung erforderlich",
        faceGrainAlternative: "Alternative vertikal an der Plattenfläche; kein Nachweis für das Aufrichten.",
        fields: {
          angle: "Hebewinkel",
          columnNotWithWasp: "Stütze ist mit WASP / WASPL nicht verfügbar",
          columnRequiresRaptorMini: "Stütze erfordert RAPTOR MINI",
          device: "Hebegerät",
          installationType: "Montageart",
          liftType: "Hubart",
          memberType: "Bauteiltyp",
          thicknessDepth: "Dicke / Höhe",
          weight: "Gewicht",
        },
        firstRows: (shown: number, total: number) =>
          `Die ersten ${shown} von ${total} Bauteilen werden angezeigt. Die ausgefüllte Excel-Datei enthält alle Bauteile.`,
        issues: {
          angleColumn: "Das Heben von Stützen erfordert genau 90 Grad zur Horizontalen.",
          angleNotNumeric:
            "Der Hebewinkel muss ein numerischer Winkel zur Horizontalen oder ein angezeigter Winkelbereich des gewählten Geräts sein.",
          angleOutOfRange: (minimum: number) =>
            `Der Hebewinkel muss für das gewählte Gerät zwischen ${minimum} und 90 Grad zur Horizontalen liegen. Der eingegebene Winkel wurde nicht geändert.`,
          angleOutsideBand:
            "Der eingegebene Anschlagwinkel liegt außerhalb des gewählten Tabellenbereichs. Den tatsächlichen Winkel neu eingeben und berechnen; ein anderer Anschlagwinkel ist nicht nachgewiesen.",
          angleRiggingMismatch:
            "Der vorgeschlagene Winkel passt nicht zum eingegebenen Anschlag. Die Eingabe korrigieren, bevor ein Ergebnis verwendet wird.",
          beamWidthRequired: "Für Trägerzeilen ist die Trägerbreite erforderlich.",
          materialConflict: (text: string) =>
            `Materialname und spezifisches Gewicht widersprechen sich in "${text}". Das vorgesehene Material bestätigen; es wurde kein Wert ersetzt.`,
          materialUnrecognized: (text: string) =>
            `Unbekanntes Holzmaterial "${text}". Ein aufgeführtes Material, DF, SYP oder ein unterstütztes numerisches spezifisches Gewicht verwenden.`,
          materialUnsupported: (text: string, choices: string) =>
            `Nicht unterstütztes spezifisches Gewicht "${text}". ${choices} wählen; der eingegebene Wert wurde nicht ersetzt.`,
          orientationInvalid: "Die Ausrichtung muss Parallel oder Senkrecht sein.",
          orientationRestricted: "Die angeforderte RAPTOR-Ausrichtung ist durch die Trägerbreite eingeschränkt.",
          processingFailed: "Dieses Bauteil konnte nicht verarbeitet werden.",
        },
        missing: (fields: string) => `Fehlend oder ungültig: ${fields}.`,
        notices: {
          andMore: (names: string, more: number) => `${names} und ${more} weitere`,
          anglesMapped: (count: number) =>
            `${count === 1 ? "1 Winkel wurde" : `${count} Winkel wurden`} Tabellenbereichen zugeordnet: der untere Wert des Bereichs ist der Tabellenwert. Der eingegebene Anschlagwinkel bleibt unverändert; er muss in diesem Bereich bleiben.`,
          appliesTo: (names: string) => `Gilt für: ${names}.`,
          materialDefault: (value: string, names: string) =>
            `G = ${value} wurde für leere Materialangaben verwendet: ${names}. Das Holzmaterial bestätigen.`,
          vgs13: "VGS13-Schrauben und zugehörige Arbeitslasten gelten ausschließlich für WASPL.",
        },
        processed: (members: number, verified: number, saved: number) =>
          `${members} ${members === 1 ? "Bauteil" : "Bauteile"} verarbeitet. ${verified} mit nachgewiesenen Empfehlungen. ${saved} als Bauteile gespeichert.`,
        processedResults: "Verarbeitete Ergebnisse",
        processedSaved: (members: number, verified: number) =>
          `${members} gespeicherte Bauteile verarbeitet. ${verified} mit verifizierten Empfehlungen. Gespeicherte Hübe bleiben unverändert.`,
        processing: "Arbeitsmappe wird verarbeitet...",
        processingSaved: "Gespeicherte Bauteile werden verarbeitet...",
        requestedUsed: "Angefordert → Verwendet",
        unverified: "Nicht verifiziert",
        verified: "Verifiziert",
        wasplOnlyShort: "Nur WASPL",
        workbookExported: "Ausgefüllte Excel-Datei exportiert.",
      },
      uploadFilledTemplate: "Ausgefüllte Vorlage hochladen",
      uploadList: "Liste für Empfehlungen hochladen",
      uploadListEyebrow: "Liste hochladen",
      uploadListFileSuffix: "hochgeladene-liste",
      uploadMemberList: "Bauteilliste hochladen",
      useForTakeoff: "Diese Option für die Mengenermittlung der gespeicherten Hübe verwenden",
      utilization: "Ausnutzung",
      verifiedRecommendationRequired:
        "Zum Speichern dieses Hubs ist eine Empfehlung mit verifizierter Tragfähigkeit erforderlich.",
      viewSavedLifts: "Gespeicherte Hübe anzeigen",
      wallPanelLiftType: "Hubart der Wandplatte",
      waspOnly: "Nur WASP - diese Konfiguration ist mit WASPL nicht zulässig.",
      wasplOnly: "Nur WASPL - diese Konfiguration ist mit WASP nicht zulässig.",
      weight: "Gewicht",
      weightLabel: "Gewicht des gehobenen Bauteils",
      weightPlaceholder: "Gewicht eingeben, z. B. 2500",
      woodSpecificGravity: "Holzart / spezifisches Gewicht",
      woodSpecificGravityShort: "Holz / spezifisches Gewicht",
    },
    generalPrinciples: {
      title: (device: string) => `${device} — Allgemeine Grundsätze`,
      introRaptor:
        "Aus dem mitgelieferten technischen Datenblatt RAPTOR. Diese Grundsätze gelten speziell für RAPTOR; für andere Hebevorrichtungen ist das jeweilige Datenblatt zu beachten.",
      introRaptorMini:
        "Aus dem mitgelieferten technischen Datenblatt RAPTOR MINI. Diese Grundsätze gelten speziell für RAPTOR MINI.",
      introWasp:
        "Aus dem mitgelieferten technischen Datenblatt WASP / WASPL. Diese Grundsätze gelten sowohl für WASP als auch für WASPL.",
      fastenerLengthPlate:
        "Die Länge des Verbindungsmittels ist jeweils anhand der Abmessungen des Holzbauteils, der Position des Verbindungsmittels, des Hebewinkels, des Gewichts der zu hebenden Last und der Anordnung der Hebeplatte zu wählen. In jedem Fall wird empfohlen, dass die Verbindungsmittel länger als die erforderliche Mindestlänge sind, die Spitze jedoch nicht aus dem zu hebenden Bauteil austritt.",
      fastenerLength:
        "Die Länge des Verbindungsmittels ist jeweils anhand der Abmessungen des Holzbauteils, der Position des Verbindungsmittels, des Hebewinkels und des Gewichts der zu hebenden Last zu wählen. In jedem Fall wird empfohlen, dass die Verbindungsmittel länger als die erforderliche Mindestlänge sind, die Spitze jedoch nicht aus dem zu hebenden Bauteil austritt.",
      wllBasis:
        "Die angegebenen Tragfähigkeitswerte (WLL) beruhen auf Berechnungen gemäß ICC-ES Evaluation Report ESR-4645, AWC-NDS 2018 und AWC Technical Report 12 sowie auf den Ergebnissen durchgeführter Versuche. Die zugrunde liegenden Unterlagen geben einen Sicherheitsfaktor von mindestens 5,0 an. Die anwendbaren Anforderungen des ",
      oshaLink: "OSHA-Abschnitts 1926.251",
      wllBasisAfterLink:
        " sind für den jeweiligen Hebevorgang zu bestätigen. Dieser Rechner bescheinigt weder die OSHA-Konformität noch die Freigabe der gesamten Anschlagvorrichtung.",
      specificGravityBasis: (maxCapacity: string) =>
        `Als Berechnungsgrundlage wurde ein spezifisches Gewicht von G = 0,42 angesetzt. Für andere Holzarten mit abweichendem spezifischem Gewicht wurde die folgende Umrechnungstabelle mit den Anpassungsfaktoren verwendet. Die Werte überschreiten nie die maximale Tragfähigkeit der Platte von ${maxCapacity}.`,
      specificGravityBasisWasp: (waspCapacity: string, wasplCapacity: string) =>
        `Als Berechnungsgrundlage wurde ein spezifisches Gewicht von G = 0,42 angesetzt. Für andere Holzarten mit abweichendem spezifischem Gewicht wurde die folgende Umrechnungstabelle mit den Anpassungsfaktoren verwendet. Die Werte überschreiten nie die maximale Tragfähigkeit von ${waspCapacity} für WASP und ${wasplCapacity} für WASPL.`,
      capacityFactors:
        "Alle Tragfähigkeitswerte der Schrauben sind mit einem dynamischen Beschleunigungsfaktor von 1/1,1, einem Lasteinwirkungsdauerfaktor CD = 1,25, einem Temperaturfaktor Ct = 1,0 und einem Feuchtefaktor CM = 1,0 angepasst und setzen voraus, dass die Schrauben unmittelbar vor dem Heben auf der Baustelle eingebaut werden.",
      brittleFailureElement:
        "Nachweise möglicher Festigkeitsminderungen infolge eines Sprödbruchs aufgrund der Geometrie des gehobenen Bauteils sind gesondert nach AWC-NDS zu führen, insbesondere wenn das Verbindungsmittel weniger als 70 % der Höhe des gehobenen Bauteils erfasst.",
      brittleFailureBeam:
        "Nachweise möglicher Festigkeitsminderungen infolge eines Sprödbruchs aufgrund der Geometrie des gehobenen Bauteils sind gesondert nach AWC-NDS zu führen, insbesondere wenn das Verbindungsmittel weniger als 70 % der Trägerhöhe erfasst.",
      loadToGrain:
        "Alle BSP-Berechnungen setzen unabhängig von der Ausrichtung einen Winkel zwischen Last und Faser von 90° voraus; beim Heben über die Schmalseite werden keine Abminderungen der Einbindelänge berücksichtigt.",
      otherConfigurationsPlate:
        "Für die Berechnung der Tragfähigkeit der Hebeplatte in anderen als den hier angegebenen Einbausituationen wenden Sie sich an die technische Abteilung von Rothoblaas.",
      otherConfigurations:
        "Für die Berechnung der Tragfähigkeit in anderen als den hier angegebenen Einbausituationen wenden Sie sich an die technische Abteilung von Rothoblaas.",
      fatigue: (device: string) =>
        `Zur Einhaltung der Ermüdungsgrenzen der Service Class 0 nach ASME BTH01-2023 darf keine Hebevorrichtung ${device} für mehr als 20.000 Lastzyklen verwendet werden.`,
      noPreDrilling: (specificGravity: string) =>
        `Die angegebenen Tragfähigkeitswerte gelten für die mit Schrauben ohne Vorbohren befestigte Platte, gemäß ICC Evaluation Report ESR-4645. Alle Mindestabstände sind bei einem spezifischen Gewicht über ${specificGravity} zu erhöhen.`,
      installation:
        "Die Verbindungsmittel sind gemäß ihrer jeweiligen Einbauanleitung einzubauen, um Schäden zu vermeiden und die volle Leistungsfähigkeit gemäß ihren Bemessungswerten zu erhalten.",
      noImpactWrenches:
        "Die Verwendung von Impulsschraubern/Schlagschraubern ist nicht zulässig. Den Einschraubwinkel mithilfe einer Vorbohrung und/oder einer Montageschablone einhalten. Biegung vermeiden. Auf korrektes Anziehen achten. Wir empfehlen drehmomentgesteuerte Schrauber, z. B. mit TORQUE LIMITER. Alternativ mit einem Drehmomentschlüssel anziehen.",
      hbsPlateValues:
        "Die im Datenblatt angegebenen Werte für die mit HBS PLATE Schrauben befestigte Transportplatte wurden mit der Geometrie und den mechanischen Kennwerten der Version HBS PL berechnet.",
      hbsPContact:
        "Für die Tragfähigkeiten der mit der Schraube HBS P befestigten Transportplatte wenden Sie sich für weitere Unterstützung an die technische Abteilung.",
      singleUse: (screws: string) =>
        `${screws} dürfen nur einmal verwendet werden. Einmal angezogen und belastet, dürfen die Schrauben nicht gelöst und ein zweites Mal zur Befestigung der Transportplatte verwendet werden. Sobald das zu transportierende Holzbauteil in seine endgültige Position gehoben wurde und die Transportplatte dafür nicht mehr benötigt wird, sind die Schrauben herauszudrehen und ordnungsgemäß zu entsorgen.`,
      screwsHbsPlateVgs: "HBS PLATE und VGS Schrauben",
      screwsVgs: "VGS Schrauben",
      vgsPlateReuse: (device: string) =>
        `VGS PLATE ist das einzige wiederverwendbare Verbindungsmittel. Bei der Verwendung mit der Vorrichtung ${device} sind die technischen Datenblätter und Anleitungen von VGS PLATE und der zugehörigen JIG REUSE zu beachten.`,
      qualifiedPersonnelPlate:
        "Die Hebeplatte darf nur von qualifiziertem Personal verwendet werden. Die Bedienungsanleitung (als QR-Code auf dem Produkt und unter www.rothoblaas.com verfügbar) muss vor der Verwendung gelesen und verstanden werden. Die darin enthaltenen Informationen und Anweisungen sind zu befolgen. Wenden Sie sich im Zweifelsfall vor der Verwendung an die technische Abteilung von Rothoblaas.",
      qualifiedPersonnelDevice:
        "Die Hebevorrichtung darf nur von qualifiziertem Personal verwendet werden. Die Bedienungsanleitung (als QR-Code auf dem Produkt und unter www.rothoblaas.com verfügbar) muss vor der Verwendung gelesen und verstanden werden. Die darin enthaltenen Informationen und Anweisungen sind zu befolgen. Wenden Sie sich im Zweifelsfall vor der Verwendung an die technische Abteilung von Rothoblaas.",
      specificGravityColumn: "Spezifisches Gewicht [G]",
      modificationFactorColumn: "Anpassungsfaktor",
    },
    admin: {
      family: "Familie",
      devicesAndScrews: "Geräte & Schrauben",
      lifts: "Hebevorgänge",
      anchorCount: "Anzahl Anschlagpunkte",
      anchorCounts: "Anzahlen Anschlagpunkte",
      angle: "Winkel",
      capacities: "Tragfähigkeiten",
      capacity: "Tragfähigkeit",
      capacityFactor: "Tragfähigkeitsfaktor",
      capacityLb: "Tragfähigkeit [lb]",
      device: "Gerät",
      deviceFamilies: "Gerätefamilien",
      deviceFamily: "Gerätefamilie",
      deviceFamilyLiftType: "Hebeart der Gerätefamilie",
      deviceFamilyLiftTypes: "Maßgrenzen",
      deviceScrewMappings: "Geräte-Schrauben-Zuordnungen",
      deviceScrewMapping: "Geräte-Schrauben-Zuordnung",
      devices: "Geräte",
      diameter: "Durchmesser",
      installationType: "Einbauart",
      length: "Länge",
      liftType: "Hebeart",
      liftTypes: "Typen",
      maxCapacity: "Maximale Tragfähigkeit",
      maxMemberDepth: "Maximale Bauteilhöhe",
      maxMemberWidth: "Maximale Bauteilbreite",
      memberType: "Bauteiltyp",
      minMemberDepth: "Minimale Bauteilhöhe",
      minMemberWidth: "Minimale Bauteilbreite",
      plateThickness: "Plattendicke",
      productUrl: "Produkt-URL",
      requiresRiggingReview: "Erfordert Prüfung des Anschlagmittels",
      screw: "Schraube",
      screwCount: "Schrauben pro Gerät",
      screwFamilies: "Schraubenfamilien",
      screwFamily: "Schraubenfamilie",
      screws: "Schrauben",
      specificGravities: "Rohdichten",
      specificGravity: "Rohdichte",
      washer: "Unterlegscheibe",
    },
    errors: {
      calculationInvalidInput: "Die Berechnung enthält einen ungültigen Wert.",
      calculationNotAvailable: "Ein gewählter Wert ist nicht mehr verfügbar. Bitte die Auswahl prüfen.",
      capacityAlreadyExists:
        "Es gibt bereits eine Tragfähigkeit mit demselben Gerät, derselben Schraube, Hebeart, Einbauart, Schraubenanzahl pro Gerät und demselben Winkel.",
      capacityNotFound: "Die Tragfähigkeit wurde nicht gefunden.",
      deviceAlreadyExists: "Der Artikel ist bereits ein Gerät.",
      deviceFamilyNameAlreadyExists: "Es gibt bereits eine Gerätefamilie mit diesem Namen.",
      deviceFamilyLiftTypeAlreadyExists: "Die Gerätefamilie hat diese Hebeart bereits.",
      deviceFamilyLiftTypeInvalidLimits:
        "Die Bauteilgrenzen müssen größer als null sein, und jedes Maximum darf nicht kleiner als sein Minimum sein.",
      deviceFamilyLiftTypeNotFound: "Die Hebeart der Gerätefamilie wurde nicht gefunden.",
      deviceScrewMappingAlreadyExists: "Die Gerätefamilie hat diese Schraubenfamilie bereits.",
      deviceScrewMappingNotFound: "Die Schraubenfamilie der Gerätefamilie wurde nicht gefunden.",
      deviceInUseByLift:
        "Das Gerät wird in gespeicherten Hebevorgängen verwendet: Setzen Sie es auf Hidden, statt es zu löschen.",
      deviceNotFound: "Das Gerät wurde nicht gefunden.",
      liftTypeAnchorCountAlreadyExists: "Die Hebeart hat diese Anzahl Anschlagpunkte bereits.",
      liftTypeAnchorCountNotFound: "Die Anzahl Anschlagpunkte wurde nicht gefunden.",
      screwAlreadyExists: "Der Artikel ist bereits eine Schraube.",
      screwInUseByLift:
        "Die Schraube wird in gespeicherten Hebevorgängen verwendet: Setzen Sie sie auf Hidden, statt sie zu löschen.",
      screwNotFound: "Die Schraube wurde nicht gefunden.",
      specificGravityAlreadyExists: "Es gibt bereits eine Rohdichte mit diesem Wert.",
      specificGravityInUseByLift:
        "Die Rohdichte wird in gespeicherten Hebevorgängen verwendet: Setzen Sie sie auf Hidden, statt sie zu löschen.",
      specificGravityNotFound: "Die Rohdichte wurde nicht gefunden.",
      uploadFileTooLarge: "Die Datei ist größer als 10 MB.",
      uploadInvalidWorkbook: "Die hochgeladene Datei ist keine gültige .xlsx-Arbeitsmappe.",
      uploadMetricTemplate:
        "Die Vorlage USA - NDS mit Gewichten in lb und Maßen in Zoll verwenden. Metrische oder europäische Vorlagen werden nicht akzeptiert.",
      uploadMissingColumns: "In der Vorlage fehlen erforderliche Spalten.",
      uploadNoHeader: "Die Arbeitsmappe muss eine Kopfzeile mit Member Name und Member Type enthalten.",
      uploadNoRows: "Es wurden keine Bauteilzeilen gefunden.",
      uploadNoWorksheet: "In der hochgeladenen Arbeitsmappe wurde kein Arbeitsblatt gefunden.",
    },
  },

  deckDesignerModule: {
    configurations: {
      singular: "Konfiguration",
      plural: "Konfigurationen",
      errors: {
        connectorFamilyNotAvailable: "Die gewählte Verbinderfamilie ist nicht mehr verfügbar: Wählen Sie eine andere.",
        connectorNotAvailable: "Der gewählte Verbinder ist nicht mehr verfügbar: Wählen Sie einen anderen.",
        screwNotAvailable: "Eine gewählte Schraube ist nicht mehr verfügbar: Wählen Sie eine andere.",
        supportFamilyNotAvailable: "Die gewählte Stützenfamilie ist nicht mehr verfügbar: Wählen Sie eine andere.",
        supportNotAvailable: "Die gewählte Stütze ist nicht mehr verfügbar: Wählen Sie eine andere.",
        substructureNotAvailable: "Die gewählte Unterkonstruktion ist nicht mehr verfügbar: Wählen Sie eine andere.",
        invalidTotalHeight:
          "Maximale Höhe Terrassenoberkante (H) ist für den ausgewählten Terrassenaufbau nicht gültig.",
        noSupportForRequiredHeightRange: (min: number, max: number) =>
          `Mit der aktuellen Konfiguration muss die Stütze eine Höhe (h) ${min === max ? `von ${min} mm` : `von ${min} bis ${max} mm`} abdecken: keine verfügbare Stütze deckt diesen Bereich ab. H oder die Neigung anpassen.`,
        noSupportForSpacing:
          "Der Abstand zwischen den Stützen entlang der Balken ist für die verfügbaren Stützen zu klein (mindestens das 1,5-Fache ihrer Breite ist erforderlich). Verringern Sie den Balkenabstand.",
        noSupportFamilyOptions: "Mit der aktuellen Konfiguration sind keine Optionen verfügbar.",
        noSupportForSlopeDrop: {
          title: "Keine verfügbare Stütze kann den Höhenunterschied des Geländes abdecken, unabhängig von H.",
          requiredRange: (min: number, max: number) =>
            `Erforderliche Stützenhöhe (h): von ${min} bis ${max} mm (Unterschied ${max - min} mm)`,
          widestSupport: (supportCode: string, rangeMm: number) =>
            `Größter von einer Stütze abgedeckter Unterschied: ${rangeMm} mm (${supportCode})`,
          fix: (maxSlope: number | null) =>
            `${maxSlope != null ? `Die Neigung auf höchstens ${maxSlope}° reduzieren` : "Die Neigung reduzieren"}, dann H unter den kompatiblen Werten wählen.`,
        },
        noCompatibleConnectorSubstructureScrew:
          "Keine passende Schraube für den ausgewählten Verbinder und die Unterkonstruktion.",
        noCompatibleSupportScrew: "Keine passende Schraube für die ausgewählte Stütze.",
        noCompatibleConnectorBoardScrew: "Keine passende Schraube für die ausgewählte Dielenstärke.",
        noCompatibleConnectorFamilyMapping:
          "Keine Verbinderfamilie ist mit der ausgewählten Unterkonstruktion, Befestigungsart, Nutart und Fuge kompatibel.",
        noCompatibleConnectorFamilyBoardThickness: "Keine passende Verbinderfamilie passt zur aktuellen Brettstärke.",
        noCompatibleConnectorFamilyBoardThicknessAndGroovingHeight:
          "Keine passende Verbinderfamilie passt zur aktuellen Brettstärke und Nuttiefe.",
        noCompatibleConnector: "Kein Verbinder ist mit der aktuellen Konfiguration kompatibel.",
        noCompatibleSupportFamilyMapping:
          "Keine Stützenfamilie ist mit der ausgewählten Unterkonstruktions-Materialklasse kompatibel.",
      },
      calculation: {
        shapeAndGeometricData: "Form und Abmessungen",
        completeFieldsToContinue: "Füllen Sie alle Felder auf dieser Seite aus, um fortzufahren",
        showBillOfQuantities: "Stückliste anzeigen",
        boards: "Dielen",
        billOfQuantities: "Mengenermittlung",
        downloadPdf: "PDF herunterladen",
        downloadPdfDescription:
          "Laden Sie das PDF mit der vollständigen Zusammenfassung der Konfiguration herunter: geometrische Daten, Dielen, Unterkonstruktion, Stützen, Ansichten der Terrasse, Auflagerreaktionen und Produktliste.",
        downloadPdfDisabledHint: "Vervollständigen Sie die Konfiguration, um das PDF herunterzuladen.",
        downloadPdfDisabledBoardFitHint:
          "Beheben Sie die Warnung zu Dielenbreite und Fuge, um das PDF herunterzuladen.",
        billOfQuantitiesEmpty: "Wählen Sie die Terrassendaten aus, um die Mengenermittlung zu berechnen.",
        calculationDetailsEmpty: "Wählen Sie die Terrassendaten aus, um die Berechnungsdetails anzuzeigen.",
        showCompatibleValues: "Kompatible Werte anzeigen",
        stratigraphySummaryTitle: "Aktueller Aufbau",
        availableHeightForConnectorAndSupport: "Verfügbare Höhe für Verbinder und Stütze",
        connectorThickness: "Verbinderdicke",
        remainingHeightForSupport: "Verbleibende Höhe für die Stütze",
        billOfQuantitiesProduct: "Produkt",
        billOfQuantitiesRequiredQuantity: "Benötigte Menge",
        billOfQuantitiesQuantityToOrder: "Zu bestellende Menge",
        calculationDetails: "Berechnungsdetails",
        deckArea: "Terrassenfläche",
        totalBoards: "Dielen",
        joists: "Balken",
        joistsLegend: "Unterkonstruktionsbalken",
        layoutCalculation: "Layoutberechnung",
        longJoists: "Lange Balken",
        shortJoists: "Kurze Balken",
        longJoist: "Langer Balken",
        shortJoist: "Kurzer Balken",
        supportCalculation: "Stützenberechnung",
        joistsSpacing: "Balkenabstand",
        maxSupportSpacing: "Max. Stützenabstand",
        supportsPerLongJoist: "Stützen pro langem Balken",
        supportsPerShortJoist: "Stützen pro kurzem Balken",
        supportSpacing: "Stützenabstand",
        supportsCount: "Anzahl Stützen",
        lShaped: "L-Form",
        horizontal: "Horizontal",
        vertical: "Vertikal",
        rectangular: "Rechteckige Form",
        boardDirection: "Dielenrichtung",
        fasteningType: "Befestigungsart",
        hiddenFastening: "Verdeckt",
        hiddenFasteningDescription:
          "Die Dielen werden ohne sichtbare Schrauben mit verdeckten Verbindern oder Clips befestigt.",
        visibleFastening: "Sichtbar",
        visibleFasteningDescription:
          "Die Dielen werden mit auf der Terrassenoberfläche sichtbaren Schrauben befestigt.",
        groovingType: "Nuttyp",
        smoothGrooving: "Glatt",
        groovedGrooving: "Genutet",
        maxBase: "Max. Breite (L1)",
        maxHeight: "Max. Länge (L2)",
        substructureSection: (unit: string) => `Querschnitt Element bs x hs [${unit}]`,
        secondaryBase: "Sekundäre Breite (l1)",
        secondaryHeight: "Sekundäre Länge (l2)",
        totalHeight: "Maximale Höhe Terrassenoberkante (H)",
        slope: "Neigung",
        slopeDirectionTitle: "Geländeneigung",
        slopeDirectionHint:
          "Ziehen Sie über die Zeichnung, um die Richtung anzugeben, in der das Gelände abfällt. Ohne Richtung bleibt das Gelände eben.",
        slopeIncompleteWarning:
          "Neigung und Richtung müssen beide angegeben werden: Die Änderungen an der Neigung werden nicht gespeichert. Trotzdem schließen?",
        totalHeightTooLittle: (height: string) =>
          `Der ausgewählte Terrassenaufbau erfordert eine Gesamthöhe von mindestens ${height}.`,
        secondaryItemDoesNotFitTotalHeight: (code: string) =>
          `Durch Aktivieren von ${code} wäre die gewählte Verbinderfamilie nicht mehr mit der Gesamthöhe (H) kompatibel.`,
        totalHeightIncompatible: (minHeight: string, maxHeight: string) =>
          `Die Gesamthöhe für diesen Terrassenaufbau muss zwischen ${minHeight} und ${maxHeight} liegen.`,
        planeInteractionHint: "Hier können Sie die Terrasse direkt zeichnen und bearbeiten.",
        planeInteractionStart: "Zeichnen starten",
        // the button of the tutorial reopened from its icon
        planeInteractionContinue: "Weiter zeichnen",
        showPlaneInteractionHint: "Tutorial anzeigen",
        planeInteractionFeatureDraw: "Klicken Sie auf die Ebene, um den Umriss der Terrasse zu zeichnen.",
        planeInteractionFeatureLength:
          "Geben Sie nach Auswahl eines Punkts die Länge der Seite ein (Rücktaste zum Korrigieren) und bestätigen Sie mit der Eingabetaste.",
        planeInteractionFeatureDrag: "Ziehen Sie die Seiten der Terrasse, um ihre Abmessungen zu ändern.",
        planeInteractionFeatureEditLabel: "Klicken Sie auf ein Etikett, um die gewünschte Abmessung einzugeben.",
        planeInteractionFeatureDifferentBoards:
          "Ziehen Sie die Dielen mit abweichender Breite, falls vorhanden, von einer Seite zur anderen.",
        planeInteractionFeatureClear:
          "Verwenden Sie das Papierkorb-Symbol, um die Punkte zu löschen und die Zeichnung neu zu beginnen.",
        liveLoad: "Nutzlast",
        deflection: "Durchbiegung (EN1995-1-1 §7 / EN1999-1-1 §4)",
        gap: "Kompatible Fugenbreite",
        gapIncompatible: (minimumGap: string, maximumGap: string) =>
          `Die Fuge muss zwischen ${minimumGap} und ${maximumGap} liegen.`,
        joistsSpacingIncompatible: (minimumSpacing: number, maximumSpacing: number, unit: string) =>
          `Der Balkenabstand muss zwischen ${minimumSpacing} ${unit} und ${maximumSpacing} ${unit} liegen.`,
        material: "Material",
        connectorMaterial: "Verbindermaterial",
        timberMaterial: "Holz",
        wpcMaterial: "WPC",
        boardWidth: "Dielenbreite",
        differentBoardWidthDragHint: "Sie können die Dielen von einer Seite der Terrasse zur anderen ziehen.",
        differentBoard: "Diele mit abweichender Breite",
        differentBoardsColumn: "Dielen mit abweichender Breite",
        longDifferentBoardWidth: "Lange Diele",
        shortDifferentBoardWidth: "Kurze Diele",
        boardThickness: "Dielenstärke",
        boardThicknessMustNotExceedBoardWidth: "Die Dielenstärke darf die Dielenbreite nicht überschreiten.",
        boardThicknessExceedsConnectorFamilyMaxThickness: (maxValue: string) =>
          `Die Dielenstärke überschreitet die maximal zulässige Stärke der ausgewählten Verbinderfamilie (${maxValue}).`,
        boardThicknessBelowAsymmetricalGroovingMinimum: (minValue: string) =>
          `Die Dielenstärke ist für die ausgewählte asymmetrische Nut zu gering (mindestens ${minValue}).`,
        boardThicknessBelowGroovingMinimum: (minValue: string) =>
          `Die Dielenstärke muss für den ausgewählten Nuttyp mindestens ${minValue} betragen.`,
        boardThicknessOutOfSymmetricalGroovingRange: (minValue: string, maxValue: string) =>
          minValue === maxValue
            ? `Die Dielenstärke muss für die aktuell ausgewählte Konfiguration ${minValue} betragen.`
            : `Die Dielenstärke muss für die mittige Nut mit der ausgewählten Verbinderfamilie zwischen ${minValue} und ${maxValue} liegen.`,
        groovingHeight: "Nuthöhe",
        groovingHeightOutOfConnectorFamilyRange: (minValue: string, maxValue: string) =>
          `Die Nuthöhe muss für die ausgewählte Verbinderfamilie zwischen ${minValue} und ${maxValue} liegen.`,
        groovingHeightBelowConnectorMinimum: (minValue: number, unit: string) =>
          `Die Nuthöhe muss für den ausgewählten Verbinder mindestens ${minValue} ${unit} betragen.`,
        boardWidthMustNotExceedDeckDimension: (maxValue: string) =>
          `Die Dielenbreite darf bei der gewählten Richtung ${maxValue} nicht überschreiten.`,
        sideNarrowerThanBoardWidth: "Diese Seite darf nicht schmaler als die gewählte Dielenbreite sein.",
        boardFitWarning:
          "Die gewählte Dielenbreite und Fuge passen nicht exakt zu den Terrassenabmessungen. Wählen Sie zur Optimierung eine der folgenden 3 Optionen:",
        boardFitApplied:
          "Änderung übernommen. Wenn sie nicht passt, klicken Sie auf „Rückgängig“, um die vorherigen Werte wiederherzustellen.",
        appliedFitParameter: "Parameter",
        appliedFitPrevious: "Vorher",
        appliedFitCurrent: "Aktuell",
        setBoardWidth: "Dielenbreite setzen",
        setBoardWidthTo: "Dielenbreite setzen auf:",
        setDeckDimensions: "Terrassenmaße setzen",
        setDeckDimensionsTo: "Terrassenmaße setzen auf:",
        setDifferentBoardWidth: "Abweichende Dielenbreiten setzen",
        setDifferentBoardWidthTo: "Abweichende Dielenbreiten setzen auf:",
        topView: "Draufsicht",
        sideView: "Seitenansicht",
        bottomView: "Unteransicht",
        slopeView: "Neigung",
        slopeViewSide: (from: number, to: number) => `Seite ${from}–${to}`,
        slopeViewLegendDeck: "Terrasse",
        slopeViewLegendTerrain: "Gelände",
        slopeViewExaggeration: "Neigungsüberhöhung (nur Darstellung)",
        slopeViewShowArrow: "Neigungspfeil anzeigen",
        unitSystemMetric: "Metrisch",
        unitSystemImperial: "Imperial",
        unitSystemChangeTitle: "Maßeinheit ändern",
        unitSystemChangeWarning:
          "Wenn Sie die Maßeinheit ändern, bleiben Form und Abmessungen erhalten; alle anderen Felder werden zurückgesetzt. Möchten Sie fortfahren?",
        slopeViewExaggerationHelp:
          "Verstärkt die Neigung in der 3D-Ansicht, damit sie besser zu sehen ist. Nur ein visueller Effekt: Werte und Konfiguration ändern sich nicht.",
        slopeViewClickHint: "Auf eine Beschriftung klicken, um h, t und H an dieser Stelle zu sehen.",
        slopeViewNotToScale: "Schematische Darstellung, nicht maßstabsgetreu.",
        slopeViewUnreachable: "Nicht erreichbar",
        slopeViewLegendTotalHeight: "H — maximale Höhe Terrassenoberkante",
        slopeViewLegendSupportHeight: "h — für die Stütze verfügbare Höhe",
        slopeViewLegendTerrainHeight: "t — Geländehöhe gegenüber dem tiefsten Eckpunkt, an dem H gemessen wird",
        supportCompatibleHRange: "Kompatible H",
        supportMinHeight: "Min. Stützenhöhe",
        supportMaxHeight: "Max. Stützenhöhe",
        supportSlopeUnchanged: "Die Neigung entspricht der eingegebenen.",
        supportSlopeReduced: "Die Neigung wird gegenüber der eingegebenen verringert.",
        supportSlopeIncreased: "Die Neigung wird gegenüber der eingegebenen erhöht.",
        supportTotalHeightUnchanged: "H entspricht dem eingegebenen Wert.",
        supportTotalHeightReduced: "H wird gegenüber dem eingegebenen Wert verringert.",
        supportTotalHeightIncreased: "H wird gegenüber dem eingegebenen Wert erhöht.",
        supportFlatTerrainOnly: "Nur bei beiden Neigungen 0°",
        supportSuggestedSlope: "Neigung",
        supportWidth: "Stützenbreite",
        suggestedValueUnchanged: "Der Wert entspricht dem eingegebenen.",
        suggestedValueReduced: "Der Wert wird gegenüber dem eingegebenen verringert.",
        suggestedValueIncreased: "Der Wert wird gegenüber dem eingegebenen erhöht.",
        supportSpacingSuggestionsInfo:
          "Für jede Stütze die nächstgelegene Konfiguration, bei der die Stützen mindestens das 1,5-Fache ihrer Breite auseinanderliegen. Zuerst werden Elementquerschnitt und Balkenabstand geändert; Durchbiegung und Nutzlast nur, wenn das nicht ausreicht.",
        noSupportSpacingSuggestion:
          "Keine Konfiguration ergibt einen Stützenabstand von mindestens dem 1,5-Fachen ihrer Breite.",
        supportApplySuggestion: "Werte übernehmen",
        noSupportCompatibleWithSlope:
          "Keine Stütze ist mit der eingegebenen Neigung kompatibel: ohne Neigung gäbe es mindestens eine. Versuchen Sie, sie zu verringern, H zu erhöhen oder die Terrassenmaße zu ändern.",
        noSupportCompatibleGeneric:
          "Keine Stütze ist mit der aktuellen Konfiguration kompatibel. Versuchen Sie, die Gesamthöhe H, die gewählte Stütze oder die anderen Terrassenelemente (Diele, Verbinder, Sekundärelement) zu ändern.",
        supportScrew: "Stützenschraube",
        connectorSubstructureScrew: "Verbinder-Schraube Unterkonstruktion",
        connectorBoardScrew: "Verbinder-Schraube Diele",
        regularLongBoards: "Reguläre lange Dielen",
        regularShortBoards: "Reguläre kurze Dielen",
        differentLongBoards: "Lange Dielen mit abweichender Breite",
        differentShortBoards: "Kurze Dielen mit abweichender Breite",
      },
    },
    connectorFamilies: {
      singular: "Verbinderfamilie",
      plural: "Verbinderfamilien",
      isScrew: "Ist Schraube",
      requiresBoardScrew: "Benötigt Dielenschraube",
      maxBoardThickness: "Max. Dielenstärke",
      description: "Beschreibung",
      errors: {
        nameAlreadyPresent: "Eine Verbinderfamilie mit demselben Namen existiert bereits.",
      },
    },
    connectorMappings: {
      singular: "Verbinderzuordnung",
      plural: "Verbinderzuordnungen",
      minimumGap: "Mindestfugenbreite",
      maximumGap: "Maximale Fugenbreite",
    },
    connectors: {
      singular: "Verbinder",
      plural: "Verbinder",
      minGroovingHeight: "Mindest-Nuthöhe",
      minBoardWidth: "Mindestbreite Diele",
      errors: {
        inUseByConfiguration:
          "Der Verbinder wird in einer gespeicherten Berechnung verwendet und kann nicht gelöscht werden.",
        itemAlreadyPresent: "Ein Verbinder mit demselben Element existiert bereits.",
        screwAlreadyPresent: "Ein Verbinder mit derselben Schraube existiert bereits.",
      },
    },
    fasteningTypes: {
      singular: "Befestigungsart",
      plural: "Befestigungsarten",
      errors: {
        nameAlreadyPresent: "Eine Befestigungsart mit demselben Namen existiert bereits.",
      },
    },
    groovingTypes: {
      singular: "Nuttyp",
      plural: "Nuttypen",
      errors: {
        nameAlreadyPresent: "Ein Nuttyp mit demselben Namen existiert bereits.",
      },
    },
    connectorMaterials: {
      singular: "Verbindermaterial",
      plural: "Verbindermaterialien",
      errors: {
        inUseByConnector: "Das Material wird von einem Verbinder verwendet: Ändern oder löschen Sie zuerst den Verbinder.",
        nameAlreadyPresent: "Ein Verbindermaterial mit demselben Namen existiert bereits.",
      },
    },
    secondaryItems: {
      singular: "Sekundarelement",
      plural: "Sekundarelemente",
      use: "Sekundarelement verwenden",
      canUse: "Kann Sekundarelement verwenden",
      errors: {
        inUseByConfiguration:
          "Das Zusatzelement wird in einer gespeicherten Berechnung verwendet und kann nicht gelöscht werden.",
        inUseByConnectorMapping:
          "Das Zusatzelement wird von einer Verbinderzuordnung verwendet: Löschen Sie zuerst die Zuordnung.",
      },
    },
    screwFamilies: {
      singular: "Schraubenfamilie",
      plural: "Schraubenfamilien",
      errors: {
        inUseByScrew: "Die Schraubenfamilie wird von einer Schraube verwendet: Löschen Sie zuerst ihre Schrauben.",
        inUseByConnectorFamily: "Die Schraubenfamilie ist einer Verbinderfamilie zugeordnet: Entfernen Sie sie zuerst dort.",
        inUseBySupportFamily: "Die Schraubenfamilie ist einer Stützenfamilie zugeordnet: Entfernen Sie sie zuerst dort.",
        nameAlreadyPresent: "Eine Schraubenfamilie mit demselben Namen existiert bereits.",
      },
    },
    screws: {
      singular: "Schraube",
      plural: "Schrauben",
      errors: {
        inUseByConfiguration:
          "Die Schraube wird in einer gespeicherten Berechnung verwendet und kann nicht gelöscht werden.",
        inUseByConnector: "Die Schraube wird als Verbinder verwendet: Löschen Sie zuerst diesen Verbinder.",
        itemAlreadyPresent: "Eine Schraube mit demselben Element existiert bereits.",
      },
    },
    substructures: {
      singular: "Unterkonstruktion",
      plural: "Unterkonstruktionen",
      base: "Breite",
      secondMomentOfArea: "Flächenträgheitsmoment",
      countries: "Länder",
      countryOptions: {
        eu: "Europa",
        us: "USA",
        all: "Alle",
      },
      errors: {
        inUseByConfiguration:
          "Die Unterkonstruktion wird in einer gespeicherten Berechnung verwendet und kann nicht gelöscht werden.",
        itemAlreadyPresent: "Eine Unterkonstruktion mit demselben Element existiert bereits.",
        baseHeightAlreadyPresent: "Eine Unterkonstruktion mit derselben Breite und Höhe existiert bereits.",
      },
    },
    substructureFamilies: {
      singular: "Unterkonstruktionsfamilie",
      plural: "Unterkonstruktionsfamilien",
      errors: {
        nameAlreadyPresent: "Eine Unterkonstruktionsfamilie mit demselben Namen existiert bereits.",
      },
    },
    substructureMaterialClasses: {
      singular: "Unterkonstruktionsmaterialklasse",
      plural: "Unterkonstruktionsmaterialklassen",
      modulusOfElasticity: "Elastizitätsmodul",
      errors: {
        inUseByConfiguration:
          "Die Materialklasse wird in einer gespeicherten Berechnung verwendet und kann nicht gelöscht werden.",
        inUseBySupport: "Die Materialklasse ist einer Stütze zugeordnet: Entfernen Sie sie zuerst dort.",
        nameAlreadyPresent: "Eine Unterkonstruktionsmaterialklasse mit demselben Namen existiert bereits.",
      },
    },
    substructureMaterials: {
      singular: "Unterkonstruktionsmaterial",
      plural: "Unterkonstruktionsmaterialien",
      errors: {
        nameAlreadyPresent: "Ein Unterkonstruktionsmaterial mit demselben Namen existiert bereits.",
      },
    },
    supportFamilies: {
      singular: "Stützenfamilie",
      plural: "Stützenfamilien",
      maxLiveLoad: "Max. Nutzlast",
      errors: {
        nameAlreadyPresent: "Eine Stützenfamilie mit demselben Namen existiert bereits.",
      },
    },
    supports: {
      singular: "Stütze",
      plural: "Stützen",
      errors: {
        inUseByConfiguration:
          "Die Stütze wird in einer gespeicherten Berechnung verwendet und kann nicht gelöscht werden.",
        itemAlreadyPresent: "Eine Stütze mit demselben Element existiert bereits.",
      },
    },
    substuructures: {
      singular: "Unterkonstruktion",
      plural: "Unterkonstruktionen",
    },
    substuructureMaterials: {
      singular: "Unterkonstruktionsmaterial",
      plural: "Unterkonstruktionsmaterialien",
    },
    substuructureFamilies: {
      singular: "Unterkonstruktionsfamilie",
      plural: "Unterkonstruktionsfamilien",
    },
  },

  lockCalculatorModule: {
    preview3d: "3D-Vorschau",
    preDrillingLegend: {
      header: "Vorbohrung Hauptträger",
      joist: "Vorbohrung Nebenträger",
      yes: "ja",
      no: "nein",
    },
    tabs: {
      codeNorm: "Norm",
      structure: "Tragwerk",
      connectors: "Verbinder",
      loading: "Lasten",
      designInput: "Eingabedaten",
      configuration: "Konfiguration",
      loads: "Lasten",
      results: "Ergebnisse",
      calculationComparison: "Berechnungsvergleich",
      bom: "Stückliste",
      report: "PDF-Bericht",
    },
    env: {
      title: "Umgebung & Normen",
      standard: "Norm",
      serviceClass: "Nutzungsklasse",
      loadDurationClass: "Lasteinwirkungsdauer",
      continuousCtrl: "Kontinuierliche Kontrolle",
      continuousCtrlLabel: "Produktion unter ständiger Kontrolle (COV < 15%)",
      coefficients: "Bemessungsbeiwerte",
      kmod: "Modifikationsbeiwert (Lasteinwirkungsdauer und Nutzungsklasse)",
      gammaM: "Teilsicherheitsbeiwert der Verbindung (γM)",
      gammaMAluminium: "Teilsicherheitsbeiwert für Aluminium (γM)",
      customCoefficients: "Benutzerdefiniert",
      customCoefficientsHint: "kmod und γM nur für diese Berechnung anstelle der normativen Werte ändern",
      normativeValue: "Normativer Wert",
      restoreNormative: "Zurücksetzen",
    },
    structural: {
      title: "Tragwerkskonfiguration",
      configType: "Seitliche Tragfähigkeit",
      connectionPosition: "Position des Verbinders",
      upliftResistanceConfig: "Konfiguration Abhebewiderstand",
      inclinedFastener: "Geneigtes Befestigungsmittel",
      headerOrientation: "Ausrichtung des Hauptträgers",
      lateralResistance: {
        columnNotch: "Column notch",
        headerBeamNotch: "Ausklinkung am Hauptträger",
        joistBeamNotch: "Ausklinkung am Nebenträger",
        inclinedFasteners: "Geneigte Befestigungsmittel",
        lockstop: "LOCK STOP",
        exposed: "Freiliegend",
      },
      headerOrientationOptions: { beam: "Träger", column: "Stütze" },
      generateAllConfigurations: "Alle Konfigurationen generieren",
      goToManualConfiguration: "Zur manuellen/halbmanuellen Konfiguration",
    },
    connection: {
      title: "Verbindungskonfiguration",
      standard: "Norm",
      serviceClass: "Nutzungsklasse",
      loadDurationClass: "Lasteinwirkungsdauer",
      connector: "Verbindung",
      finish: "Oberfläche",
      connectorOne: "Verbinder 1",
      connectorTwo: "Verbinder 2",
      productType: "Produkttyp",
    },
    fasteners: {
      headerFastener: "Kopfträger-Befestigungsmittel",
      joistFastener: "Nebenträger-Befestigungsmittel",
      upliftFastener: "Abhebesicherung",
      lockstop: "Lockstop",
    },
    customMaterial: {
      create: "Benutzerdefiniertes Material erstellen",
      edit: "Benutzerdefiniertes Material bearbeiten",
      remove: "Material löschen",
      badge: "benutzerdefiniert",
      name: "Name",
      baseGrade: "Basierend auf",
      keepCurrentBase: "Aktuelle Basis beibehalten",
      baseGradeHint: "Kategorie, Norm und Strukturfamilie werden vom Basismaterial übernommen",
      inherited: "Von der Basis übernommen",
      family: "Strukturfamilie",
      category: "Kategorie",
      normative: "Norm",
      properties: "Eigenschaften",
      advanced: "Erweitert",
      rhoK: "Charakteristische Rohdichte ρk",
      rhoM: "Mittlere Rohdichte ρm",
      fc0K: "Druck parallel zur Faser fc,0,k",
      fc90K: "Druck senkrecht zur Faser fc,90,k",
      fvK: "Schub fv,k",
      frK: "Rollschub fr,k",
      faxK: "Auszugswiderstand fax,k",
      usedInCalculations: (n: number) =>
        `In ${n} Berechnungen verwendet: sie werden bei der nächsten Neuberechnung aktualisiert.`,
      deleteConfirm: "Möchten Sie dieses benutzerdefinierte Material wirklich löschen?",
      deleteBlocked: "Nicht löschbar: das Material wird in anderen Berechnungen verwendet.",
    },
    timber: {
      sectionTitle: "Geometrie",
      title: "Holzelemente",
      header: "Hauptträger",
      joist: "Nebenträger",
      headerMaterialGrade: "Festigkeitsklasse des Hauptträgers",
      headerWidth: "Breite des Hauptträgers (b)",
      headerHeight: "Höhe des Hauptträgers (h)",
      joistMaterialGrade: "Festigkeitsklasse des Nebenträgers",
      joistWidth: "Breite des Nebenträgers (b)",
      joistHeight: "Höhe des Nebenträgers (h)",
      joistAngle: "Nebenträgerwinkel α",
      joistAngleBeta: "Nebenträgerwinkel β",
      connectorTopDistance: "Abstand des Verbinders zur Oberkante des Nebenträgers",
      joistTopOffset: "Versatz der Nebenträger-Oberkante zur Hauptträger-Oberkante",
      joistTopAlignment: "Lage der Nebenträger-Oberkante",
      joistTopAligned: "Bündig mit der Stützen-Oberkante",
      joistTopOffsetOption: "Versetzt zur Stützen-Oberkante",
    },
    connector: {
      sectionTitle: "Verbinder & Schrauben",
      setupTitle: "Verbindungsaufbau",
      selectionTitle: "Verbinderauswahl",
      fastenersTitle: "Schrauben",
      connectorPairCount: "Anzahl der Verbinderpaare",
      noCompatibleConnector: "Kein LOCK-T-Verbinder kompatibel mit den eingegebenen Querschnitten.",
      noCompatibleSecondConnector: "Kein zweiter Verbinder mit dem ausgewählten kompatibel",
      generateFromData: "Konfigurationen aus eingegebenen Daten generieren",
      generateManual: "Manuelle Lösung generieren",
    },
    loads: {
      sectionTitle: "Bemessungslasten (Ed)",
      title: "Lasten (Ed)",
      fvD: "Querkraft (Fv,d)",
      faxD: "Axialkraft (Fax,d)",
      flatD: "Seitenkraft (Flat,d)",
      fupD: "Abhebekraft (Fup,d)",
      alphaDeg: "Winkel α",
    },
    combined: {
      title: "Kombinierte Nachweise",
      description: "Wählen Sie die Lastrichtungen für den kombinierten Nachweis:",
      includeFv: "Fv,d (Querkraft)",
      includeFup: "Fup,d (Abheben)",
      includeFax: "Fax,d (Auszug)",
      includeFlat: "Flat,d (Seitlich)",
    },
    configuration: {
      title: "Konfiguration",
      configType: "Konfigurationstyp",
      connectionCount: "Anzahl Verbindungen",
    },
    errors: {
      fillRequiredFields: "Bitte füllen Sie alle Pflichtfelder aus.",
    },
    selectPlaceholder: "Select...",
    helpTexts: {
      standard: "Anzuwendende Bemessungsnorm (Eurocode 5 oder NTC 2018)",
      serviceClass: "Feuchtigkeits- und Temperaturbedingungen am Einbauort (EC5 §2.3.1.3)",
      loadDurationClass: "Einwirkungsdauer (ständig, lang, mittel, kurz, sehr kurz)",
      continuousCtrl: "Zertifizierte Produktion mit COV < 15% erlaubt reduziertes γM (EC5 §2.4.1)",
      lateralResistanceConfig: "Konfigurationstyp für Quertragfähigkeitsnachweis",
      upliftResistanceConfig:
        "Wählen Sie, ob das geneigte Befestigungsmittel für die Berechnung des Abhebewiderstands verwendet werden soll. Verwendet dasselbe Befestigungsmittel, das im Schritt Connectors für die seitliche Konfiguration ausgewählt wurde.",
      headerOrientation: "Ausrichtung des Hauptträgers: horizontal (Träger) oder vertikal (Stütze).",
      headerMaterialGrade: "Holzgüte des Hauptträgers (GL24h, GL28h, LVL, C24...)",
      headerWidth:
        "Querschnittsbreite des Hauptträgers (Abmessung b). Bei Stützen-Ausrichtung muss die eigene Breite des Verbinders in dieses Maß minus einer Toleranz passen (17,5mm bei Mini, 24mm bei Midi).",
      headerHeight:
        "Querschnittshöhe des Hauptträgers (Abmessung h). Bei Träger-Ausrichtung begrenzt sie die maximale Aufbauhöhe (assembly_height) des Verbindermoduls (≤ Höhe Hauptträger − 15mm).",
      joistMaterialGrade:
        "Holzgüte des Nebenträgers. Bei einer Rohdichte (rho_k) über 420 kg/m³ ist eine Vorbohrung erforderlich, was die maximal zulässige Verbinderbreite verringert (siehe Breite Nebenträger).",
      joistWidth:
        "Breite des Nebenträgers (Querschnitt b). Die eigene Breite des Verbinders muss in dieses Maß passen — genau, wenn eine Vorbohrung erforderlich ist, oder mit einer zusätzlichen Toleranz von 8mm (Mini) / 6mm (Midi), wenn nicht.",
      joistHeight:
        "Höhe des Nebenträgers (Querschnitt h). Begrenzt die maximale Aufbauhöhe (assembly_height) des Verbindermoduls (≤ Höhe Nebenträger), ohne Toleranz.",
      angleAlpha: "",
      joistTopAlignment:
        "Stützen-Hauptträger. Bündig: Die Oberkante des Nebenträgers liegt bündig mit der Stützen-Oberkante, c_min wird am Abstand des Verbinders zur Nebenträger-Oberkante geprüft. Versetzt: Die Oberkante des Nebenträgers liegt unter der Stütze (Versatzfeld), c_min wird an der Summe aus Versatz und Verbinderabstand geprüft.",
      joistTopOffset:
        "Um wie viel die Oberkante des Nebenträgers unter der Stützen-Oberkante liegt. Wenn leer, gilt das c_min des gewählten LOCK.",
      connectorTopDistance:
        "Vertikaler Abstand von der Oberkante des Nebenträgers zur Verbindereinheit (der Hauptträger bewegt sich mit). Wenn leer: bei bündigem Nebenträger gilt das c_min des gewählten LOCK; bei versetztem Nebenträger 0 (Verbinder bündig mit der Nebenträger-Oberkante). Für LOCKT135 ohne Vorbohrung beträgt das Minimum 5 mm. Bei Stützen-Hauptträger muss c_min erreicht werden: allein durch diesen Abstand bei bündigem Nebenträger, durch die Summe mit dem Versatz bei versetztem.",
      angleBeta:
        "Scherwinkel des Nebenträgers in der X-Z-Ebene, unabhängig vom Winkel α. Bestimmt die maximal zulässige Länge des Nebenträger-Befestigungsmittels: bis 20° gilt 48,35mm (Mini) / 65,7mm (Midi); darüber ist kein Befestigungsmittel kompatibel (Grenzwert 0).",
      connectorPairCount: "Anzahl der LOCK-Verbinderpaare im Anschluss",
      headerLock:
        "Ein breiteres Lock auf der Hauptträgerseite halbiert die Verbinder in der Stückliste und aktualisiert die Schrauben auf dieser Seite. Die Auswahl oder Änderung kann den LOCK STOP aus der Konfiguration entfernen: Das Ergebnis muss dann neu berechnet werden. Breiteres Lock und LOCK STOP schließen sich aus: Die Wahl des einen entfernt das andere.",
      connectionCount: "Gesamtzahl identischer Anschlüsse im Tragwerk",
      connectorOneLock: "LOCK-Modell für Verbinder 1",
      connectorOneFinish: "Oberflächenausführung von Verbinder 1",
      connectorTwoLock: "LOCK-Modell für Verbinder 2 (bei zwei Verbinderpaaren)",
      connectorTwoFinish: "Oberflächenausführung von Verbinder 2",
      headerFastener: "Befestigungsmittel auf der Hauptträgerseite",
      joistFastener: "Befestigungsmittel auf der Nebenträgerseite",
      upliftFastener: "Befestigungsmittel für den Abhebenachweis",
      lockstop: "LOCK STOP Element für Quertragfähigkeit",
      lockstopWidth: "Breiteneinstellung für LOCK STOP Element (mm)",
      fvD: "Vertikale Querkraft (Bemessungswert)",
      fupD: "Abhebekraft (Bemessungswert)",
      faxD: "Axialkraft (Bemessungswert)",
      flatD: "Querkraft senkrecht zum Träger (Bemessungswert)",
      combined:
        "Kombinierter Nachweis: (Fax,Ed / Rax,d)² + (Fv,Ed / Rv,d)² ≤ 1. Wählen Sie die einzubeziehenden Lastrichtungen.",
    },
    notices: {
      title: "Hinweise",
      goToField: "Zum Feld",
      goToAction: "Zur Schaltfläche",
      count: (n: number) => (n === 1 ? "1 Hinweis" : `${n} Hinweise`),
      severity: { error: "Fehler", warning: "Warnung", info: "Info" },
      axes: { vertical: "vertikal (Fv)", uplift: "Abheben (Fup)", axial: "axial (Fax)", lateral: "lateral (Flat)" },
      members: { header: "Hauptträger", joist: "Nebenträger" },
      reasons: {
        columnWidth: "die Stützenbreite",
        topDistance: "den Abstand des Verbinders zur Oberkante",
        joistWidth: "die Nebenträgerbreite",
        assemblyHeight: "die Querschnittshöhen",
        evoRequired: "die EVO-Anforderung der Nutzungsklasse 3",
      },
      coefficients: { kmod: "kmod", gammaM: "γM (Verbindung)", gammaMAluminium: "γM (Aluminium)" },
      slots: {
        header: "Verbindungsmittel im Hauptträger",
        joist: "Verbindungsmittel im Nebenträger",
        inclined: "geneigtes Verbindungsmittel",
      },
      inputs: {
        standardId: "Norm",
        serviceClassId: "Nutzungsklasse",
        loadDurationClassId: "Klasse der Lasteinwirkungsdauer",
        "connectors[0].lockItemId": "erster Verbinder",
        "connectors[1].lockItemId": "zweiter Verbinder",
        headerFastenerItemId: "Verbindungsmittel im Hauptträger",
        joistFastenerItemId: "Verbindungsmittel im Nebenträger",
        lockstopItemId: "LOCK STOP",
        headerMaterialGradeId: "Material des Hauptträgers",
        joistMaterialGradeId: "Material des Nebenträgers",
        headerOrientation: "Ausrichtung des Hauptträgers",
        alphaDeg: "Winkel α",
        headerH: "Höhe des Hauptträgers",
        headerB: "Breite des Hauptträgers",
        joistB: "Breite des Nebenträgers",
        joistH: "Höhe des Nebenträgers",
        anyLoad: "mindestens eine Last",
        inclinedFastenerItemId: "geneigtes Verbindungsmittel",
        lateralResistanceConfig: "Querwiderstand",
        kmod: "kmod",
        gammaM: "γM",
      },
      messages: {
        topDistanceBelowCMin: (p: NoticeParams) =>
          `Verbinderabstand ${p.distance} mm + Versatz des Nebenträgers ${p.offset} mm liegen unter dem Mindestmaß c_min des LOCK (${p.cMin} mm): die Konfiguration ist nicht nachgewiesen. Abstand oder Versatz erhöhen.`,
        topDistanceBelowLock135Min: (p: NoticeParams) =>
          `LOCKT135 an einem nicht vorgebohrten Nebenträger braucht mindestens ${p.min} mm von der Oberkante (jetzt ${p.distance} mm): die Konfiguration ist nicht nachgewiesen.`,
        serviceClassNotAllowed: (p: NoticeParams) =>
          `Die Nutzungsklasse ${p.classNumber} ist für das Material des Hauptträgers nicht zulässig (zulässig: ${p.allowed}): die Konfiguration ist nicht nachgewiesen.`,
        axisInputsMissing: (p: NoticeParams) =>
          `Für die Achse ${p.axis} liegt eine Last vor, sie lässt sich aber nicht berechnen: es fehlt ${p.missing}.`,
        noCompatibleLock: (p: NoticeParams) =>
          `Kein LOCK passt zu dieser Konstruktion: ${p.rejected} von ${p.total} wurden durch ${p.reason} ausgeschlossen.`,
        edgeDistanceUnavailable: (p: NoticeParams) =>
          `Die Mindestrandabstände (§11) sind für ${p.member} nicht verfügbar: die Dichte (${p.rhoK} kg/m³) liegt über dem EC5-Grenzwert von 500.`,
        customCoefficientsEnabled: () =>
          "kmod und γM lassen sich jetzt nur für diese Berechnung ändern: solange kein Wert geändert wird, gilt der normative.",
        coefficientOverrideDiffers: (p: NoticeParams) =>
          `${p.coefficient} beträgt ${p.value}; der normative Wert ist ${p.normative}.`,
        topDistanceEqualsDefault: () => "Es wird der Standardwert verwendet: das c_min des gewählten LOCK.",
        resultStale: (p: NoticeParams) =>
          `Die Eingaben haben sich seit der letzten Berechnung geändert: das Ergebnis der endgültigen Konfiguration ist möglicherweise veraltet. "${p.button}" drücken, um mit den aktuellen Daten neu zu berechnen.`,
        reportStale: (p: NoticeParams) =>
          `Die Eingaben wurden nach dem letzten Bericht geändert: Das angezeigte PDF ist nicht mehr aktuell. "${p.generateReport}" im Tab "${p.reportTab}" drücken.`,
        draftsStale: (p: NoticeParams) =>
          `Die Eingaben haben sich seit der Erzeugung der Konfigurationen geändert: "${p.button}" drücken, um die Liste zu aktualisieren.`,
        noDraftWithinLimits: (p: NoticeParams) =>
          `Die LOCK passen zur Konstruktion, aber keine erzeugte Konfiguration hält die Lasten ein. Lasten oder Materialien prüfen und dann erneut "${p.button}" drücken.`,
        generationInputsMissing: (p: NoticeParams) =>
          `"${p.generateAll}" und "${p.generateFromData}" sind nicht verfügbar: es fehlen ${p.missing}.`,
        manualGenerationInputsMissing: (p: NoticeParams) =>
          `"${p.generateManual}" ist nicht verfügbar: es fehlen ${p.missing}.`,
        noCompatibleFastener: () =>
          `Die LOCK passen zur Konstruktion, aber kein Verbindungsmittel ist mit ihnen kompatibel: Trägerhöhen, Winkel und Materialien prüfen.`,
        noCompatibleLockstop: () =>
          `Die LOCK passen zur Konstruktion, aber kein LOCK STOP ist mit ihnen kompatibel: einen anderen Querwiderstand wählen.`,
        combinedNeedsTwoAxes: (p: NoticeParams) =>
          `Der kombinierte Nachweis braucht mindestens zwei Achsen: nur die Achse ${p.axis} ist gewählt. Eine weitere auswählen.`,
        lateralConfigCorrected: (p: NoticeParams) =>
          `Der Querwiderstand wurde von "${p.fromConfig}" auf "${p.toConfig}" geändert: Erstere ist bei der Ausrichtung "${p.orientation}" des Hauptträgers nicht verfügbar.`,
        continuousControlCleared: () =>
          "Die kontinuierliche Produktionskontrolle wurde zurückgesetzt: sie gilt nicht für Eurocode 5.",
        secondConnectorCleared: () => "Der zweite Verbinder wurde entfernt: es ist nur ein Verbinderpaar gewählt.",
        lockstopClearedByHeaderLock: () =>
          "Der LOCK STOP und die Art des Seitenwiderstands wurden entfernt, weil ein breiteres Lock an der Hauptträgerseite gewählt wurde: Wählen Sie eine neue Art des Seitenwiderstands und berechnen Sie das Ergebnis neu.",
        headerLockClearedByLockstop: () =>
          "Das breitere Lock an der Hauptträgerseite wurde entfernt, weil ein LOCK STOP gewählt wurde: Das Ergebnis muss neu berechnet werden.",
        lockstopCleared: () =>
          "Der LOCK STOP wurde entfernt: der Querwiderstand ist nicht mehr auf LOCK STOP eingestellt.",
        combinedAxisDisabled: (p: NoticeParams) =>
          `Die Achse ${p.axis} wurde aus dem kombinierten Nachweis entfernt: es liegt keine Last vor.`,
        upliftConfigSetByLateral: () =>
          "Der Abhebewiderstand wurde wie beim Querwiderstand auf geneigte Verbindungsmittel gesetzt.",
        upliftConfigSetByLoad: () =>
          "Der Abhebewiderstand wurde auf geneigte Verbindungsmittel gesetzt, weil eine Abhebelast eingegeben ist.",
        inclinedFastenerCleared: () =>
          "Das geneigte Verbindungsmittel wurde entfernt: weder Quer- noch Abhebewiderstand nutzen geneigte Verbindungsmittel.",
        inclinedFastenerNotAllowed: () =>
          "Das geneigte Verbindungsmittel wurde entfernt: der gewählte LOCK nimmt keine geneigten Verbindungsmittel auf.",
        fastenerCleared: (p: NoticeParams) =>
          `Das ${p.slot} wurde entfernt: es ist mit der aktuellen Auswahl nicht kompatibel.`,
        finishForced: () => "Die Oberfläche wurde auf Aluminium gestellt: der gewählte LOCK hat keine EVO-Version.",
        finishSetByServiceClass: () => "Die Oberfläche wurde auf EVO gestellt: die Nutzungsklasse 3 verlangt es.",
        secondLockCleared: () => "Der zweite LOCK wurde entfernt: er passt nicht mehr zum ersten.",
        pairCountReduced: () => "Es wurde nur ein Verbinderpaar beibehalten: kein zweiter LOCK passt zum ersten.",
        topDistanceFollowedDefault: (p: NoticeParams) =>
          `Der Verbinderabstand ist dem neuen Standardwert des gewählten LOCK gefolgt: ${p.before} mm → ${p.after} mm.`,
        topOffsetFollowedDefault: (p: NoticeParams) =>
          `Der Versatz des Nebenträgers ist dem neuen Standardwert des gewählten LOCK gefolgt: ${p.before} mm → ${p.after} mm.`,
        connectorsInvalidated: () =>
          "Die gewählten LOCK wurden zurückgesetzt: sie waren für einen anderen Abstand zur Oberkante gewählt.",
      },
    },
    wizard: {
      stepOf: (current: number, total: number) => `Step ${current} of ${total}`,
      completeFieldsToContinue: "Füllen Sie alle Felder auf dieser Seite aus, um fortzufahren",
    },
    finish: {
      aluminium: "Aluminium",
      evo: "EVO",
      aluminiumShort: "ALU",
      evoShort: "EVO",
    },
    continuousCtrl: {
      active: "Active",
      inactive: "Inactive",
      standard: "Standard",
      cov: "COV",
      std: "STD",
    },
    results: {
      title: "Nachweisergebnisse",
      verified: "Nachgewiesen",
      utilizationRatio: "Auslastung",
      notYetCalculated: "Füllen Sie das Formular aus, um die Berechnung durchzuführen.",
      notVerified: "Nicht nachgewiesen",
      verifiedOk: "Nachweis erbracht",
      axes: {
        vertical: "Vertical (Fv)",
        uplift: "Uplift (Fup)",
        axial: "Axial (Fax)",
        lateral: "Lateral (Flat)",
      },
      load: "Load",
      resistance: "Resistance",
      eta: "η",
      failed: "FAILED",
      noLoad: "NO LOAD",
      formulaAltSingle: "η = Ed / Rd ≤ 1",
      formulaAltCombined: "Σ (Ed/Rd)² ≤ 1",
      finalConfiguration: "Endgültige Konfiguration",
      removeComparison: "Aus dem Vergleich entfernen",
      slipModulus: "Verschiebungsmodul",
    },
    generatedConfigurations: {
      title: "Generierte Konfigurationen",
      empty: "Es wurde noch keine Konfiguration generiert — füllen Sie Code & Norm, Lasten und Struktur aus.",
      lock: "Lock",
      fastener: "Schraube",
      combined: "Kombiniert",
      chosen: "Ausgewählt",
      compare: "Vergleichen",
      downloadData: "Daten herunterladen",
      load: "Laden",
      selectAll: "Alle Konfigurationen auswählen",
      selectRow: "Diese Konfiguration auswählen",
      search: "Konfigurationen suchen",
    },
    bom: {
      title: "Stückliste",
      noComponents: "Noch keine Komponenten konfiguriert.",
      component: "Komponente",
      code: "Code",
      quantity: "Menge",
      headerLock: "Breiteres Hauptträger-Lock",
    },
    report: {
      title: "PDF-Bericht",
      empty: "Wählen Sie eine Konfiguration, um den Berechnungsbericht zu erstellen.",
      error: "Der Bericht konnte nicht erstellt werden.",
      retry: "Erneut versuchen",
      generate: "PDF erstellen",
      download: "PDF herunterladen",
      fullscreen: "Vollbild",
      exitFullscreen: "Vollbild beenden",
      savingChanges: "Änderungen werden gespeichert: Der Bericht wird aus den gespeicherten Daten erstellt.",
      notGenerated: "Klicken Sie auf „PDF erstellen“, um den Berechnungsbericht zu erstellen.",
    },
    helpModal: {
      illustrationAlt: "Help illustration",
    },
    aria: {
      calculationSteps: "Calculation steps",
      calculationResults: "Calculation Results",
    },
    admin: {
      tabs: {
        reference: "Reference",
        catalog: "Catalog",
        coefficients: "Coefficients",
        connectors: "Lock",
      },
      catalog: {
        fasteners: "Fasteners",
        materialGrades: "Material Grades",
      },
      coefficients: {
        kmodValues: "Kmod Values",
        gammaMValues: "γM Values",
      },
      reference: {
        standards: "Standards",
        serviceClasses: "Service Classes",
        loadDurationClasses: "Load Duration Classes",
      },
      fasteners: {
        singular: "Fastener",
        diameter: "Ø (mm)",
        length: "Length (mm)",
        threadLength: "Thread Length (mm)",
        myRk: "My,Rk (kNm)",
        ftensK: "Ftens,K (kN)",
        headType: "Head Type",
      },
      materialGrades: {
        singular: "Material Grade",
        rhoK: "ρk (kg/m³)",
        rhoM: "ρm (kg/m³)",
      },
      standards: {
        singular: "Standard",
        jurisdictionCode: "Region",
        normativeType: "Normtyp",
        normativeTypes: {
          EUROCODE: "Eurocode",
          NATIONAL_CODE: "Nationale Norm",
          HARMONIZED_STANDARD: "Harmonisierte Norm",
          ETA: "ETA",
          DOP: "DoP",
        },
      },
      serviceClasses: {
        singular: "Service Class",
        classNumber: "Class #",
        nameIt: "Name IT",
        nameDe: "Name DE",
        nameFr: "Name FR",
        nameEs: "Name ES",
        description: "Description",
        typicalExamples: "Typical Examples",
        order: "Order",
      },
      loadDurationClasses: {
        singular: "Load Duration Class",
      },
      kmodValues: {
        singular: "Kmod Value",
        serviceClass: "Service Class",
        loadDurationClass: "Load Duration Class",
        kmod: "kmod",
      },
      gammaMValues: {
        singular: "γM Value",
        standard: "Standard",
        continuousCtrl: "Continuous Ctrl",
        notApplicable: "Not applicable (EC5)",
        gammaM: "γM",
      },
      connectors: {
        singular: "Lock Connector",
        family: "Family",
        nModules: "N. Modules",
        nInclinedUplift: "Inclined Fasteners (Uplift)",
        nInclinedLateral: "Inclined Fasteners (Lateral)",
        width: "Width (mm)",
        height: "Height (mm)",
        thickness: "Thickness (mm)",
        screwEdgeThickness: "Screw edge thickness (mm)",
        evoAvailable: "EVO Available",
        lockModule: "Lock Module",
        edgeDistance: "Edge Distance (mm)",
        assemblyHeight: "Assembly Height (mm)",
      },
      lockModules: {
        singular: "Lock Module",
        assemblyHeight: "Assembly height (mm)",
        sideHeight: "Side height (mm)",
        thickness: "Thickness (mm)",
        moduleWidth: "Module Width (mm)",
        joistFastenersPerModule: "Nebenträger-Befestiger / Modul",
        headFastenersPerModule: "Kopf-Befestiger / Modul",
        kLockFactor: "kLock",
        f1AluRk: "F1,alu,Rk (kN)",
        f2AluRkPerModule: "F2,alu,Rk / module (kN)",
        kh2PerModule: "kh2 / Modul",
        contactAreaALock: "Contact Area ALock (mm²)",
        cMin: "c_min (mm)",
        coefficients: "Lateral/Vertical Coefficients",
      },
      lockStops: {
        singular: "Lock Stop",
        fyK: "fy,k (MPa)",
        area: "Area (mm²)",
        fLockstopRk: "FLockstop,Rk (kN)",
        isSingle: "Single",
      },
      connectorAccessories: {
        singular: "Connector Accessory",
        quantity: "Qty",
        notes: "Notes",
        accessorySku: "Accessory SKU",
        connector: "Connector",
      },
      fastenerUsages: {
        singular: "Schraubenverwendung",
        fastener: "Schraube",
        usage: "Verwendung",
        usageStandard: "Standard (Hauptträger / Nebenträger)",
        usageInclined: "Geneigt",
        scope: "Auswahl nach",
        scopeFamilies: "Ganze Familien",
        scopeFasteners: "Einzelne Schrauben",
        families: "Familien",
        fasteners: "Schrauben",
        exceptions: "Ausnahmen (nicht hinzufügen)",
        resultAlreadyPresent: "Bereits zuvor hinzugefügt",
        resultAdded: "Neu hinzugefügt",
        resultNothingToDo: "Keine Elemente zum Hinzufügen oder Löschen",
        resultRemoved: "Waren vorhanden und wurden als Ausnahmen gelöscht",
      },
      lockLockstopMappings: {
        singular: "Lock → Lockstop",
        lock: "Lock",
        lockstop: "Lockstop",
        quantity: "Quantity",
        installationLevels: "Installationsebenen",
      },
      lockPairLockstopMappings: {
        singular: "Paar → Lockstop",
        lockA: "Lock A",
        lockB: "Lock B",
        lockstop: "Lockstop",
        quantity: "Gesamtmenge",
        installationLevels: "Installationsebenen",
      },
      model3d: {
        singular: "3D-Modell",
        quantity: "Menge",
      },
      emptyValue: "--",
    },
  },
  beamReinforcementModule: {
    tabs: {
      generalData: "Allgemeine Daten & Lasten",
      beams: "Balken",
      screw: "Schraube & Verbindung",
    },
    screwFamilies: {
      singular: "Schraubenfamilie",
      plural: "Schraubenfamilien",
      errors: {
        nameAlreadyPresent: "Eine Schraubenfamilie mit demselben Namen existiert bereits.",
      },
    },
    screws: {
      singular: "Schraube",
      plural: "Schrauben",
      errors: {
        nameAlreadyPresent: "Es existiert bereits eine Schraube mit demselben Namen.",
      },
      fields: {
        family: "Familie",
        headDiameter: "Kopfdurchmesser",
        threadedLength: "Gewindelänge",
        yieldMoment: "Fließmoment",
        tensileCapacity: "Zugkapazität",
      },
    },
    timbers: {
      singular: "Holzklasse",
      plural: "Holzklassen",
      errors: {
        nameAlreadyPresent: "Eine Holzklasse mit demselben Namen existiert bereits.",
      },
      fields: {
        density: "Dichte rho",
        bendingStrength: "Biegefestigkeit fmk",
        shearStrength: "Schubfestigkeit fvk",
        modulusOfElasticity: "Elastizitätsmodul E0,mean",
        isGlulam: "Brettschichtholz",
      },
    },
    drawing: {
      view2D: "2D-Ansicht",
      view3D: "3D-Ansicht",
      enable3DHint: "Geben Sie beide Balkenlängen ein, um die 3D-Ansicht zu aktivieren.",
    },
    calculation: {
      serviceClass: "Nutzungsklasse",
      serviceClass1: "1 — u ≤ 10%",
      serviceClass2: "2 — 10% < u ≤ 16%",
      serviceClass3: "3 — u ≥ 16%",
      loadDuration: "Lasteinwirkungsdauer",
      loadDurationPerm: "Ständig",
      loadDurationLong: "Lange Dauer",
      loadDurationMedium: "Mittlere Dauer",
      loadDurationShort: "Kurze Dauer",
      loadDurationInst: "Kurzzeitig",
      tipTolerance: "Spitzentoleranz",
      permanentLoad: "Ständige Last",
      liveLoad: "Verkehrslast",
      beamLength: "Balkenlänge",
      existingBeamTitle: "Vorhandener Balken",
      reinforcementBeamTitle: "Verstärkungsbalken",
      strengthClass: "Festigkeitsklasse",
      base: "Breite",
      height: "Höhe",
      lengthOptionalHint:
        "Optional. Wenn beide Balkenlängen definiert sind, kann die Vorschau zwischen 2D und 3D umschalten.",
      existingLength: "Länge des vorhandenen Balkens",
      reinforcementLength: "Verstärkungslänge",
      reinforcementSides: "Verstärkungsseiten",
      reinforcementSidesOne: "1 — Verstärkung auf einer Seite",
      reinforcementSidesTwo: "2 — Verstärkung auf beiden Seiten",
      screw: "Schraube",
      screwSpacing: "Schraubenabstand",
      preDrilled: "Vorgebohrtes Loch",
    },
    results: {
      summaryTitle: "Zusammenfassung der Nachweise",
      bendingUtilization: "Ausnutzungsgrad Biegung",
      shearUtilization: "Ausnutzungsgrad Schub",
      connectionUtilization: "Ausnutzungsgrad Verbindung",
      screwAdmissible: "Schraube zulässig",
      screwNotAdmissible: "Schraube NICHT zulässig",
      reasonGeometry: "Geometrie (l_ef / Länge / t₂)",
      reasonHeight: "h₂ < a₄,t + a₄,c",
      reasonSpacing: "Abstand < Mindestabstand a₁",
      reasonOverload: "η > 100%",
      incompleteWarning: "Füllen Sie alle Felder aus, um den Nachweis zu berechnen.",
      warningInsufficientPenetration:
        "Unzureichende Gewindeeinbindung in den vorhandenen Balken (l_ef = 0). Wählen Sie eine längere Schraube.",
      warningScrewTooLong: (max: number) =>
        `Die Schraube überschreitet die verfügbare Breite b₁+b₂−10 mm = ${max} mm: die Spitze tritt aus dem vorhandenen Balken aus.`,
      warningHeightInsufficient: (height: number, min: number) =>
        `Unzureichende Höhe: h₂ = ${height} mm < a₄,t + a₄,c = ${min} mm. Erhöhen Sie h₂ oder verringern Sie den Schraubendurchmesser.`,
      warningSpacingInsufficient: (spacing: number, min: number) =>
        `Der gewählte Abstand (${spacing} mm) liegt unter dem Mindestabstand a₁ = ${min} mm.`,
      warningConnectionOverload: (eta: number) =>
        `Verbindung nicht nachgewiesen: η = ${eta}% > 100%. Verringern Sie den Abstand oder wählen Sie eine größere Schraube.`,
    },
    details: {
      title: "Berechnungsdetails",
      loadsSection: "Beiwerte, Lasten und Einbindetiefe",
      kmod: "k_mod",
      selfWeight: "Eigengewicht g₁",
      designLoad: "Bemessungslast q_d",
      spacingSection: "Mindest- und effektive Abstände (EC5 §8.3)",
      minimum: "Minimum",
      effective: "effektiv",
      outcome: "Ergebnis",
      stiffnessSection: "Steifigkeitsverteilung",
      bendingSection: "Biegenachweis",
      shearSection: "Schubnachweis",
      axialSection: "Axiale Tragfähigkeit der Schraube",
      johansenSection: "Verbindungstragfähigkeit (Johansen)",
      connectionSection: "Verbindungsnachweis",
      existingBeamLabel: "Vorhandener Balken (1)",
      reinforcementBeamLabel: "Verstärkungsbalken (2)",
      designMoment: "Bemessungsmoment M_d",
      designShear: "Bemessungsquerkraft V_d",
      bendingStress: "Biegespannung σ_m,d",
      bendingStrength: "Biegefestigkeit f_m,d",
      shearStress: "Schubspannung τ_d",
      shearStrength: "Schubfestigkeit f_v,d",
      screwCount: "Schrauben über die Spannweite",
      designShearForce: "Bemessungsquerkraft pro Schraube V_Ed",
      connectionResistance: "Verbindungstragfähigkeit F_v,Rd",
      yes: "Ja",
      no: "Nein",
    },
  },
  screwConnectorModule: {
    tabs: {
      screwFamilies: "Schraubenfamilien",
      screwDiameters: "Schraubendurchmesser",
      screws: "Schrauben",
      washers: "Unterlegscheiben",
      timberMaterials: "Holzwerkstoffe",
      normativeTables: "Normtabellen",
    },
    screwFamilies: {
      singular: "Schraubenfamilie",
      plural: "Schraubenfamilien",
      errors: {
        nameAlreadyPresent: "Es existiert bereits eine Schraubenfamilie mit demselben Namen.",
      },
      fields: {
        description: "Beschreibung",
        threadType: "Gewindetyp",
        material: "Material",
        coating: "Beschichtung",
        ccCode: "CC-Code",
        tcCode: "TC-Code",
        scEuCode: "SC (EU) Code",
        ecUsCode: "ESR / EC (US) Code",
        wdUsCode: "WD (US) Code",
        standardReference: "Normreferenz",
        connectionTypes: "Verbindungsarten",
        substrates: "Untergründe",
      },
    },
    threadTypes: {
      partial: "Teilgewinde",
      full: "Vollgewinde",
    },
    headTypes: {
      countersunk: "Senkkopf",
      cylindrical: "Zylinderkopf",
      hexagonal: "Sechskantkopf",
      round_plate: "Rundkopf (Plattenbefestigung)",
      pan_plate: "Kegelkopf (Plattenbefestigung)",
      reinforced_pan_plate: "Verstärkter Kegelkopf (Plattenbefestigung)",
      hexagonal_pan_plate: "Sechskant-Kegelkopf (Plattenbefestigung)",
    },
    tipTypes: {
      three_thorns: "3 THORNS",
      self_drilling: "Bohrspitze",
      standard: "Standardspitze",
      hardwood: "Hartholzspitze",
    },
    connectionTypes: {
      shear_screws: "Scherschrauben",
      steel_to_timber: "Stahl-Holz",
      timber_to_timber: "Holz-Holz",
    },
    substrateCategories: {
      solidwood_softwood: "Vollholz - Nadelholz",
      glulam_softwood: "Brettschichtholz - Nadelholz",
      clt_softwood: "BSP - Nadelholz",
      lvl_softwood: "LVL - Nadelholz",
      solidwood_hardwood: "Vollholz - Laubholz",
      lvl_hardwood: "LVL - Laubholz",
      panels: "Platten",
      steel: "Stahl",
    },
    compatibilityLevels: {
      notCompatible: "Nicht kompatibel",
      restricted: "Eingeschränkt kompatibel",
      compatible: "Kompatibel",
    },
    washers: {
      singular: "Unterlegscheibe",
      plural: "Unterlegscheiben",
      fields: {
        code: "Code",
        innerDiameter: "Innendurchmesser",
        outerDiameter: "Außendurchmesser",
        height: "Höhe",
      },
    },
    screwDiameters: {
      singular: "Schraubendurchmesser",
      plural: "Schraubendurchmesser",
      filterByFamily: "Nach Schraubenfamilie filtern",
      selectFamilyHint: "Wählen Sie eine Schraubenfamilie aus, um deren Durchmesser zu verwalten.",
      fields: {
        screwFamily: "Schraubenfamilie",
        diameter: "Durchmesser",
        coreDiameter: "Kerndurchmesser",
        threadOuterDiameter: "Gewindeaußendurchmesser",
        headDiameter: "Kopfdurchmesser",
        headType: "Kopftyp",
        tipType: "Spitzentyp",
        bit: "Bit",
        yieldMoment: "Fließmoment",
        tensileCapacity: "Zugtragfähigkeit",
        washers: "Kompatible Unterlegscheiben",
        resistanceValues: "Festigkeitswerte",
      },
    },
    resistanceValues: {
      fields: {
        resistanceParam: "Parameter",
        substrateCategory: "Untergrundkategorie",
        substrateSpecies: "Holzart des Untergrunds",
        preDrilled: "Vorgebohrt",
        valueNMm2: "Wert",
        referenceDensity: "Referenzdichte",
        densityRange: "Dichtebereich",
      },
      params: {
        fax_k: "f_ax,k",
        fhead_k: "f_head,k",
      },
      addRow: "Festigkeitswert hinzufügen",
    },
    screws: {
      singular: "Schraube",
      plural: "Schrauben",
      filterByDiameter: "Nach Schraubendurchmesser filtern",
      selectDiameterHint: "Wählen Sie einen Schraubendurchmesser aus, um dessen Schrauben zu verwalten.",
      fields: {
        screwDiameter: "Schraubendurchmesser",
        length: "Länge",
        partialThreadLength: "Teilgewindelänge",
        fullThreadExtraLength: "Zusätzliche Vollgewindelänge",
        tipLength: "Spitzenlänge",
      },
    },
    timberMaterials: {
      singular: "Holzwerkstoff",
      plural: "Holzwerkstoffe",
      fields: {
        materialCategory: "Materialkategorie",
        species: "Holzart",
        class: "Klasse",
        characteristicDensity: "Charakteristische Dichte ρk",
        meanDensity: "Mittlere Dichte ρmean",
        compressionStrength0: "Druckfestigkeit f_c,0,k",
        compressionStrength90: "Druckfestigkeit f_c,90,k",
        rollingShearStrength: "Rollschubfestigkeit f_r,k",
        shearStrength: "Schubfestigkeit f_v,k",
        standardReference: "Normreferenz",
      },
    },
    normativeTables: {
      title: "Normtabellen",
      loadDurationClasses: {
        singular: "Klasse der Lasteinwirkungsdauer",
        plural: "Klassen der Lasteinwirkungsdauer",
        fields: {
          code: "Code",
          name: "Name",
          order: "Reihenfolge",
        },
      },
      serviceClasses: {
        singular: "Nutzungsklasse",
        plural: "Nutzungsklassen",
        fields: {
          code: "Code",
          name: "Name",
        },
      },
      kmod: {
        title: "k_mod-Werte",
        hint: "Modifikationsbeiwert k_mod nach Nutzungsklasse und Lasteinwirkungsdauerklasse.",
      },
      gammaM: {
        title: "Teilsicherheitsbeiwerte γM",
        fields: {
          standard: "Norm",
          columnA: "Spalte A",
          columnB: "Spalte B",
        },
      },
    },
    errors: {
      familyAlreadyExists: "Eine Schraubenfamilie mit diesem Namen existiert bereits.",
      familyMissingName: "Der Name der Schraubenfamilie ist erforderlich.",
      familyNotFound: "Die Schraubenfamilie wurde nicht gefunden.",
      gammaMValueNotFound: "Der γM-Wert wurde nicht gefunden.",
      kmodValueNotFound: "Der kmod-Wert wurde nicht gefunden.",
      loadDurationClassAlreadyExists: "Eine Klasse der Lasteinwirkungsdauer mit diesem Code existiert bereits.",
      loadDurationClassMissingCode: "Der Code der Klasse der Lasteinwirkungsdauer ist erforderlich.",
      loadDurationClassNotFound: "Die Klasse der Lasteinwirkungsdauer wurde nicht gefunden.",
      screwAlreadyExists: "Der Artikel ist bereits eine Schraube.",
      screwDiameterNotFound: "Der Schraubendurchmesser wurde nicht gefunden.",
      screwNotFound: "Die Schraube wurde nicht gefunden.",
      serviceClassAlreadyExists: "Eine Nutzungsklasse mit diesem Code existiert bereits.",
      serviceClassNotFound: "Die Nutzungsklasse wurde nicht gefunden.",
      timberMaterialAlreadyExists: "Ein Holzwerkstoff mit dieser Kategorie und Klasse existiert bereits.",
      timberMaterialNotFound: "Der Holzwerkstoff wurde nicht gefunden.",
      washerAlreadyExists: "Eine Unterlegscheibe mit diesem Code existiert bereits.",
      washerMissingCode: "Der Code der Unterlegscheibe ist erforderlich.",
      washerNotFound: "Die Unterlegscheibe wurde nicht gefunden.",
    },
    units: {
      unitMm2: "mm²",
      unitNMm2: "N/mm²",
      unitKgM3: "kg/m³",
    },
    calculation: {
      shell: {
        navLabel: "Modulbereiche",
        openMenu: "Menü öffnen",
        closeMenu: "Menü schließen",
        inputsGroup: "Projektdaten",
        resultsGroup: "Ergebnisse",
        toolsGroup: "Werkzeuge",
        resultsView: "3D-Ansicht und Nachweise",
        resultsSolutions: "Kompatible Lösungen",
        resultsPositioning: "Schraubenanordnung",
        generateReport: "PDF-Bericht erstellen",
        reportQuestion: "Welche Berichtsversion?",
        reportCompact: "Kompakter Bericht",
        reportCompactHint: "ohne methodische Hinweise",
        reportFull: "Vollständiger Bericht",
        reportFullHint: "mit methodischen Hinweisen",
        exportDxf: "DXF exportieren (Detail)",
        dotOk: "Vollständig",
        dotBad: "Zu prüfen",
        dotPending: "Ausstehend",
        tutorial: "Tutorial",
      },
      tutorial: {
        dialogLabel: "Tutorial",
        stepOf: (step: number, total: number) => `Schritt ${step} von ${total}`,
        done: "Erledigt",
        redo: "Wiederholen",
        skip: "Tutorial überspringen",
        back: "Zurück",
        next: "Weiter",
        finish: "Fertig",
        confirmTitle: "Tutorial starten?",
        confirmText:
          "Eine kurze Vorführung der wichtigsten Funktionen an einem Beispiel: Jeder Schritt wird für Sie ausgeführt, weiter mit „Weiter“. Die Felder werden für die Vorführung geleert, die nur Beispieldaten verwendet und eine nachgewiesene Lösung zeigt; beim Schließen werden Ihre Daten genau so wiederhergestellt, wie sie waren. Während des Tutorials wird die Berechnung nicht gespeichert.",
        cancel: "Abbrechen",
        start: "Tutorial starten",
        steps: {
          intro: {
            title: "So füllen Sie die Berechnung aus",
            text: "Gehen Sie von oben nach unten vor: In jedem Schritt sehen Sie, wie die Maus über die Felder des Bereichs fährt und ein Beispiel einträgt. Zu Beginn sind θ, b1, d1, t1, b2, d2, t2, Fn, Fv und Fa leer: Solange Sie sie nicht ausfüllen, wird die Berechnung nicht ausgeführt.",
            note: "Die Felder wurden geleert: Beim Schließen des Tutorials werden Ihre Daten wiederhergestellt.",
          },
          connectionType: {
            title: "2 · Verbindungstyp und Winkel",
            text: "Wählen Sie die Art der Verbindung mit einem Klick auf eines der acht Symbole, jeweils mit dem Namen darunter (Holz-Holz, Holz-Holz / einschnittig beidseitig (3 Bauteile), Holz-Holz / zweischnittig (3 Bauteile), Stahl-Holz mit dem Stahlblech als Bauteil 1 oder Stahl-Holz / einschnittig beidseitig, mit zwei Stahlblechen und dem Holz in der Mitte, Platte-Holz / einschnittig, mit einer Holzwerkstoffplatte als Bauteil 1, Platte-Holz / einschnittig beidseitig, mit zwei Platten und dem Holz in der Mitte, oder Platte-Holz / zweischnittig, mit der Platte in der Mitte und dem Holz an den Seiten) und geben Sie den Winkel θ zwischen den beiden Bauteilen ein (von 1° bis 180°).",
          },
          element1: {
            title: "3 · Daten Bauteil 1",
            text: "(Bei 3 Bauteilen ist es das mittlere Bauteil.) Wählen Sie Material, Holzart und Klasse (bei Platte-Holz nur Holzwerkstoffplatten: Sperrholz, OSB/3-OSB/4, Spanplatten; bei BSP-Platten auch Lagen und Einschraubseite: Bauteil 1 nur Seite wide). Geben Sie Breite b1, Länge d1 (oder aktivieren Sie «Durchlaufendes Bauteil», wenn es kein Ende gibt) und Dicke t1 in mm ein.",
          },
          element2: {
            title: "4 · Daten Bauteil 2",
            text: "Dieselben Daten für Bauteil 2 (bei 3 Bauteilen gelten sie auch für Bauteil 3): Material, b2, d2 und Dicke t2 in mm. Bei BSP-Platten können Sie die Seite wide oder narrow wählen (narrow: eine einzige Schraubenreihe auf der Achse).",
          },
          threeMembers: {
            title: "5 · Verbindung mit 3 Bauteilen",
            text: "Mit «Holz-Holz / einschnittig beidseitig» oder «Holz-Holz / zweischnittig» wird Bauteil 1 zum mittleren Bauteil (belastet, um θ gedreht); die Bauteile 2 und 3 sind die beiden Seitenhölzer, fest und identisch: Die Daten von Bauteil 2 gelten auch für Bauteil 3. Im ersten Fall werden die Schrauben von beiden Seiten eingedreht (Kopf auf 2 und auf 3, Spitze im Mittelholz), der Widerstand verdoppelt sich (zwei einschnittige Verbindungen, eine pro Seite); die Spitzen beider Seiten dürfen sich überlappen oder, mit der Option zum Versetzen der Schrauben beider Seiten um 1d, wird Seite 3 versetzt. Im zweiten Fall durchdringt eine einzige Schraube die drei Bauteile (Kopf auf Bauteil 2, Spitze in Bauteil 3): zweischnittig nach EC5 Gl. 8.7 und ein einziges Schraubenraster. In beiden Fällen erscheint der Nachweis gegen Aufspalten von Bauteil 1. Mit «Stahl-Holz / einschnittig beidseitig» liegen die beiden Stahlbleche (Bauteile 1 und 3) seitlich am mittleren Holz (Bauteil 2): Schrauben von beiden Seiten wie im ersten Fall, Aufspalten im Holz. BSP (immer Seite wide) ist bei Holz-Holz beidseitig nur in einer Position zulässig (BSP-Holz-BSP oder Holz-BSP-Holz, nie dreimal BSP) und bei zweischnittigen Verbindungen nicht wählbar.",
            note: "Für die Vorführung wird der Verbindungstyp kurz eingestellt und anschließend auf den von Ihnen gewählten zurückgesetzt.",
          },
          panelTimber: {
            title: "6 · Verbindung Platte-Holz",
            text: "Mit «Platte-Holz / einschnittig» ist Bauteil 1 eine Holzwerkstoffplatte (Sperrholz, OSB/3-OSB/4, Spanplatten) auf der Seite des Schraubenkopfs, belastet und um θ gedreht; Bauteil 2 ist das Holz, fest. Es werden nur HBS- und TBS-Schrauben mit Kopf ≥ 2·d verwendet. Die Lochleibung der Platte folgt EC5 §8.3.1.3 (die ETA gibt sie nicht an), die Mindestabstände Tabelle 8.2 mit a1 und a2 ×0,85 an beiden Bauteilen, kmod ist die Wurzel aus dem Produkt der kmod-Werte von Platte und Holz. Mit «Platte-Holz / einschnittig beidseitig» liegen die Platten (Bauteile 1 und 3, identisch) seitlich am mittleren Holz (Bauteil 2): Schrauben von beiden Seiten, doppelter Widerstand, Aufspalten im Holz, wie bei Stahl-Holz beidseitig. Mit «Platte-Holz / zweischnittig» ist die Platte das mittlere Bauteil 1 und das Holz liegt an den Seiten (Bauteile 2 und 3, identisch): Eine einzige Schraube durchdringt die drei Bauteile, wie bei Holz-Holz zweischnittig (Gl. 8.7), ohne Nachweis gegen Aufspalten. Die Berechnung wird mit Angabe des Grundes angehalten, wenn die Plattendicke unter dem Minimum liegt (6 mm Sperrholz, 8 mm Spanplatten und OSB, in jedem Fall ≥ 1,2·d), wenn der Kopf kleiner als 2·d ist oder wenn die Nutzungsklasse für diese Platte nicht zulässig ist. Bei zweischnittigen Verbindungen ist BSP für die Seitenhölzer nicht wählbar; in der 3D-Ansicht werden die Platten in einem dunkleren Holz als Vollholz oder Brettschichtholz dargestellt.",
            note: "Für die Vorführung wird der Verbindungstyp kurz eingestellt und anschließend auf den von Ihnen gewählten zurückgesetzt.",
          },
          loads: {
            title: "7 · Bemessungslasten",
            text: "Geben Sie Fn (Kraft entlang der Achse von Bauteil 1), Fv (Abscheren, senkrecht) und Fa (Herausziehen, positiv, wenn die Schraube herausgezogen wird) in kN ein. Wählen Sie Nutzungsklasse und Lasteinwirkungsdauer: γM wird gemäß der Norm automatisch ausgefüllt.",
          },
          connection: {
            title: "8 · Wahl der Verbindung",
            text: "Wählen Sie Familie, Bohrlochart (vorgebohrt oder nicht), Durchmesser und Länge der Schraube: Die Menüs bieten nur Schrauben an, die mit Dicken und Materialien kompatibel sind. Verlangt die ETA ein Vorbohren (ρk über 550 kg/m³, 500 für Edelstahl, außer bei den Familien HARDWOOD, LBS und KKT), wird das Feld auf «Vorgebohrt» gesperrt und ein Hinweis erklärt den Grund. Mit «Unterlegscheibe verwenden» können Sie die Schraube verlängern. Erscheint «keine kompatible Schraube», ändern Sie die Dicken oder suchen Sie eine Lösung.",
          },
          grid: {
            title: "9 · Schraubenraster",
            text: "Legen Sie Reihen und Spalten von Schrauben fest: Sie sind auf das durch die Mindestabstände zulässige Maximum begrenzt. Mit «Schrauben im zulässigen Bereich verteilen» (standardmäßig aktiv, mit automatischen Mindestabständen) rücken die gewählten Reihen und Spalten an die Grenzen des zulässigen Bereichs, statt eng beieinander zu bleiben: Die tatsächlichen Abstände erscheinen unter den Feldern s1 und s2. Sie können die Schrauben auch versetzen, die Rastermitte verschieben oder die maximale Schraubenanzahl berechnen.",
          },
          view: {
            title: "10 · 3D-Ansicht und Nachweise",
            text: "Prüfen Sie die 3D-Ansicht und die Nachweise (Abscheren und Herausziehen): Das Ergebnis ist «Zulässig» oder «Nicht zulässig». Die Berechnungswerte und die Formeln stehen in den Ergebniskarten.",
          },
          solutions: {
            title: "11 · Kompatible Lösungen",
            text: "Sie wissen nicht, welche Schraube Sie verwenden sollen? Mit «Kompatible Lösungen suchen» schlägt das Programm die Schrauben und die Mindestanzahl vor, die alle Nachweise erfüllen. Die Optionen «Vorgebohrt» und «Unterlegscheibe» schränken die Suche ein; «Diese verwenden» übernimmt die Lösung.",
          },
          positioning: {
            title: "12 · Schraubenanordnung",
            text: "Hier sehen Sie, wo die Schrauben in Bezug auf die Ränder jedes Bauteils liegen: Die Zeichnungen geben die Maße vom Rand zur Schraubenachse an und die Tabelle listet die Abstände jeder Schraube auf. Jetzt öffnet sich die Karte und scrollt nach unten.",
            note: "Die Karte kehrt in ihren vorherigen Zustand (geöffnet oder geschlossen) zurück, wenn Sie zum nächsten Schritt gehen.",
          },
          reportPreview: {
            title: "13 · Berichtsvorschau",
            text: "Mit «Bericht anzeigen» sehen Sie den PDF-Bericht, den Sie herunterladen, in Ihrer Sprache: Jetzt sehen Sie, wie er sich öffnet und Seite für Seite nach unten scrollt. Mit der Leiste oben können Sie den Zoom ändern, auf Einzel- oder Doppelseite wechseln und die Vorschau in Schwarzweiß anzeigen.",
            note: "Fehlen Daten, werden für die Vorführung die Beispielwerte eingetragen (beim Schließen entfernt).",
          },
          reportPdf: {
            title: "14 · PDF-Bericht",
            text: "Wählen Sie unter Werkzeuge › PDF-Bericht erstellen die kompakte oder die vollständige Version; derselbe Befehl befindet sich in der Titelleiste und in der Karte Berechnungsbericht. Die Berechnungsdaten werden automatisch gespeichert: Sie müssen sie nicht manuell speichern.",
          },
          dxf: {
            title: "15 · DXF exportieren",
            text: "Unter Werkzeuge › DXF exportieren (Detail) oder mit der Schaltfläche «DXF exportieren» der Karte Berechnungsbericht laden Sie das Konstruktionsdetail für das CAD herunter: Vorderansicht der Bauteile, Ansichten mit den Randmaßen, Schrauben mit ihrem tatsächlichen Durchmesser, Schriftfeld und Positionstabelle (auch für die Verbindung mit 3 Bauteilen: Die versetzten Schrauben von Seite 3 liegen auf einem eigenen Layer; das Schriftfeld gibt bei verteilten Schrauben auch die tatsächlichen Abstände an). Die Zeichnung ist im Maßstab 1:1 und der Befehl ist nur bei nachgewiesener Verbindung aktiv.",
            note: "Im Tutorial wird die Datei nicht heruntergeladen: Der Cursor zeigt nur, wo sich der Befehl befindet.",
          },
        },
      },
      status: {
        pending: "Warten auf Daten",
        verified: "Nachgewiesen",
        notVerified: "Nicht nachgewiesen",
      },
      sections: {
        tipologia: "Verbindungstyp und Winkel",
        element1: "Daten Bauteil 1",
        element1Panel: "Daten Bauteil 1 (Platte)",
        element1Central: "Daten Bauteil 1 (Mittelholz)",
        element1PanelCentral: "Daten Bauteil 1 (mittlere Platte)",
        element13Steel: "Daten Bauteile 1 und 3 (Stahlbleche)",
        element13Panel: "Daten Bauteile 1 und 3 (Platten)",
        element2: "Daten Bauteil 2",
        element2Central: "Daten Bauteil 2 (Mittelholz)",
        element23: "Daten Bauteile 2 und 3 (Seitenhölzer)",
        loads: "Bemessungslasten",
        connection: "Wahl der Verbindung",
        grid: "Schraubenraster",
      },
      engine: {
        a4Reduce: {
          notThin: (t: string, fiveD: string) => `— nicht angewendet: Dicke t=${t}mm nicht kleiner als 5d=${fiveD}mm.`,
          spacing: (twentyFiveD: string, values: string) =>
            `— nicht angewendet: erfordert a1, a3,t und a3,c ≥ 25d=${twentyFiveD}mm (aktuell: ${values}).`,
        },
        block: {
          noScrew:
            "keine Schraube ist mit den eingegebenen Dicken und Materialien kompatibel: ändern Sie die Dicken t1/t2 oder die Materialien oder wählen Sie eine andere Familie/einen anderen Durchmesser.",
          length: (L: number, rule: string, values: string, washerH: number) =>
            `die Schraubenlänge (L=${L} mm) ist nicht mit den Dicken kompatibel: erforderlich ist ${rule} (${values}${washerH > 0 ? `, Scheibe h=${washerH} mm` : ""}).`,
          partialThread: (rule: string, aRule: { rule: string; values: string } | null, values: string) =>
            `Schraube mit Teilgewinde nicht mit den Dicken kompatibel (Kompatibilitätsregel): erforderlich ist ${rule}${aRule ? ` und ${aRule.rule} (hier ${aRule.values})` : ""} (hier ${values}).`,
          penetrationRule: (screw: string) =>
            `Schraube nicht mit den Dicken kompatibel (Kompatibilitätsregel zu Länge/Gewinde/Eindringtiefe für ${screw}).`,
          minSize: (screw: string) =>
            `${screw} nicht mit der Mindestdicke/-breite der Bauteile kompatibel (Kompatibilitätsregel): erhöhen Sie die Dicken oder wählen Sie einen kleineren Durchmesser.`,
          emptyFields: (tutorial: string) => `füllen Sie die noch leeren Felder aus. Siehe ${tutorial} für die geführte Eingabe.`,
          cltNarrowPenetration: (lt1: string, pen: string, min: string, neededL: string) =>
            `Eindringtiefe in die CLT-Platte (schmale Seite) L−${lt1} = ${pen} mm kleiner als das Minimum 10·d = ${min} mm nach ETA-11/0030: das Berechnungsverfahren ist nicht anwendbar. Erhöhen Sie die Schraubenlänge (L ≥ ${lt1}+10d = ${neededL} mm) oder wählen Sie eine Schraube mit kleinerem Durchmesser.`,
          penetration: (ptN: number, lt1: string, pen: string, min: string, neededL: string) =>
            `Eindringtiefe in Bauteil ${ptN} L−${lt1} = ${pen} mm nicht größer als das Minimum 4·d = ${min} mm: das Berechnungsverfahren ist nicht anwendbar. Erhöhen Sie die Schraubenlänge (L > ${lt1}+4d = ${neededL} mm) oder wählen Sie eine Schraube mit kleinerem Durchmesser.`,
        },
        cltWideOnly: {
          panelSides:
            "Platten-Holz-Verbindung auf zwei Seiten: Die Schrauben dringen von den beiden breiten Flächen des mittleren Holzes ein: nur die breite Seite ist zulässig.",
          steelSides:
            "Stahl-Holz-Verbindung auf zwei Seiten: Die Schrauben dringen von den beiden breiten Flächen des mittleren Holzes ein: nur die breite Seite ist zulässig.",
          three:
            "Verbindung mit drei Bauteilen: Die Schrauben durchdringen die Bauteile 2 und 3 von der breiten Fläche aus: nur die breite Seite ist zulässig.",
        },
        epsNote: {
          element1:
            '— Winkel zwischen Schraubenachse und Faserrichtung von Bauteil 1, NUR für f<sub>h,1,k</sub> (Lochleibung) verwendet: abweichend vom Kraft/Faser-Winkel für die Mindestabstände a1/a2, der unverändert bleibt. Deaktivieren Sie „automatisch“, um ihn manuell festzulegen.',
          element2: (elementRef: string) =>
            `— Winkel zwischen Schraubenachse und Faserrichtung von <span id="lbEps2n">${elementRef}</span>, NUR für f<sub>h,k</sub> (Lochleibung) verwendet: abweichend vom Kraft/Faser-Winkel für die Mindestabstände a1/a2 (φ<sub>2</sub> oben, verknüpft mit θ/Fn/Fv), der unverändert bleibt. Deaktivieren Sie „automatisch“, um ihn in beiden Fällen manuell festzulegen.`,
          cltWide:
            'CLT-Platte, breite Seite (ETA-11/0030): ε = Winkel zwischen Kraft und Faserrichtung der Außenschicht, verwendet in f<sub>h,k</sub> (Lochleibung wie bei Vollholz mit 110 % der Mindestrohdichte der Schichten). Automatisch = berechnet aus θ, F<sub>n</sub>/F<sub>v</sub> und Ausrichtung der Außenschicht; deaktivieren Sie „automatisch“, um ihn manuell festzulegen.',
        },
        layout: {
          spacingBelowMin: (spacing: string, min: string) =>
            `⚠ ${spacing} mm kleiner als der erforderliche Mindestwert (${min} mm).`,
          lengthBelowEndDistance: (name: string, current: number, required: string) =>
            `⚠ erhöhen Sie ${name} (aktuell ${current} mm): muss ≥ ${required} mm sein — die Bauteillänge darf nicht kleiner als der erforderliche Endabstand sein.`,
          cltHalfThickness: (d2: string, required: string, minThickness: string) =>
            `⚠ d2 = b2/2 = ${d2} mm ist kleiner als ${required} mm: erhöhen Sie die Plattendicke (erforderlich ist eine Gesamtdicke ≥ ${minThickness} mm) oder verringern Sie den Schraubendurchmesser.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— Zeilen und/oder Spalten auf ${rows}×${cols} reduziert, um den Abstand zwischen den Schrauben an den Rasterenden auf 2·b1 = ${max} mm zu begrenzen`,
        },
        maxScrews: {
          none: "<strong>Keine zulässige Anordnung</strong> — nicht einmal eine Schraube erfüllt gleichzeitig die Mindestabstände beider Bauteile.",
          max: (rows: number, cols: number, total: number) =>
            `<strong>Theoretisches Maximum: ${rows} Zeilen × ${cols} Spalten = ${total} Schrauben</strong>`,
          spacing: (s1: string, s2: string) =>
            ` (Abstand s<sub>1</sub>=${s1} mm entlang der Achse von Bauteil 1, s<sub>2</sub>=${s2} mm entlang der Achse von Bauteil 2 — diagonales Raster, normatives Minimum für beide Achsen)`,
          offset: (x: string, y: string) =>
            `— Raster aus der Mitte verschoben (Mittenverschiebung: horizontal=${x} mm, vertikal=${y} mm), um vollständig zu passen: ein REGELMÄSSIGES Raster (gleichmäßiger Abstand, keine Versetzung), das auf den Schnittpunkt der Achsen von Bauteil 1/Bauteil 2 zentriert ist, würde weniger enthalten, weil die zulässige Hüllkurve bezüglich dieses Schnittpunkts nicht symmetrisch ist.`,
          cornerCapped: (rows: number, cols: number, max: string) =>
            `— streifender Winkel (θ≤20° oder θ≥160°): Zeilen und/oder Spalten auf ${rows}×${cols} reduziert, um den Abstand zwischen den Schrauben an den Rasterenden auf 2·b<sub>1</sub> = ${max} mm zu begrenzen. Deaktivieren Sie das entsprechende Kontrollkästchen, um alle geometrisch zulässigen Zeilen/Spalten zu berücksichtigen.`,
          capped:
            "— Suche auf eine praktische Obergrenze von 500×500 begrenzt: beim Mindestabstand könnte die Anzahl zulässiger Schrauben in dieser Geometrie (Bauteil 2 mit unendlicher Oberseite und/oder Bauteil 1 mit unendlicher F-Seite) noch höher sein.",
        },
        panel: {
          notAdmitted: (classe: string) =>
            `die Platte «${classe}» ist nach ETA-11/0030 nicht zulässig (zulässig: Sperrholz EN 636, OSB/3-OSB/4, Spanplatten EN 312): wählen Sie ein anderes Material für Bauteil 1.`,
          noEmbedment: (d: string) =>
            `f_h,k der Platte für d = ${d} mm nicht bestimmbar (EC5 §8.5.1.2: für d > 8 mm nur Sperrholz, OSB und Spanplatten).`,
          noKmod: (classe: string, serviceClass: string) =>
            `k_mod der Platte «${classe}» ist in EC5 Tabelle 3.1 für die Nutzungsklasse ${serviceClass} nicht definiert: die Platte ist für diese Nutzungsklasse nicht geeignet.`,
          tooThin: (t1: string, minT: number, tMin: string) =>
            `Plattendicke t1 = ${t1} mm kleiner als das Minimum max(${minT} mm; 1,2·d) = ${tMin} mm (ETA-11/0030): das Berechnungsverfahren ist nicht anwendbar.`,
          smallHead: (dk: string, twoD: string) =>
            `Kopfdurchmesser dk = ${dk} mm kleiner als 2·d = ${twoD} mm: die Formeln EC5 §8.3.1.3 für f_h,k der Platten erfordern einen Kopf ≥ 2·d.`,
          onlyHbsTbs: "im Modul Platte-Holz sind nur HBS- und TBS-Schrauben zulässig.",
        },
        predrill: {
          reason: (rho: string, limit: number, element: number, hard: boolean, stainless: boolean) =>
            `ρk = ${rho} kg/m³ > ${limit} kg/m³ (Bauteil ${element}${hard ? ", Laubholz" : ""}, Schrauben aus ${stainless ? "Edelstahl" : "Kohlenstoffstahl"})`,
          forced: (reasons: string) => `— Vorbohren erforderlich (ETA-11/0030 S. 8 und 14): ${reasons}.`,
        },
        resistance: {
          ropeCap: (rp: string) =>
            ` — F<sub>ax</sub>/4 begrenzt auf 100 % des Johansen-Anteils (${rp} N, EC5 §8.2.2(2))`,
          mode: (mode: string) => `Modus (${mode})`,
          steelPlate: {
            thin: "dünnes Blech",
            thick: "dickes Blech",
            interp: "Blech mittlerer Dicke",
          },
        },
        results: {
          spreadNote: (s1: string, s2: string, s1Min: string, s2Min: string) =>
            `Schrauben über den zulässigen Bereich verteilt: s<sub>1</sub> = ${s1} mm, s<sub>2</sub> = ${s2} mm (erforderliche Mindestwerte ${s1Min} / ${s2Min} mm); die äußeren Schrauben liegen an den Mindestrandabständen.`,
          staggered: (lEff: string, limit: string, d: string, dir: string, element: number) =>
            `ℹ Schrauben der beiden Seiten versetzt: L−h = ${lEff} mm überschreitet ${limit} mm, daher sind die Schrauben von Bauteil 3 um 1·d = ${d} mm (Richtung ${dir}) gegenüber denen von Bauteil ${element} verschoben, damit sich die Spitzen im mittleren Bauteil nicht überlappen.`,
          tipsOverlap: (steel3: boolean, lEff: string, limit: string, tMinus: string, fourD: string, gridTitle: string) =>
            `ℹ Spitzen der beiden Seiten überlappen im mittleren ${steel3 ? "Holz" : "Bauteil"}: L−h = ${lEff} mm überschreitet ${limit} mm. Zulässig (EC5 §8.3.1.1(7)), weil (t − t2) = ${tMinus} mm > 4d = ${fourD} mm; um die Schrauben der beiden Seiten um 1·d zu versetzen, aktivieren Sie das Kontrollkästchen im Bereich ${gridTitle}.`,
          washerNet: (h: string, lEff: string) => ` — abzüglich der Scheibe (h=${h} mm): L−h=${lEff} mm`,
          screwTooLong: (l: string, rule: string, margin: string, twoSides: boolean) =>
            `⚠ Schraube zu lang (L=${l}): es muss L < ${rule} gelten (mindestens ${margin} mm von der Spitze zur gegenüberliegenden Fläche${twoSides ? " des mittleren Bauteils, EC5: (t − t2) > 4d" : ""}).`,
          screwTooShortClt: (l: string, penetration: string, tenD: string, need: string) =>
            `⚠ Schraube zu kurz (L=${l}): Eindringtiefe in die CLT-Platte (schmale Seite) ${penetration}, Minimum 10·d = ${tenD} mm (ETA-11/0030) — erforderlich ist ${need}.`,
          screwTooShort: (l: string, rule: string, element: number) =>
            `⚠ Schraube zu kurz (L=${l}): es muss ${rule} gelten (Mindesteindringtiefe 4d in Bauteil ${element}).`,
          cltInfo: (thickness: string, rhok: string, classe: string) =>
            `Gesamtdicke der Platte: ${thickness} mm · ρk = ${rhok} kg/m³ (Klasse ${classe})`,
          cltNarrowThin: (threshold: string) =>
            ` — ⚠ kleiner als 10·d = ${threshold} mm, erforderlich nach ETA-11/0030 Tab. B1 für das Einführen auf der schmalen Seite.`,
        },
        screwLists: {
          noFamily: (el1: string, el2: string) =>
            `⚠ keine kompatible Familie (Verbindungstyp/Nutzungsklasse) hat eine mit den aktuellen Dicken kompatible Schraube (${el1}mm, ${el2}mm): Dicke reduzieren.`,
          noDiameter: (el1: string, el2: string) =>
            `⚠ kein Durchmesser dieser Familie hat eine mit den aktuellen Dicken kompatible Länge (${el1}mm, ${el2}mm): Dicke reduzieren oder eine andere Familie wählen.`,
          noLength: (lower: string, upper: string, washerH: number, el1: string, el2: string) =>
            `⚠ keine Länge dieser Familie/dieses Durchmessers liegt zwischen ${lower} und ${upper} mm${washerH > 0 ? ` (mit Scheibe h=${washerH} mm)` : ""} (aktuelle Dicken ${el1}mm, ${el2}mm): Dicke reduzieren oder einen anderen Durchmesser wählen.`,
        },
        threeNotes: {
          twoSides:
            "Bauteil 1 ist mittig (belastet, um θ gedreht). Bauteile 2 und 3 sind seitlich, fest und identisch: Die Daten von Bauteil 2 gelten auch für Bauteil 3. Schrauben von den beiden Außenseiten eingeführt (Kopf auf Bauteil 2 und Bauteil 3, Spitze in Bauteil 1): gleiche Schraube und gespiegeltes Raster; die Last verteilt sich je zur Hälfte auf die beiden Verbindungen (eine pro Seite, jeweils einschnittig mit einer Scherfuge).",
          double:
            "Bauteil 1 ist mittig (belastet, um θ gedreht). Bauteile 2 und 3 sind seitlich, fest und identisch: Die Daten von Bauteil 2 gelten auch für Bauteil 3. Eine einzige Schraube durchdringt die drei Bauteile: Kopf auf Bauteil 2, Durchgang durch Bauteil 1, Spitze in Bauteil 3 (doppelte Scherfuge, EC5 Gl. 8.7): ein einziges Schraubenraster, keine Versetzung.",
          steelSides:
            "Bauteil 2 ist mittleres Holz und fest. Stahlbleche an den Seiten: Bauteil 1 und seine identische Kopie (Bauteil 3), belastet und um θ gedreht; die Daten von Bauteil 1 gelten auch für Bauteil 3. Schrauben von den beiden Außenseiten eingeführt (Kopf auf den Blechen, Spitze im Holz): gleiche Schraube und gespiegeltes Raster; die Last verteilt sich je zur Hälfte auf die beiden Verbindungen (eine pro Seite, jeweils einschnittig mit einer Scherfuge).",
          panelDouble:
            "Bauteil 1 = Holzwerkstoffplatte, mittig (belastet, um θ gedreht). Bauteile 2 und 3 sind Holz, seitlich, fest und identisch: Die Daten von Bauteil 2 gelten auch für Bauteil 3. Eine einzige Schraube (nur HBS und TBS, Kopf ≥ 2·d) durchdringt die drei Bauteile: Kopf auf Bauteil 2, Durchgang durch die Platte, Spitze in Bauteil 3 (doppelte Scherfuge, EC5 Gl. 8.7): ein einziges Schraubenraster, keine Versetzung. Lochleibung der Platte nach EC5 §8.3.1.3 / §8.5.1.2; Abstände mit a1, a2 ×0,85; kmod = √(kmod,Platte·kmod,Holz).",
          panelSides:
            "Bauteil 2 ist mittleres Holz und fest. Holzwerkstoffplatten an den Seiten: Bauteil 1 und seine identische Kopie (Bauteil 3), belastet und um θ gedreht; die Daten von Bauteil 1 gelten auch für Bauteil 3. Nur HBS- und TBS-Schrauben mit Kopf ≥ 2·d. Schrauben von den beiden Außenseiten eingeführt (Kopf auf den Platten, Spitze im Holz): gleiche Schraube und gespiegeltes Raster; die Last verteilt sich je zur Hälfte auf die beiden Verbindungen (eine pro Seite, jeweils einschnittig mit einer Scherfuge). Lochleibung der Platte nach EC5 §8.3.1.3 / §8.5.1.2; Abstände mit a1, a2 ×0,85; kmod = √(kmod,Platte·kmod,Holz).",
        },
        violations: {
          edge2: (code: string) => `Bauteil 2: Rand (${code})`,
          end2: (code: string) => `Bauteil 2: unteres Ende (${code})`,
          line1: (n: string, code: string) => `Bauteil 1: Linie ${n} (${code})`,
          side3: (text: string) => `Seite Bauteil 3 (um 1d versetzt): ${text}`,
        },
      },
      elementRefs: {
        element1: "Bauteil 1",
        element1Central: "Bauteil 1, mittig",
        elements13Steel: "Bauteile 1 und 3, Stahlbleche",
        elements13Panel: "Bauteile 1 und 3, Platten",
        element2: "Bauteil 2",
        element2Central: "Bauteil 2, Mittelholz",
        elements23: "Bauteile 2 und 3",
      },
      typeSection: {
        theta: "θ — Winkel Bauteil 1",
        thetaUnit: "0°→F nach unten · 180°→F nach oben · gegen den Uhrzeigersinn",
        connectionType: "Verbindungstyp",
        types: {
          legno: "Holz-Holz / einschnittig",
          legno3: "Holz-Holz / einschnittig beidseitig",
          legno3d: "Holz-Holz / zweischnittig",
          acciaio: "Stahl-Holz / einschnittig",
          acciaio3: "Stahl-Holz / einschnittig beidseitig",
          pannello: "Platte-Holz / einschnittig",
          pannello3: "Platte-Holz / einschnittig beidseitig",
          pannello3d: "Platte-Holz / zweischnittig",
        },
      },
      // Names of the engine's material type keys (and of the species fallbacks of panels/steel)
      materialTypes: {
        solidwoodSoftwood: "Nadelvollholz",
        glulam: "Brettschichtholz",
        clt: "BSP-Platten (CLT)",
        lvl: "LVL (Furnierschichtholz)",
        solidwoodHardwood: "Laubvollholz",
        panels: "Holzwerkstoffplatten",
        steel: "Stahl",
        panelsSpecies: "OSB- / zementgebundene Platten",
        steelSpecies: "Baustahl",
      },
      element: {
        materialType: "Materialtyp",
        materialTypeUnit: "Holz-DB",
        species: "Holzart",
        class: "Klasse",
        customName: "Materialname",
        customNameUnit: "benutzerdefiniert",
        customNamePlaceholder: "z. B. Spezialholz XYZ",
        rhoHint:
          "ρ<sub>k</sub> und ρ<sub>mean</sub> werden bei der Materialwahl aus der Datenbank übernommen und bleiben änderbar.",
        rhoMissing: "ρ<sub>k</sub> in der Datenbank nicht verfügbar, bitte manuell eingeben",
        fiber: (n: number) => `φ${n === 1 ? "₁" : "₂"} — Faserrichtung`,
        fiberUnit1: "bezogen auf die Achse von Bauteil 1",
        fiberUnit2: "bezogen auf die Achse von Bauteil 2",
        fiberUnit23: "bezogen auf die Achse der Bauteile 2 und 3",
        eps: (n: number) => `ε${n === 1 ? "₁" : "₂"} — Winkel Schraubenachse/Faser`,
        epsUnit1: "nur für fₕ,ₖ von Bauteil 1",
        epsUnit2: "nur für fₕ,ₖ von Bauteil 2",
        epsUnit23: "nur für fₕ,ₖ der Bauteile 2 und 3",
        epsAuto: (n: number) => `ε<sub>${n}</sub> automatisch`,
        epsAutoUnit1: "immer 90° (Schraube immer ⟂ zur Faser, unabhängig von θ)",
        epsAutoUnit2: "90° für Stahl-Holz (Schraube immer ⟂ zur Faser, unabhängig von θ); aus θ abgeleitet für Holz-Holz",
        continuous: "Durchlaufendes Bauteil",
        continuousUnit: "kein reales Ende: läuft auf beiden Seiten über die Verbindung hinaus",
        thickness: (n: number) => `Dicke t${n}`,
        rhomeanUnit: "kg/m³ — für Kₛₑᵣ",
        rhomeanMissing: "Für diese Klasse nicht in der Datenbank: bitte manuell eingeben",
        douglas: "Holzart Douglasie",
        douglasUnit: "ohne Vorbohren: ETA-11/0030 verlangt a1 und a3 ×1,5",
        a4reduce: (n: number) => `a4,c,${n} (unbeanspruchter Rand) auf 3d reduzieren`,
        a4reduceUnit1: "dünnes Holz, erfordert a1 und a3,t,1 ≥ 25d",
        a4reduceUnit2: "dünnes Holz, erfordert a1 und a3 ≥ 25d",
        a1e: (n: number) => `a1,Bauteil${n}`,
        a2e: (n: number) => `a2,Bauteil${n}`,
        a1Unit: "mm · Mindestabstand entlang der Achse",
        a2Unit: "mm · Mindestabstand rechtwinklig",
        a3t1: "a3,t,1 (beanspruchtes Hirnholzende)",
        a3t2Unit: "mm · Ende, 0°≤θ≤90°",
        a3c2Unit: "mm · Ende, 90°<θ≤180°",
      },
      clt: {
        lato: "Einschraubseite",
        latoOptions: {
          narrow: "Narrow (Schmalseite, Schraube parallel zur Plattenebene)",
          wide: "Wide (Breitseite, Schraube rechtwinklig zur Plattenebene)",
        },
        latoHint1: "Bauteil 1 in einer Holz-Holz-Verbindung: nur die Breitseite (wide) ist zulässig.",
        latoHint2:
          "Schmalseite: b<sub>2</sub> = Gesamtdicke der Platte und d<sub>2</sub> = b<sub>2</sub>/2 (automatisch); t<sub>2</sub> (Einschraubtiefe) ist änderbar.",
        numStrati: "Anzahl der Lagen",
        disposizione: "Lagenaufbau",
        disposizioni: {
          fisso: "Fest (abwechselnd 0°/90°)",
          alternati: "Abwechselnd",
          accoppiati: "Gepaart (0° gepaart an den Außenlagen)",
          zeroDoppio: "Alle 0°-Lagen doppelt (nur 11 Lagen)",
          userDefined: "Benutzerdefiniert (je Lage)",
        },
        spessoriMode: "Lagendicke",
        spessoriModes: {
          uniforme: "Einheitlich (gleiche Dicke für alle Lagen)",
          differenziato: "Unterschiedlich (0° / 90°)",
        },
        spessoreSingle: "Dicke einer Lage",
        spessore0: "Dicke der 0°-Lagen",
        spessore90: "Dicke der 90°-Lagen",
        classe: "Festigkeitsklasse Nadelvollholz",
        userLayers: "Lagen — Dicke, Richtung, Klasse",
        userLayersUnit: "von außen nach innen",
        layer: (i: number) => `Lage ${i}`,
        layerThickness: "Dicke (mm)",
        layerDirection: "Faserrichtung",
        layerClass: "Festigkeitsklasse Vollholz",
      },
      loadsSection: {
        fn: "Fn, Normalkraft",
        fnUnit: "kN · entlang der Achse von Bauteil 1",
        fv: "Fv, Querkraft",
        fvUnit: "kN · rechtwinklig zur Achse von Bauteil 1",
        fa: "Fa, Auszugskraft",
        faUnit: "kN · entlang der Schraubenachse, positiv = Auszug",
        resultantAngle: "Winkel Resultierende/Faser",
        withdrawal: "Auszug",
        notYetVerified: "noch nicht auf Herausziehen nachgewiesen",
        serviceClass: "Nutzungsklasse",
        loadDuration: "Lasteinwirkungsdauer",
        gammaMRef: "Bezug γM",
        gammaMRefUnit: "Verbindungen",
        standards: {
          EC5: "EC5 (Tabelle 2.3 — Verbindungen)",
          NTC2018: "NTC2018 (Tab. 4.4.III — Verbindungen)",
        },
        column: "Spalte",
        columns: {
          A: "A — Serienfertigung",
          B: "B — mit zertifizierter WPK",
        },
        gammaMAuto: "γ<sub>M</sub> automatisch (aus der gewählten Norm)",
      },
      connectionSection: {
        family: "Schraubenfamilie",
        foro: "Bohrlochart",
        predrilled: "Vorgebohrt",
        nonPredrilled: "Nicht vorgebohrt",
        diameter: "Gewindeaußendurchmesser d",
        length: "Schraubenlänge L",
        lengthUnit: "mm · aus der Schraubendatenbank",
        washer: "Unterlegscheibe verwenden",
        washerUnit:
          "erhöht den wirksamen Kopfdurchmesser (d<sub>h</sub>) beim Nachweis des Kopfdurchziehens (F<sub>ax,ε,Rk</sub>)",
        autoSpacing: "Automatische Mindestabstände (a1, a2, a3, a4 nach Norm)",
      },
      notes: {
        panel:
          "Element 1 = Holzwerkstoffplatte (Sperrholz, OSB/3-OSB/4, Spanplatten), auf der Schraubenkopfseite, belastet und um θ gedreht. Element 2 = Holz, fest. Nur HBS- und TBS-Schrauben mit Kopf ≥ 2·d. Einschraubtiefe in der Platte nach EC5 §8.3.1.3 (Gl. 8.20–8.22) oder §8.5.1.2 (Gl. 8.36–8.37) für d &gt; 8 mm; Mindestabstände in der Platte nach EC5 Tabelle 8.2 mit α = 0 und ohne Abhängigkeit von ρ<sub>k</sub>, sowie a<sub>1</sub>, a<sub>2</sub> ×0,85 bei Platte und Holz (§8.3.1.3); k<sub>mod</sub> = √(k<sub>mod,Platte</sub>·k<sub>mod,Holz</sub>) (§2.3.2.2).",
        continuous: (n: 1 | 2): string =>
          n === 1
            ? "— d1 und a3,t,1 werden nicht mehr benötigt (sie verlieren ihre Bedeutung: es gibt kein reales Ende). Die Schraubenpositionierung wird direkt durch ein virtuelles, inneres und verborgenes Ende begrenzt, gleich b2/(2·|cos(θ)|), gemessen vom Schnittpunkt der Achsen der beiden Elemente."
            : "— d2 und a3,t,2/a3,c,2 werden nicht mehr benötigt (sie verlieren ihre Bedeutung: es gibt kein reales Ende). Die Schraubenpositionierung wird direkt durch ein virtuelles, inneres und verborgenes Ende begrenzt, gleich b1/(2·|cos(θ)|), gemessen vom Schnittpunkt der Achsen der beiden Elemente.",
        continuousDisabled:
          "— nicht verfügbar bei θ=0°/180°: die Achsen der beiden Elemente fallen zusammen (Element 1 und Element 2 lägen auf derselben Geraden).",
        steel1Spacing:
          "— Element 1 als Stahlblech: ein Blech hat keine eigene Faser, daher sind a1/a2 von Element 1 keine physikalische Anforderung und beeinflussen den Rasterabstand nicht (EC5 §8.3.1.4): s1/s2 hängen nur von a1,Element2/a2,Element2 ab (bereits ×0,7).",
        steel1Dist:
          "— Element 1 als Stahlblech: a4,t,1 = a4,c,1 = 1,5·1,1·d, a3,t,1 = 1,2·1,1·d (Mindestlochabstand im Blech, nicht Tabelle 8.2 für Holz)",
        a3t1Rule:
          "— Regel: Keine Schraube darf näher als a3,t,1 am realen Ende von Element 1 liegen (Linie 3 in der Zeichnung), weder in Holz noch in Stahlblech — Schrauben in diesem Bereich werden rot markiert und zählen nicht für die Tragfähigkeit.",
        foro:
          '— "Ohne Vorbohren": Die Formel für die Mindestabstände wird automatisch aus der oben gewählten Schraubenfamilie abgeleitet (KKT für KKT-Familien, HBS-Formel für HBS-Familien mit d = 6…12 mm, EC5 Tabelle 8.2 für alle anderen Schrauben und Durchmesser, gemäß ETA-11/0030).',
        plateHw:
          "— Familie HBS PLATE / LBS HARDWOOD EVO für Holz-Holz-Verbindung: ETA-11/0030 verlangt a1 und a2 ×1,5 an beiden Elementen.",
        myk:
          "— My,k für diese Familie/diesen Durchmesser nicht in der Schraubendatenbank vorhanden: einige Johansen-Bruchbilder werden nicht berücksichtigt, das Ergebnis ist möglicherweise nicht konservativ. Daten in Screw.xlsx prüfen oder ergänzen.",
        limitGrid:
          "— Grenze deaktiviert: Sie können mehr Schrauben eingeben als geometrisch zulässig; die außerhalb der Hüllkurve bleiben rot markiert und im Feld darunter, auf eigenes Risiko",
      },
      gridSection: {
        rows: "Reihen (entlang der Achse von Bauteil 1)",
        rowsUnit: "max. aus θ/Schraube",
        cols2: "Spalten (entlang der Achse von Bauteil 2)",
        cols23: "Spalten (entlang der Achse der Bauteile 2 und 3)",
        cltRowNote: "Bauteil 2 aus BSP, Schmalseite: eine einzige Schraubenreihe in der Achse der Dicke von Bauteil 2.",
        s1Unit: "mm · entlang der Achse von Bauteil 1",
        s2Unit2: "mm · entlang der Achse von Bauteil 2",
        s2Unit23: "mm · entlang der Achse der Bauteile 2 und 3",
        offsetX: "Verschiebung Mittelpunkt, horizontal",
        offsetY: "Verschiebung Mittelpunkt, vertikal",
        spread: "Schrauben im zulässigen Bereich verteilen (maximaler Abstand, nur mit automatischen Mindestabständen)",
        stagger: "Schrauben der faserparallelen Reihe um 1d versetzen (vermeidet die Abminderung n<sub>ef</sub>)",
        staggerTwoSides:
          "Schrauben beider Seiten um 1d versetzen, wenn sich die Spitzen überlappen (sonst bleiben sie überlappt, sofern (t − t<sub>2</sub>) &gt; 4d)",
        maxScrews: "Maximale Schraubenanzahl berechnen",
        applyMax: "Reihen/Spalten übernehmen",
        limitGrid: "Reihen/Spalten auf die maximal zulässige Anzahl begrenzen (aus θ und Schraubendurchmesser)",
        limitGrazing:
          "Bei streifendem Winkel (θ≤20° oder θ≥160°) den Abstand zwischen den äußersten Schrauben des Rasters auf 2×b<sub>1</sub> begrenzen",
      },
      view: {
        title: "3D-Ansicht",
        showDims: "Maße anzeigen",
        branchMeta: (theta: string, branch: string) => `θ = ${theta}°  ·  Bereich ${branch}`,
        branchPending: "Bereich —",
        hintControls:
          "Ziehen = drehen · Mausrad = zoomen · rechte Maustaste = verschieben · Menü Ansicht = Kameravoreinstellungen · Menü Elemente = Komponenten ein-/ausblenden.",
        hintTail: "der geometrische Kontext; die Schrauben sind das durch die Berechnung nachgewiesene Produkt. Schrauben außerhalb des zulässigen Bereichs sind rot.",
        hintSubjects: {
          two: "Die zwei Holzbauteile sind",
          three: "Die drei Holzbauteile sind",
          panel: "Die Platte und das Holz sind",
          steel3: "Die drei Bauteile (zwei Stahlbleche und das Holz) sind",
          panel3: "Die drei Bauteile (zwei Platten und das Holz) sind",
        },
        legend: {
          el2: "Bauteil 2 (Holz, fest)",
          el2Central: "Bauteil 2 (Mittelholz, fest)",
          el23: "Bauteile 2 und 3 (Holz, fest, identisch)",
          el1: "Bauteil 1 (um θ gedreht)",
          el1Panel: "Bauteil 1 (Platte, um θ gedreht)",
          el1Central: "Bauteil 1 (mittig, um θ gedreht)",
          el1PanelCentral: "Bauteil 1 (mittlere Platte, um θ gedreht)",
          el13Steel: "Bauteile 1 und 3 (Stahlbleche, um θ gedreht)",
          el13Panel: "Bauteile 1 und 3 (Platten, um θ gedreht)",
          screwOk: "zulässige Schraube",
          screwBad: "unzulässige Schraube",
          dims: "Maße (Eingabewerte)",
          fn: "Fn (Normalkraft)",
          fv: "Fv (Querkraft)",
          fa: "Fa (Auszug, entlang der Schraubenachse)",
        },
        toolbar: {
          views: {
            iso: "3D-Ansicht",
            front: "Vorderansicht",
            top: "Draufsicht",
            side: "Seitenansicht",
          },
          menuView: "Ansicht",
          menuElements: "Elemente",
          resetView: "Ansicht zurücksetzen",
          elements: {
            element1: "Bauteil 1",
            elements13: "Bauteile 1 und 3",
            element2: "Bauteil 2",
            elements23: "Bauteile 2 und 3",
            screws: "Schrauben",
            forces: "Kräfte",
            grid: "Raster",
            solid: "Fest",
            ghost: "Halbdurchsichtig",
            hidden: "Ausgeblendet",
          },
        },
        capture: {
          legibilityWarning: (lines: string) =>
            `Achtung: Einige Maße in den Ansichten des Berichts sind möglicherweise nicht lesbar (${lines}).`,
          legibilityLine: (view: string, clipped: number, overlapping: number) =>
            `${view}: ${clipped} Maße abgeschnitten, ${overlapping} überlappend`,
        },
      },
      stats: {
        layout: "Schraubenanordnung",
        layoutOk: "Zulässig",
        layoutBad: "Nicht zulässig",
        combined: "Kombinierte Beanspruchung (EC5 8.28)",
        fvRkScrew: "F<sub>v,Rk</sub> je Schraube",
        nefAxial: "wirksame Anzahl n (axial)",
        nefShear: "wirksame Anzahl n (Abscheren)",
        admissibleScrews: (ok: number, total: number, twoSides: boolean) =>
          `zul. Schrauben ${ok}/${total}${twoSides ? " je Seite (×2 Seiten)" : ""}`,
        fvRkUnion: "F<sub>v,Rk</sub> Verbindung",
        totalKn: "kN gesamt",
        faxRd: "F<sub>ax,Rd</sub>",
        kmodGammaM: "k<sub>mod</sub> · γ<sub>M</sub>",
        fvEd: "F<sub>v,Ed</sub>",
        fvRdUnion: "F<sub>v,Rd</sub> Verbindung",
        kser: "K<sub>ser</sub> je Scherfuge",
        kserUnit: "(GZG)",
        notAvailable: "k. A.",
        shearCheck: "Nachweis Abscheren F<sub>v,Ed</sub>/F<sub>v,Rd</sub>",
        axialCheck: "Nachweis Herausziehen F<sub>ax,Ed</sub>/F<sub>ax,Rd</sub>",
        splitCheck: "Nachweis Aufspalten des Mittelholzes",
        verified: "nachgewiesen",
        notVerified: "NICHT nachgewiesen",
      },
      violations: {
        title: "Schrauben außerhalb des zulässigen Bereichs",
        allOk: "Alle Schrauben halten die erforderlichen Mindestabstände ein.",
        blockedPrefix: "Berechnung nicht durchgeführt: ",
      },
      solutions: {
        title: "Kompatible Lösungen",
        intro:
          "Für jede aktuell kompatible Schraube (Familie/Durchmesser/Länge, nach den Filtern für Dicken/Nutzungsklasse/Verbindungstyp) wird die minimale Schraubenanzahl (Reihen×Spalten, zentriertes Raster) gesucht, die alle aktiven Nachweise erfüllt (Abscheren; kombinierte Beanspruchung und Herausziehen nur bei F<sub>a</sub>&gt;0). Aufwendige Berechnung: Sie startet auf Anforderung; nach der ersten Suche aktualisiert sie sich bei jeder Änderung der Eingabedaten selbst (die vorherige Tabelle wird gelöscht). Während der Suche werden \"Automatische Mindestabstände\" und \"Verschiebung Mittelpunkt\"=0 für alle Kandidaten vorübergehend erzwungen und danach der aktuelle Zustand wiederhergestellt.",
        predrilled: "Vorgebohrt",
        washer: "Unterlegscheibe",
        optionsHint:
          "Ohne Häkchen: nur Lösungen ohne Vorbohren und ohne Unterlegscheibe (die Formulareinstellungen zählen nicht; Schrauben, für die die ETA das Vorbohren vorschreibt, werden trotzdem mit Vorbohren nachgewiesen). Mit einem Häkchen: nur Lösungen mit dieser Option (mit \"Unterlegscheibe\" werden alle kompatiblen Unterlegscheiben geprüft).",
        search: "Kompatible Lösungen suchen",
        preparing: "Vorbereitung...",
        progress: (processed: number, total: number, found: number) =>
          `Auswertung läuft: ${processed}/${total} Kandidatenschrauben (${found} Lösungen bisher gefunden)...`,
        missingTitle: (count: number) =>
          count === 1
            ? "Lösungen können nicht erzeugt werden: bitte das angegebene Feld ausfüllen."
            : "Lösungen können nicht erzeugt werden: bitte die angegebenen Felder ausfüllen.",
        missingPanel: (panel: string) => `— Bereich «${panel}»`,
        none: (candidates: number) =>
          `Keine Lösung unter ${candidates} Kandidatenschrauben gefunden: keine Kombination aus Familie/Durchmesser/Länge/Raster erfüllt alle Nachweise innerhalb der geometrisch maximal zulässigen Schraubenanzahl.`,
        summary: (found: number, candidates: number, shown: number) =>
          `${found} Lösungen unter ${candidates} ausgewerteten Kandidatenschrauben gefunden — die ersten ${shown} werden angezeigt, sortiert nach % Abscheren (dann % kombiniert, dann % Herausziehen).`,
        selectNew:
          "Die aktuelle Konfiguration ist mit den eingegebenen Daten nicht mehr gültig: <b>die neue kompatible Lösung</b> in der Tabelle auswählen (Schaltfläche «Diese verwenden»).",
        columns: {
          family: "Familie",
          grid: "Reihen×Spalten (ges.)",
          options: "Optionen",
          shear: "η Abscheren",
          combined: "η kombiniert",
          withdrawal: "η Herausziehen",
        },
        predrilledOption: "vorgebohrt",
        apply: "Diese verwenden",
      },
      formulas: {
        noDiameter:
          'Daten nicht verfügbar (kein gültiger Schraubendurchmesser gewählt — siehe Warnung oben „Außengewindedurchmesser“).',
        staggerNef: (n: number) =>
          `Versatz ≥1d aktiv (EC5 Tabelle 8.1, Anmerkung 8): keine Abminderung, n<sub>ef</sub> = n = ${n} (zulässige Schrauben).`,
        nefAxial: (n: number, pow: string, ninety: string, result: string) =>
          `n<sub>ef,axial</sub> = max(n<sup>0,9</sup>; 0,9·n) — ETA-11/0030 (selbstbohrende Schrauben, Reihe von n Schrauben parallel zur Last) — n=${n} → max(${pow}; ${ninety}) = ${result}`,
        staggerNefShear: (n: number) =>
          `Versatz ≥1d aktiv (EC5 Tabelle 8.1, Anmerkung 8): keine Abminderung, n<sub>ef,Quer</sub> = n = ${n}.`,
        rowParallel: (
          nRow: number,
          a1: string,
          a1d: string,
          element: number,
          reps: number,
          extra: string,
        ) =>
          `Reihe von ${nRow} Schrauben parallel zur Last (tatsächlicher Abstand a1=${a1}mm=${a1d}d, Element ${element}), ${reps}-mal wiederholt (unabhängige Reihen)${extra}`,
        table81Steel: (a1Eff: string, ratio: string) =>
          ` (für Tabelle 8.1, Stahl-Holz: a<sub>1</sub>/0,7 = ${a1Eff}mm=${ratio}d)`,
        kefLine: (kef: string, n: number, nef0: string) =>
          `k<sub>ef</sub> = ${kef} (EC5 Tabelle 8.1) → n<sub>ef,0</sub> = n<sup>k<sub>ef</sub></sup> = ${n}<sup>${kef}</sup> = ${nef0}`,
        angleLine: (angle: string, panel: string, nef0: string, nRow: number, result: string) =>
          `tatsächlicher Winkel Kraft/Faser = ${angle}°${panel} → n<sub>ef,Quer</sub> = n<sub>ef,0</sub> + (n − n<sub>ef,0</sub>)·(Winkel/90°) = ${nef0} + (${nRow} − ${nef0})×(${angle}/90) = ${result}`,
        panelNoInterp: " (Platte-Holz: keine Interpolation mit dem Winkel, wie im Referenzbericht)",
        totalShear: (reps: number, result: string, crit: string) =>
          `n<sub>ef,Quer</sub> (gesamt) = ${reps} × ${result}${crit}`,
        critElement: (element: number) => ` (Element ${element}, größte Schraubenzahl in einer Reihe)`,
        doubleNote: "doppelte Scherung: dieselbe Schraube durchdringt die drei Elemente, 2 Scherfugen",
        steelNote: (sides: string) => `zwei einschnittige Verbindungen, eine pro Seite: ${sides} mit Mittelholz 2`,
        threeNote: "zwei einschnittige Verbindungen, eine pro Seite: Elemente 2 und 3 mit Element 1",
        sidePanels: "Platte 1 und Platte 3",
        sidePlates: "Blech 1 und Blech 3",
        govPlane: "maßgebend",
        kserNoRho:
          "ρ<sub>mean</sub> nicht verfügbar (N.D. in der Datenbank für diese Klasse): ρ<sub>mean</sub> manuell eingeben, um K<sub>ser</sub> zu berechnen.",
        kserLvlNote: (family: string) =>
          `<span class="report-formula">ETA-11/0030 — Schraube ${family} in LVL-Nadelholz, 5≤d≤10mm, unabhängig vom Winkel ε</span>`,
        kserPredrilled: "vorgebohrt",
        kserNotPredrilled: "nicht vorgebohrt",
        kserPanelRho: (rhok: string, rhoMean: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>Platte</sub>·ρ<sub>mean,2</sub>) = √(${rhok}×${rhoMean}) = ${rhoM} kg/m³ (ρ<sub>mean</sub> der Platte gleich ρ<sub>k</sub> angenommen)`,
        kserMean2: (rhoM: string) => `ρ<sub>mean,2</sub> = ${rhoM} kg/m³`,
        kserTimberRho: (rho1: string, rho2: string, rhoM: string) =>
          `ρ<sub>m</sub> = √(ρ<sub>mean,1</sub>·ρ<sub>mean,2</sub>) = √(${rho1}×${rho2}) = ${rhoM} kg/m³`,
        kserSteelNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Tabelle 7.1, wie für Nägel ${predrilled} — ${rho}; ×2,0 für Stahl-Holz-Verbindungen (EC5 §7.1(3))</span>`,
        kserTimberNote: (predrilled: string, rho: string) =>
          `EN 1995-1-1 §7.1/Tabelle 7.1, wie für Nägel ${predrilled} — ${rho}</span>`,
      },
      positioning: {
        title: "Schraubenpositionierung zu den Rändern",
        meta: "Element 1 und Element 2, Maße in mm",
        metaThree: "Element 1 (mittig) sowie Elemente 2 und 3, Maße in mm",
        metaSteel3: "Elemente 1 und 3 (Stahlbleche) und Element 2 (Mittelholz), Maße in mm",
        metaPanel3: "Elemente 1 und 3 (Platten) und Element 2 (Mittelholz), Maße in mm",
        notAvailable: "Schraubenpositionierung nicht verfügbar: Berechnung nicht durchgeführt.",
        notAvailableSee: "Schraubenpositionierung nicht verfügbar: Berechnung nicht durchgeführt (siehe Warnung rechts).",
        none: "Keine zulässige Schraube zu positionieren: Zeilen/Spalten oder Eingabedaten ändern.",
        missingDims: "Geometrische Daten vervollständigen, um die Schraubenpositionierung zu sehen.",
        captionElements13Plates: "Elemente 1 und 3 (identische Stahlbleche)",
        captionElements13Panels: "Elemente 1 und 3 (identische Platten)",
        captionElement1: "Element 1",
        suffixPlate: " (Stahlblech)",
        suffixPanel: " (Platte)",
        suffixCentral: " (mittig)",
        captionView1: " — Ansicht in der eigenen Ebene: Ende unten (entgegengesetzt zu F), F nach oben",
        captionView2: " — Ansicht in der eigenen Ebene: unteres Ende unten",
        captionElements23: "Elemente 2 und 3 (identisch)",
        captionElement2Timber: "Element 2 (Mittelholz)",
        captionElement2: "Element 2",
        continuousNoEnd: " · durchlaufendes Element: kein Endmaß",
        axisElement1: "Element 1",
        axisElements23: "Elemente 2 und 3",
        axisElement2: "Element 2",
        note: (p: {
          s1: string;
          s2: string;
          axis2: string;
          spread: boolean;
          s1Min: string;
          s2Min: string;
          rows: number;
          cols: number;
          staggered: boolean;
          nShow: number;
          nTotal: number;
          threeD: boolean;
          twoSides: boolean;
          steel3: boolean;
          panel1: boolean;
          stagger: boolean;
          d: string;
          dir: string;
        }) =>
          `Maße in mm, vom Elementrand zur Schraubenachse. Rasterabstände: s<sub>1</sub> = ${p.s1} mm (längs der Achse von Element 1), s<sub>2</sub> = ${p.s2} mm (längs der Achse von ${p.axis2})${p.spread ? ` — Schrauben bis an die Grenzen des zulässigen Bereichs verteilt (erforderliche Mindestabstände ${p.s1Min} / ${p.s2Min} mm)` : ""} · ${p.rows} × ${p.cols} Schrauben${p.staggered ? ", versetzt" : ", ausgerichtet (nicht versetzt)"} · ${p.nShow} zulässig${p.nShow < p.nTotal ? ` von ${p.nTotal}; Schrauben außerhalb der Hüllkurve sind rot und dürfen nicht montiert werden` : ""}${p.threeD ? " · Elemente 2 und 3 identisch: dieselbe Schraube durchdringt die drei Elemente (ein einziges Raster)" : ""}${p.twoSides ? ` · Elemente 2 und 3 identisch: die Schrauben von Element 3 sind die Spiegelkopie derer von Element 2 (${p.nShow} pro Seite, ${2 * p.nShow} insgesamt)${p.stagger ? `, um 1·d = ${p.d} mm versetzt (Richtung ${p.dir}), weil L−h größer als t2 + t1/2 ist: leere Kreise = Schrauben von Element 3 (nicht bemaßt)` : ""}` : ""}${p.steel3 ? ` · ${p.panel1 ? "Platten 1 und 3 identisch" : "Bleche 1 und 3 identisch"}: die Schrauben der Seite 3 sind die Spiegelkopie derer von Seite 1 (${p.nShow} pro Seite, ${2 * p.nShow} insgesamt)${p.stagger ? `, um 1·d = ${p.d} mm versetzt (Richtung ${p.dir}), weil L−h größer als t1 + t2/2 ist: leere Kreise = Schrauben von Blech 3 (nicht bemaßt)` : ""}` : ""}.`,
        tableNo: "Nr.",
        tableElements13: (plates: string) => `Elemente 1 und 3 (${plates})`,
        tableElement1: "Element 1",
        tableElements23: "Elemente 2 und 3",
        tableElement2: "Element 2",
        tableDistance: "Randabstand [mm]",
        tableEnd: "Ende",
        tableLeft: "links",
        tableRight: "rechts",
        tableMinMaxTitle: "Minimaler/maximaler Randabstand [mm]",
        tableMin: "Minimum",
        tableMax: "Maximum",
        tableFromEnd1: "Element 1 — vom Ende",
        tableFromLeft1: "Element 1 — vom linken Rand",
        tableFromLeft2: (el2: string) => `${el2} — vom linken Rand`,
        tableFromBottom2: (el2: string) => `${el2} — vom unteren Ende`,
        tableMoreThan40:
          "Bei mehr als 40 Schrauben werden die Koordinaten jeder Schraube nicht aufgelistet: es gelten die Außenmaße in der Abbildung und die Rasterabstände s<sub>1</sub>/s<sub>2</sub>.",
        platesWord: "Bleche",
        panelsWord: "Platten",
      },
      report: {
        title: "Berechnungsbericht",
        note: "Vorschau des PDF-Berichts, der gedruckt wird (Daten, Ansichten, Ergebnisse, methodische Hinweise).",
        versionLabel: "Berichtsversion",
        full: "Vollständig",
        fullHint: "(mit methodischen Hinweisen)",
        compact: "Kompakt",
        compactHint: "(ohne methodische Hinweise)",
        show: "Bericht anzeigen",
        hide: "Bericht ausblenden",
        generating: "Bericht wird erstellt…",
        generatePdf: "PDF-Bericht erstellen",
        exportDxf: "DXF exportieren",
        viewer: {
          toolbar: "Steuerung der Berichtsvorschau",
          previousPage: "Vorherige Seite",
          pageNumber: "Seitenzahl",
          nextPage: "Nächste Seite",
          zoomOut: "Verkleinern",
          zoom: "Zoom",
          zoomIn: "Vergrößern",
          fitWidthOption: (pct: number) => `Breite anpassen (${pct} %)`,
          fitPageOption: (pct: number) => `Seite anpassen (${pct} %)`,
          fitWidth: "An Breite anpassen",
          fitPage: "An Seite anpassen",
          continuous: "Fortlaufendes Scrollen",
          singlePage: "Einzelseite",
          twoPages: "Zwei Seiten nebeneinander",
          blackWhite: "Schwarz-Weiß-Vorschau",
          blackWhiteShort: "S/W",
          paginating: "Bericht wird umbrochen…",
        },
        previewError: "Die Berichtsvorschau konnte nicht erstellt werden: bitte erneut versuchen.",
      },
    },
  },

  envelopeDesignerModule: {
    resultCodes: {
      errors: {
        INVALID_REQUEST_BODY: "Ungültiger Anfrageinhalt.",
        INVALID_REQUEST_UNKNOWN_ACTION: "Ungültige Anfrage: unbekannte Aktion.",
        INVALID_REQUEST_NO_LAYERS: "Ungültige Anfrage: keine Schichten zu berechnen.",
        INVALID_REQUEST_NO_MATERIALS: "Ungültige Anfrage: Die Materialdatensätze fehlen.",
        INVALID_REQUEST_NO_MONTHLY_CLIMATE: "Ungültige Anfrage: Das Monatsklima (12 Temperaturen und Dampfdrücke) fehlt - laden Sie eine EPW-Datei.",
        INVALID_REQUEST_OUTSIDE_SIDE: "Monatliches Glaser-Verfahren: Die Anfrage gibt die Außenseite nicht an.",
        LAYERS_TOO_MANY: (p: { max: number }) => `Ungültige Anfrage: höchstens ${p.max} Schichten.`,
        MONTHLY_OUTSIDE_TEMPERATURE_INVALID: "Ungültige Eingabe: Eine mittlere monatliche Außentemperatur liegt außerhalb von -60 bis 60 °C.",
        MONTHLY_OUTSIDE_VAPOUR_PRESSURE_INVALID: "Ungültige Eingabe: Ein mittlerer monatlicher Außendampfdruck liegt nicht zwischen 0 und 10000 Pa.",
        MONTHLY_METHOD_UNKNOWN: "Ungültige Eingabe: unbekanntes Berechnungsverfahren.",
        MONTHLY_CONDENSATION_TOLERANCE_INVALID: "Ungültige Eingabe: Die Tauwassertoleranz muss 0 (exakt) oder 1e-9 sein.",
        MONTHLY_SERVER_ERROR: "Monatliches Glaser-Verfahren: Die Analyse ist auf dem Server fehlgeschlagen.",
        CALCULATION_SERVER_ERROR: "Die Berechnung ist auf dem Server fehlgeschlagen.",
        HUMIDITY_CLASS_INVALID: "Ungültige Eingabe: Die Feuchteklasse nach UNI EN ISO 13788 muss 1, 2, 3, 4 oder 5 sein.",
        INSIDE_TEMPERATURE_INVALID: "Ungültige Eingabe: Die Innentemperatur muss eine Zahl zwischen -10 und 50 °C sein.",
        INSIDE_RELATIVE_HUMIDITY_INVALID: "Ungültige Eingabe: Die relative Innenluftfeuchte muss eine Zahl über 0 und höchstens 100 % sein.",
        EN15026_MONTHLY_VALUES_MISSING: "Ungültige Eingabe: Das Innenklima nach EN 15026 Annex D benötigt die aus der Klimadatei berechneten Monatswerte (laden Sie die EPW-Datei erneut).",
        ASHRAE160_UNAVAILABLE: "Innenklima ASHRAE 160: noch nicht verfügbar - die Werte des vereinfachten Verfahrens sind nicht geprüft.",
        INSIDE_MODEL_UNKNOWN: "Ungültige Eingabe: unbekanntes Innenklimamodell.",
      },
      warnings: {} as Record<string, { title: string; detail: string }>,
    },
    inputErrors: {
      layer: (position: number, material: string) => `Schicht ${position} (${material})`,
      stud: (layer: string, material: string) => `${layer}, Ständer ${material}`,
      bad: { nan: "keine Zahl", infinite: "unendlich", negative: (value: number) => `negativ (${value})`, zero: "0" },
      messages: {
        layerThickness: (subject: string, got: string) => `${subject}: Die Dicke muss größer als 0 sein (erhalten: ${got}).`,
        layerLambda: (subject: string, got: string) => `${subject}: Die Wärmeleitfähigkeit λ muss größer als 0 sein (erhalten: ${got}).`,
        layerMu: (subject: string, got: string) => `${subject}: Die Wasserdampf-Diffusionswiderstandszahl μ muss größer als 0 sein (erhalten: ${got}).`,
        studWidth: (subject: string, got: string) => `${subject}: Die Breite muss größer als 0 sein (erhalten: ${got}).`,
        studSpacing: (subject: string, got: string) => `${subject}: Der Abstand muss größer als 0 sein (erhalten: ${got}).`,
        studDepth: (subject: string, got: string) => `${subject}: Die Tiefe muss größer als 0 sein (erhalten: ${got}).`,
        studDepthExceeds: (subject: string, _got: string, depthMM: number, thicknessMM: number) =>
          `${subject}: Die Tiefe (${depthMM} mm) übersteigt die Dicke ihrer Schicht (${thicknessMM} mm).`,
        studLambda: (subject: string) => `${subject}: Das Material hat keine gültige Wärmeleitfähigkeit λ.`,
        studMu: (subject: string) => `${subject}: Das Material hat keine gültige Wasserdampf-Diffusionswiderstandszahl μ.`,
        rsi: (_subject: string, got: string) => `Der innere Wärmeübergangswiderstand Rsi muss größer als 0 sein (erhalten: ${got}).`,
        rse: (_subject: string, got: string) => `Der äußere Wärmeübergangswiderstand Rse muss größer als 0 sein (erhalten: ${got}).`,
        tInt: (_subject: string, got: string) => `Die Innentemperatur muss eine Zahl sein (erhalten: ${got}).`,
        tExt: (_subject: string, got: string) => `Die Außentemperatur muss eine Zahl sein (erhalten: ${got}).`,
        rhInt: (_subject: string, got: string) => `Die relative Innenluftfeuchte muss zwischen 0 und 100 % liegen (erhalten: ${got}).`,
        rhExt: (_subject: string, got: string) => `Die relative Außenluftfeuchte muss zwischen 0 und 100 % liegen (erhalten: ${got}).`,
      },
    },
    admin: {
      materials: "Materialien",
      examples: "Beispielaufbauten",
      manufacturer: "Hersteller",
      variable: "variabel",
      deleteMaterialQuestion:
        "Möchtest du dieses Material wirklich löschen? Ein Material, das von einer Berechnung oder einem Beispiel verwendet wird, kann nicht gelöscht werden: blende es stattdessen aus.",
      material: "Material",
      subcategory: "Unterkategorie",
      category: "Kategorie",
      categories: "Kategorien",
      subcategories: "Unterkategorien",
      condCategories: "Tauwasserkategorien",
      rule: "Regel",
      condCategory: "Tauwasserkategorie (EN ISO 13788)",
      hatch: "Schraffur",
      thicknessMode: "Dickenmodus",
      defaultThickness: "Referenzdicke",
      knownThicknesses: "Verfügbare Dicken (durch Komma getrennt)",
      minThickness: "Mindestdicke",
      maxThickness: "Höchstdicke",
      sdCurveHint: "Nur feuchtevariable Bahnen: Punkte mit RH als Anteil 0-1 und Sd in Metern.",
      sectionMembrane: "Bahn",
      sectionTestDatabase: "Testdatenbank (hygrische Werte der Datei)",
      sectionCurves: "Gemessene Kurven",
      sectionProvenance: "Datenquellen",
      sdVariableMin: "Variabler Sd-Wert, min [m]",
      sdVariableMax: "Variabler Sd-Wert, max [m]",
      dryCupPerm: "Dry-Cup-Permeanz [US perm]",
      curvesHint: "Jede Kurve ist eine JSON-Liste von Punkten mit den in ihrem Feld gezeigten Schlüsseln (leer: keine Kurve).",
      moistureCurve: "Sorptionskurve w(RF) [RF %, w kg/m³]",
      lambdaMoistureCurve: "λ(w) [w m³/m³, λ W/(m·K)]",
      muMoistureCurve: "μ(w) [w m³/m³, μ -]",
      sdHumidityCurve: "Feuchtevariable Sd-Kurve Sd(RF) [RF 0-1, Sd m]",
      suctionCurve: "Saugkurve [log10 p_C Pa, w kg/m³]",
      liquidConductivityCurve: "Flüssigleitfähigkeit [w kg/m³, log10 K_l s]",
      datasheetUrl: "Datenblatt (URL)",
      productPageUrl: "Produktseite (URL)",
      datasheetChecked: "Datenblatt geprüft am",
      formerlyNamed: "Früherer Name",
      sourceDatabase: "Quelldatenbank",
      sourceLaboratory: "Labor",
      sourceDate: "Messdatum",
      sourceFile: "Quelldatei",
      assumedFields: "Angenommene Werte (nicht im Datenblatt)",
      derivedFields: "Abgeleitete Werte (aus dem Datenblatt berechnet)",
      dataNote: "Hinweis zum Datenblatt",
      dataNoteLambda: "λ des Datenblatts [W/(m·K)]",
      invalidCurve: (curve: string) => `${curve}: keine gültige Liste von Punkten.`,
      label: "Bezeichnung",
      element: "Bauteil",
      structure: "Konstruktion",
      regions: "Regionen",
      layerCount: "Schichten",
      default: "Standard",
      deleteExampleQuestion: "Möchtest du dieses Beispiel wirklich löschen?",
      example: "Beispiel",
      description: "Beschreibung",
      layersInsideOut: "Schichten (innen → außen)",
      addBridge: "Ständer/Sparren hinzufügen",
      width: "Breite",
      spacing: "lichter Abstand",
      fullDepth: "ganze Dicke",
      addLayer: "Schicht hinzufügen",
    },
    errors: {
      materialAlreadyExists: "Ein Material mit gleichem Namen und Hersteller existiert bereits.",
      materialInUse:
        "Das Material wird von einer Berechnung oder einem Beispiel verwendet: blende es aus, statt es zu löschen.",
      exampleNotFound: "Beispiel nicht gefunden.",
      invalidExample: "Ungültiger Beispielaufbau.",
      exampleAlreadyExists: "Ein Beispiel mit demselben Code existiert bereits.",
      invalidMaterial: "Ungültiges eigenes Material.",
      materialNotFound: "Eine Schicht verwendet ein Material, das nicht im Katalog ist.",
      customMaterialNotFound: "Eine Schicht verwendet ein nicht vorhandenes eigenes Material.",
      invalidLayer: "Ungültige Schicht.",
      invalidBridge: "Ungültige Wärmebrücke.",
      condCategoryNotFound: "Unbekannte Tauwasserkategorie.",
      categoryAlreadyExists: "Eine Kategorie mit demselben Code existiert bereits.",
      categoryInUseByMaterial: "Es gibt Materialien dieser Kategorie: Blenden Sie sie aus, statt sie zu löschen.",
      categoryInUseBySubcategory: "Es gibt Unterkategorien dieser Kategorie: Löschen oder verschieben Sie sie zuerst.",
      subcategoryAlreadyExists: "Eine Unterkategorie mit demselben Code existiert bereits.",
      subcategoryInUseByMaterial: "Es gibt Materialien dieser Unterkategorie: Blenden Sie sie aus, statt sie zu löschen, und verschieben Sie sie nur in die Kategorie ihrer Materialien.",
    },
    nav: {
      project: "Projekt",
      data: "Daten",
      dataTitle: "Daten: Projekt, Standort, Bauteil, Analyse und Berechnungsmodus",
      stratigraphy: "Schichtaufbau",
      stratigraphyTitle: "Schichtaufbau: Schichten und Materialien des Bauteils",
      report: "Bericht",
      reportTitle: "Bericht: Vorschau und Druck",
      general: "Allgemein",
      materialsDatabase: "Materialdatenbank",
      materialsDatabaseTitle: "Materialdatenbank: Materialien durchsuchen, filtern und hinzufügen",
      settings: "Einstellungen",
      settingsTitle: "Einstellungen: Einheiten, Normen, Berechnung und Tastenkürzel",
      tutorial: "Tutorial",
      tutorialTitle: "Tutorial - eine Vorführung der wichtigsten Funktionen",
      toggleSidebar: "Seitenleiste ein-/ausklappen",
      analysisResults: "Analyse und Ergebnisse",
      analysisResultsTitle: "Analyse und Ergebnisse: die statische und die quasi-dynamische Analyse",
      quasiDynamic: "Quasi-dynamisch",
      quasiDynamicLockedTitle: "Quasi-dynamische Analyse: in dieser Beta nicht verfügbar",
      openQuasiDynamicTitle: "Auch die quasi-dynamische Analyse öffnen?",
      openQuasiDynamicMessage: "Im Reiter Daten wurde die statische Analyse gewählt. Möchten Sie auch mit der quasi-dynamischen Analyse fortfahren?",
      openQuasiDynamicConfirm: "Ja, öffnen",
      reportLockedNote: "Verfügbar, sobald die statische Analyse für diesen Schichtaufbau berechnet ist",
      reportLockedToast: "Der Bericht: verfügbar, sobald die statische Analyse für diesen Schichtaufbau berechnet ist.",
      completeDataFirst: "Füllen Sie zuerst den Reiter Daten aus (Projekt, Bauteil, Analyse)",
      addLayerFirst: "Fügen Sie dem Schichtaufbau zuerst mindestens eine Schicht hinzu",
      openStratigraphyFirst: "Öffnen Sie zuerst den Schichtaufbau",
      statusTodo: "Noch auszufüllen",
      statusOk: "Vollständig",
      statusWarn: "Erfüllt, mit einer Warnung",
      statusBad: "Mindestens ein Nachweis nicht erfüllt",
      statusBusy: "Berechnung läuft…",
      undoPrevious: "Vorherige Aktion rückgängig machen",
      showRecentActions: "Letzte Aktionen anzeigen",
      redoNext: "Nächste Aktion wiederherstellen",
      recentUndoHistory: "Letzter Änderungsverlauf",
      undoMultiple: "Mehrere Aktionen rückgängig machen",
      noActions: "Keine Aktionen",
      historyEmpty: "Ihre letzten Änderungen werden hier angezeigt.",
      historyFoot: "Wählen Sie eine Aktion, um zum Zustand unmittelbar davor zurückzukehren. Bewegen Sie den Mauszeiger darüber, um alle Details der Änderung zu sehen.",
    staticAnalysis: "Statisch",
    staticAnalysisTitle: "Statische Analyse: Ergebnisse (1D oder 2D), monatliches Glaser-Verfahren, Einstellungen",
    quasiDynamicTitle: "Quasi-dynamische Analyse: Eingaben und Ergebnisse",
    },
    preferences: {
      preferences: "Präferenzen",
      preferencesTitle: "Präferenzen: Einheiten und Normen",
      units: "Einheiten",
      unitsTitle: "SI: metrisch. Imperial: alle Werte umgerechnet (Dampfdiffusion als Permeanz in US perm). UK: SI, außer der Dampfdiffusion, die als Dampfwiderstand in MN·s/g angegeben wird. Die Berechnungen selbst erfolgen immer in SI - dies ändert nur die Anzeige und den Ausdruck.",
      ukTitle: "Dieselben SI-Einheiten wie überall, außer der Dampfdiffusion, angegeben als Dampfwiderstand in MN·s/g - die in der britischen Bauphysik übliche Einheit",
      unitsHint: "Mit der Berechnung gespeichert; eine neue Berechnung beginnt mit den zuletzt gewählten Einheiten (sonst SI).",
      standards: "Normen",
      standardEuTitle: "Die Menüs zeigen die UNI / EN-Optionen (EN 15026, UNI EN ISO 13788)",
      standardNaTitle: "Die Menüs zeigen die ASHRAE-Optionen (ASHRAE 160)",
      standardsHint: "Menüs mit normabhängigen Alternativen (Regen in der quasi-dynamischen Analyse, Innenklima der monatlichen Glaser-Analyse) zeigen nur die Optionen der gewählten Norm.",
    },
    settings: {
      changesPending: "Änderungen noch nicht übernommen - Fertig übernimmt sie",
      changesApplyOnDone: "Änderungen werden mit Fertig übernommen",
      done: "Fertig",
      title: "Einstellungen",
      keyboardShortcuts: "Tastenkürzel",
      solver2d: "2D-Solver",
      climate: "Klima",
      quasiDynamic: "Quasi-dynamisch",
      quasiDynamicLockedTitle: "Quasi-dynamische Analyse: in dieser Beta nicht verfügbar",
      quasiDynamicLockedLabel: "Quasi-dynamisch: in dieser Beta nicht verfügbar",
      about: "Info",
      shortcutsHint: "Klicken Sie auf ein Feld und drücken Sie dann die gewünschte Tastenkombination.",
      resetToDefaults: "Auf Standard zurücksetzen",
      solver2dHeading: "Stationäres hygrothermisches 2D-Verfahren — Netz, Iterationen und Toleranzen",
      solver2dIntro: "Steuert die gekoppelte Finite-Volumen-Lösung von Temperatur und Dampfdruck im 2D-Netzmodus. Ohne Einfluss auf das 1D-Glaser-Verfahren.",
      solvingMethod: "Lösungsverfahren",
      solverCg: "Konjugierte Gradienten (Jacobi-vorkonditioniert)",
      solverCgHint: "Die statische 2D-Analyse verwendet immer das Jacobi-vorkonditionierte CG-Verfahren: Es konvergiert auch mit Dampfsperren, bei denen Gauss-Seidel und der V-Zyklus anhalten, bevor die Energiebilanz erfüllt ist.",
      meshRows: "Netz (Zeilen über die Dicke)",
      meshHint: "Auto (erste Position) rechnet mit 20→40→70→100→160→250→300 Zeilen neu und hält an, sobald sich der Wärmestrom zwischen zwei Netzen um weniger als 0,1 % ändert und der Solver selbst konvergiert ist (siehe Karte Diagnose). Schieben Sie den Regler nach rechts, um das Netz festzulegen: 20, 40, 70, 100, 160, 250 oder 300 Zeilen - feiner ist genauer und langsamer.",
      maxIterationsBefore: "Max. Iterationen (T und p",
      maxIterationsAfter: ", gekoppelte Schleife)",
      epsU: "εU — Toleranz U-Wert / Wärmedurchgang [-]",
      epsRH: "εRH — Toleranz Dampfdruck / RH [-]",
      toleranceHintBefore: "Die Toleranz gilt nur für die beiden Größen, die für das Ergebnis tatsächlich zählen: εU ist die relative Änderung von U-Wert/Wärmestrom zwischen gekoppelten Durchläufen (da das Temperaturfeld aus derselben thermischen Lösung stammt, bestimmt sie auch, wie genau sich die Temperaturen einstellen); εRH ist die Änderung der relativen Feuchte zwischen gekoppelten Durchläufen (RH=p",
      toleranceHintAfter: "(T), bleibt also erst konstant, wenn sich sowohl das Dampfdruckfeld als auch die Temperatur, von der sie abhängt, eingestellt haben). Ein gröberes Netz, lockerere Toleranzen oder weniger Iterationen konvergieren schneller, lösen das Feld um ein Ständerelement aber weniger genau auf - ob die aktuellen Einstellungen tatsächlich Konvergenz erreichen, zeigen die Netzstatus-Karten über der Schaltfläche Beispiele. Gauss-Seidel und konjugierte Gradienten lösen das feine Netz direkt; das V-Zyklus-Mehrgitterverfahren nutzt zusätzlich eine Hierarchie gröberer, algebraisch aggregierter Netze, um dieselbe Lösung zu beschleunigen - alle drei konvergieren zum selben Feld.",
      aboutNotice: "© 2026 Rotho Blaas Srl · Envelope Designer. Beta - inoffizielle Version, noch in Entwicklung.",
      methodology: "Methodik und Grenzen",
      aboutBuild: "Über diese Version",
      climateSources: "Klimaquellen",
      climateSourcesHint: "Wählen Sie Größe für Größe, woher das Klima stammt (PVGIS, Ihre EPW-Datei, Schätzungen), sehen Sie, welcher Anbieter was liefert, und laden Sie das kombinierte Jahr als .epw herunter.",
      epwAdvanced: ".epw erweitert…",
      epwWhereToGet: "Bezugsquellen für EPW-Wetterdateien",
      epwHint: "Wählen Sie die dem Standort nächstgelegene Station mit ähnlicher Höhe und Exposition (EN 15026 §5.2.1); ein Küstenflughafen kann stark von einem Standort im Binnenland oder im Gebirge abweichen. Verwenden Sie für den Bemessungsfall ein typisches Jahr und für Winterprobleme die Option −2 K des Panels. Die Datei muss ein volles Jahr mit Stundenwerten enthalten (8760 Zeilen); das Panel prüft dies und meldet Lücken. Eine hochgeladene Datei wird mit jeder Berechnung gesendet und nicht gespeichert (das Anfrageprotokoll behält nur einen Fingerabdruck).",
      epwOneBuilding: "kostenlose typische Jahre (TMYx) für Tausende Wetterstationen weltweit, eine ZIP-Datei pro Station mit der EPW-Datei darin. Beachten Sie die Nutzungsbedingungen des Archivs; geben Sie die Dateien nicht weiter.",
      epwMap: "eine Karte derselben Stationen: Klicken Sie auf die dem Standort nächstgelegene, um sie herunterzuladen.",
      epwPvgis: "das Werkzeug \"Typical meteorological year\" exportiert als EPW dasselbe typische Jahr, das die Quelle PVGIS hier für den Kartenpunkt herunterlädt.",
      epwEnergyPlus: "die EnergyPlus-Seite für Wetterdateien, die auf das obige Archiv verweist.",
      epwNsrdb: "satellitengestützte typische Jahre für Nordamerika (kostenlose Registrierung); Download als CSV, in EPW umzuwandeln.",
      epwLicensed: "lizenzierte, qualitätsgeprüfte Datensätze (Meteonorm: meteonorm.com), wenn ein Projekt sie erfordert.",
      qdHeading: "Quasi-dynamische Analyse — Berechnung",
      qdIntro: "Gilt für den nächsten Lauf des Panels Quasi-dynamisch.",
      timeStep: "Zeitschritt",
      timeStepAuto: "Automatisch (1D: 5 min, 2D: 1 h)",
      timeStepHint: "Implizite Zeitintegration: Jeder Schritt ist stabil, aber Schritte über 1 h verwenden das über den Schritt gemittelte Klima, sodass der Tagesgang (nächtliche Abkühlung, Mittagssonne) geglättet wird. EN 15026 arbeitet mit stündlichen Klimadaten: Behalten Sie für Bemessungsergebnisse 1 h oder weniger bei. Gemessen an einer 2x6-Ständerwand (2D, 20 Zeilen, ein Jahr): Die Kondensatspitze betrug 94 g/m² mit 1 h, 100 mit 6 h, 112 mit 24 h.",
      solutionMethod: "Lösungsverfahren (2D-Netz)",
      qdMethods: {
        pcg: "Konjugierte Gradienten, linienvorkonditioniert (PCG)",
        "pcg-jacobi": "Konjugierte Gradienten, Jacobi-vorkonditioniert",
        gs: "Gauss-Seidel",
        jacobi: "Jacobi",
        thomas: "Direkt tridiagonal (Thomas)",
      },
      defaultDash: " - Standard",
      solutionMethodHint: "Löser der Wärme- und Dampfsysteme jedes Zeitschritts, alle mit demselben relativen Residuum als Abbruchkriterium. Das linienvorkonditionierte PCG löst in jeder Iteration jede Spalte des Gitters exakt (dünne Membranen und Wärmeübergänge an den Oberflächen koppeln die Zellen über die Dicke viel stärker als seitlich) und ist am schnellsten; das Jacobi-vorkonditionierte PCG ist der Referenzalgorithmus des Validierungsberichts 05. Gauss-Seidel und Jacobi benötigen weit mehr Iterationen (bei einer 2x6-Wand dauerte ein Jahr über 12 Minuten, mehr als die 300 s des Servers): Verwenden Sie sie zur Gegenprüfung kurzer Läufe. Der Abschnitt Konvergenz zeigt jede Stunde. Die 1D-Analyse verwendet immer den direkten tridiagonalen Löser (Thomas).",
      tolerance: "Toleranz",
      defaultParen: " (Standard)",
      maxIterations: "Max. Iterationen",
      toleranceQdHint: "Eine Lösung gilt als konvergiert, wenn ‖b − Ax‖ / ‖b‖ unter der Toleranz liegt. Bei 1e−10 hinterließen die 2D-Gitter mit dünnen Membranzeilen Massenbilanz-Residuen von etwa 1e−5; 1e−12 hält sie unter 1e−6. Max. Iterationen: pro Lösung.",
      gridRows: "Zeilen des 2D-Gitters",
      gridRowsOption: (rows: number) => `${rows} Zeilen`,
      gridRowsHint: "Nur im 2D-Netzmodus. Feinere Gitter lösen die Grenzflächen besser auf, dauern aber länger; der Server bricht eine Berechnung nach 300 s ab. Gemessen an einer 2x6-Ständerwand, ein simuliertes Jahr mit dem linienvorkonditionierten PCG: 20 Zeilen 12 s, 40 Zeilen 18 s, 70 Zeilen etwa 4 min (nur ein Jahr). Feinere Gitter würden nicht rechtzeitig fertig.",
      restoreDefaults: "Standardwerte wiederherstellen",
      lockedNote: "In dieser Beta nicht verfügbar",
    notAppliedTitle: "Einstellungen nicht übernommen",
    notAppliedMessage: "Sie haben Einstellungen geändert, die noch nicht übernommen wurden: Keine Registerkarte wird aktualisiert, bis Sie auf Fertig klicken. Wie möchten Sie fortfahren?",
    keepEditing: "Weiter bearbeiten",
    discardChanges: "Änderungen verwerfen",
    applyDone: "Übernehmen (Fertig)",
    meshAuto: "Auto",
    meshRowsReadout: (rows: number) => `${rows} Zeilen`,
    },
    confirm: {
      cancel: "Abbrechen",
    },
    data: {
      stepLabel: (n: number, title: string) => `Schritt ${n}: ${title}`,
      stepSite: "Standort",
      stepElement: "Bauteil",
      stepAnalysis: "Analyse",
      stepCalculation: "Berechnung",
      useThisSite: "Diesen Standort verwenden",
      elements: {
        wall: "Wand",
        roof: "Dach",
        ground: "Erdberührt",
      },
      analyses: {
        static: "Stationäre Analyse",
        dynamic: "Quasi-dynamische Analyse",
      },
      wallDesc: "Außenwand",
      roofDesc: "Dach oder oberste Geschossdecke",
      groundDesc: "Bodenplatte gegen Erdreich",
      groundDynamicNote: "Die quasi-dynamische Analyse bildet den Kontakt mit dem Erdreich nicht ab: Die Außenoberfläche ist der Außenluft ausgesetzt.",
      staticOption: "Stationär",
      staticDesc: "Bemessungsbedingung und monatliches Glaser-Verfahren",
      quasiDynamic: "Quasi-dynamisch",
      quasiDynamicDesc: "Stündlich über mehrere Jahre",
      simplified: "Vereinfacht",
      simplifiedDesc: "1D durch die Schichten",
      meshMode: "Netzmodus",
      meshModeDesc: "2D-Netz, Ständer aufgelöst",
      meshNoteLabel: "2D-Netzmodus: numerische Verfahren (Hinweis)",
      numericalMethods: "Numerische Verfahren.",
      meshNote: "Der 2D-Netzmodus verwendet numerische Verfahren: Die Prüfung ihrer Gültigkeit sowie der Eignung jedes im Schichtaufbau gewählten Materials und Produkts für den konkreten Fall liegt in der alleinigen Verantwortung des zuständigen Planers.",
      missing: {
        site: "Standort",
        element: "Bauteil",
        analysis: "Analyse",
      },
      stillToChoose: (list: string) => `Noch auszuwählen: ${list}.`,
      continueToStratigraphy: "Weiter zum Schichtaufbau →",
      continueToAnalysis: {
        static: "Weiter zur stationären Analyse →",
        dynamic: "Weiter zur quasi-dynamischen Analyse →",
      },
      backToData: "← Daten",
      howToStart: "Wie möchten Sie beginnen?",
      fromExample: "Aus einem Beispiel",
      fromExampleDesc: (n: number, element: string) => `Wählen Sie einen der ${n} Aufbauten (${element}) und passen Sie ihn an`,
      fromScratch: "Neu beginnen",
      fromScratchDesc: "Ein leerer Schichtaufbau: Fügen Sie die Schichten aus der Materialbibliothek hinzu",
      back: "← Zurück",
    },
    examples: {
      close: "Schließen",
      examplesTitle: (element: string) => `Beispiele: ${element}`,
      elementName: {
        wall: "Wand",
        roof: "Dach",
        ground: "erdberührtes Bauteil",
      },
      exampleLoaded: (name: string) => `Beispiel geladen: ${name}`,
      changeExample: "Beispiel wechseln",
      changeExampleTitle: (element: string) => `Ersetzt die Schichten durch ein anderes Beispiel (${element}); Rückgängig stellt sie wieder her`,
      structures: {
        timber: "Holzrahmenbau",
        mass_timber: "Massivholz",
        masonry: "Mauerwerk",
        concrete: "Beton",
        steel: "Stahl",
        other: "Sonstige",
      },
      types: {
        wall: "Wand",
        roof: "Dach / Decke",
        ground: "Bodenplatte",
      },
      searchPlaceholder: "Beispiele suchen...",
      exampleSingular: "Beispiel",
      examplePlural: "Beispiele",
      filtersLabel: "Beispielfilter",
      region: "Region",
      yourLocation: (country: string) => `Ihr Standort: ${country}`,
      type: "Typ",
      structure: "Tragwerk",
      resetFilters: "Filter zurücksetzen",
      noMatch: "Kein Aufbau entspricht der Suche oder den gewählten Filtern.",
      noneInRegion: "Kein Aufbau dieses Typs in der gewählten Region.",
      backToTypes: "Zurück zu den Typen",
    },
    climate: {
      loadError: "Die Klimadaten konnten nicht geladen werden.",
      suggestedCorrections: "Vorgeschlagene Korrekturen",
      applyAndReload: "Anwenden und neu laden",
      correctionsHint: "Es wird nur korrigiert, was Sie auswählen; die angewendeten Korrekturen werden bei den Ergebnissen aufgeführt.",
      correctionsChosen: (list: string) => `Gewählte Korrekturen: ${list} (für diese Quelle nichts zu korrigieren).`,
      undo: "Rückgängig",
      cardTitle: "Klima des Standorts",
      autoEpwTitle: "Das Klima des Kartenpunkts, automatisch von einem Anbieter erstellt (PVGIS, ERA5 über Open-Meteo, TMYx-Station).",
      autoEpw: "Auto-EPW",
      importEpwTitle: "Laden Sie eine EPW-Datei hoch (TMYx, NSRDB, Meteonorm oder jede andere Quelle).",
      importEpw: "EPW importieren",
      yearType: "Jahrestyp",
      yearTypical: "Typisches Jahr, wiederholt",
      yearMinus2: "Typisches Jahr −2 K (Winterprobleme, EN 15026 §5.2.1 c)",
      yearPlus2: "Typisches Jahr +2 K (Sommerprobleme)",
      provider: "Klimadatenanbieter",
      providerPvgis: "PVGIS typisches Jahr (EU JRC) - ohne Regen",
      providerOpenMeteo: "ERA5 über Open-Meteo - Regen, Bodentemperaturen",
      providerTmyx: "TMYx-Wetterstation (climate.onebuilding.org)",
      era5Year: "ERA5-Jahr",
      era5YearTitle: "Das erste typische Jahr eines Standorts lädt 10 Jahre Stundendaten herunter (etwa 10-20 s); danach bleibt es für diese Rasterzelle gespeichert.",
      era5Typical: "Typisches Jahr (EN ISO 15927-4, letzte 10 Jahre)",
      actualYear: (year: number) => `Tatsächliches Jahr ${year}`,
      tmyxStation: "TMYx-Station",
      tmyxStationTitle: "Entfernung und Höhenunterschied zum Standort. Die beste Übereinstimmung wertet 100 m Höhe wie 10 km; die Temperatur wird um den Höhenunterschied korrigiert.",
      bestMatch: (station: string) => `Beste Übereinstimmung: ${station}`,
      lat: "Breite",
      latitude: "Breitengrad [°]",
      lon: "Länge",
      longitude: "Längengrad [°]",
      elevTitle: "Höhe des Standorts [m]. Leer: die Höhe der Klimadaten (grau angezeigt). Geben Sie einen Wert ein, um die Temperatur auf den Standort zu korrigieren (0,65 K pro 100 m).",
      elev: "Höhe",
      elevAuto: "auto",
      siteElevation: "Höhe des Standorts [m]",
      viewClimateData: "Klimadaten anzeigen",
      viewClimateDataLoading: "Klimadaten anzeigen (wird geladen)",
      climateDataLoading: "Klimadaten werden geladen…",
      loadingClimateData: "Klimadaten werden geladen",
      climateData: "Klimadaten",
      backToMap: "Zurück zur Karte",
      map: "Karte",
      epwFile: "EPW-Datei",
      fileUnreadable: (name: string) => `Die Datei ${name} konnte nicht gelesen werden.`,
      weatherStationHint: "Eine Wetterstation in der Nähe des Standorts ist die zuverlässigste Quelle (EN 15026 §5.2.1).",
      elevationFromEpw: (elevation: number) => `${elevation} (aus der EPW)`,
      fromEpw: "aus der EPW",
      epwElevationTitle: "Leer: die Höhe der EPW. Geben Sie einen Wert ein, um die Temperatur auf den Standort zu korrigieren (0,65 K pro 100 m).",
      notEpwFile: (name: string) => `${name} ist keine EPW-Datei: Wählen Sie eine Datei mit der Endung .epw.`,
      readingClimate: "Klima wird gelesen…",
      loaded: "geladen",
      replace: "Ersetzen…",
      remove: "Entfernen",
      dropEpwHere: "EPW-Datei hierher ziehen",
      epwSources: "TMYx, NSRDB, Meteonorm oder jede andere Quelle",
      chooseEpw: "EPW-Datei auswählen…",
      noWindData: "Keine Winddaten in dieser Klimadatei.",
      loadingTheClimateData: "Klimadaten werden geladen…",
      insideBasis: "UNI EN ISO 13788 Klasse 3",
      ashraeNotAvailable: ", ASHRAE 160 noch nicht verfügbar",
      heatingDesignBasis: "Heiz-Auslegungstemperatur 99,6 %",
      coldestHour: (day: number, month: string, time: string) => `kälteste Stunde, ${day}. ${month} ${time}`,
      aboveSaturation: (rh: string) => ` Dieser Dampfdruck liegt bei Te über der Sättigung (rF ${rh} %): Es werden 100 % vorgeschlagen.`,
      climateOfSite: "das Klima des Standorts",
      suggestionTitle: (method: string, source: string, notes: string) => `${method}. Klima: ${source}.${notes} Die Felder bleiben bearbeitbar.`,
      fromTheClimate: (basis: string) => `Aus dem Klima (${basis}):`,
      inUse: "verwendet",
      use: "Verwenden",
      searchFailed: "Die Suche ist fehlgeschlagen.",
      noPlaceFound: "Kein Ort gefunden.",
      searchNotSent: "Die Suche konnte nicht gesendet werden.",
      mapLabel: "Karte: Klicken Sie, um den Standort zu wählen",
      searchPlaceholder: "Stadt, Straße oder Ort suchen",
      searchLabel: "Ort suchen",
      searching: "Suche läuft",
      usZonesTitle: "IECC-Klimazonen der Vereinigten Staaten (PNNL Building America Solution Center)",
      usZones: "US-Klimazonen",
      addToFavourites: "☆ Zu Favoriten hinzufügen",
      favouritesFull: "Favoriten voll (10): Entfernen Sie zuerst einen.",
      favouriteName: "Name des Favoriten",
      cancel: "Abbrechen",
      add: "Hinzufügen",
      renameFavourite: (name: string) => `Neuer Name für ${name}`,
      favouriteHint: "Ziehen zum Umsortieren, Doppelklick zum Umbenennen",
      removeFavourite: (name: string) => `${name} aus den Favoriten entfernen`,
      morePlaces: (n: number) => `${n} weitere Orte`,
      more: (n: number) => `${n} weitere`,
    monthsShort: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
    chartLabel: "Referenzjahr: Tagesmittel und Tagesspanne jeder Größe",
    quantitiesLabel: "Angezeigte Größen (zum Aus- oder Einblenden klicken)",
    allHidden: "Alle Größen sind ausgeblendet.",
    legendTitle: (name: string, hidden: boolean) => `${name} - zum ${hidden ? "Einblenden" : "Ausblenden"} klicken`,
    series: {
      outsideTemperature: "Außenlufttemperatur",
      insideTemperature: "Innentemperatur",
      outsideRh: "Relative Außenluftfeuchte",
      insideRh: "Relative Innenluftfeuchte",
      solar: "Globalstrahlung auf die Horizontale",
      wind: "Windgeschwindigkeit in 10 m Höhe",
      rain: "Niederschlag auf die Horizontale",
    },
    windRoseLabel: "Windrose: Anteil der Stunden nach Richtung und Geschwindigkeit",
    windClassesLabel: "Windgeschwindigkeitsklassen (zum Aus- oder Einblenden klicken)",
    windSpeed: "Windgeschwindigkeit",
    windFrom: (direction: string, percent: string) => `Aus ${direction}: ${percent} % der Stunden`,
    windMean: (value: string, unit: string) => `Mittel ${value} ${unit}`,
    windMax: (value: string, unit: string) => `Max. ${value} ${unit}`,
    },
    stratigraphy: {
      flipInsideOutsideTitle: "Innen/Außen tauschen - legt fest, welche Seite Innen/Außen ist (und tauscht Ti/RHi/Rsi mit Te/RHe/Rse), ohne die Schichten neu anzuordnen",
      resetZoomTitle: "Zoom zurücksetzen (zum Vergrößern/Verkleinern über der Zeichnung scrollen, im gezoomten Zustand zum Verschieben ziehen)",
      clearLayersTitle: "Alle Schichten löschen (mit Rückfrage; Rückgängig stellt sie wieder her)",
      condensationToggleTitle: "Tauwasser im Bauteil in der Zeichnung ein-/ausblenden - feuchte Schichten blau eingefärbt, mit Tropfenmarkierungen an den Tauwasserstellen",
      fluxVectorsTitle: "Wärmestromvektoren über dem Schnitt anzeigen (nur im 2D-hygrothermischen Modus) - die Pfeillänge folgt der lokalen Wärmestromdichte, die Richtung dem Temperaturgradienten (senkrecht zu den Isothermen)",
      overlayTemperatureTitle: "Temperaturverlauf über die Zeichnung legen (ziehen Sie den Griff, um festzulegen, wie viel der Breite er abdeckt)",
      overlayHumidityTitle: "Feuchteverlauf (RH) über die Zeichnung legen (ziehen Sie den Griff, um festzulegen, wie viel der Breite er abdeckt)",
      meshGridTitle: "Die einzelnen Zellen des 2D-hygrothermischen Berechnungsnetzes anzeigen, mit oder ohne Temperatur-/Feuchteüberlagerung (nur im 2D-Netzmodus, Ausdehnung über denselben Griff)",
      thickness: "Dicke",
      editMaterial: "Material bearbeiten…",
      changeMaterial: "Material ändern…",
      removeBridge: "Wärmebrücke entfernen",
      resizeHandleTitle: "Ziehen, um die Größe dieses Felds zu ändern - Doppelklick zum Zurücksetzen",
      layerTable: "Schichttabelle",
      layerTableHide: "ausblenden",
      layerTableShow: "berechnete Eigenschaften jeder Schicht anzeigen",
      layerTableHint: "Berechnete thermische Eigenschaften des aktuellen Schichtaufbaus, von innen nach außen. Ziehen Sie den Griff einer Zeile, um sie zu verschieben, klicken Sie auf den Namen eines Materials (oder sein Stiftsymbol), um es anzuzeigen/zu bearbeiten - oder nutzen Sie den grafischen Schnitt oben für weitere Aktionen (Rechtsklick zum Einfügen/Duplizieren/Löschen/für Wärmebrücken).",
      layerTable2DNote: "2D-hygrothermischer Modus: Die Werte unten stammen aus dem konvergierten 2D-Feld entlang der Mitte des Gefachs (ohne Tragelement) und sind kein echter 1D-Wert je Schicht - das vollständige Feld finden Sie im Bereich 2D-hygrothermisch der Ergebnisseite.",
      colMaterial: "Material",
      colTempMin: "Temp. min",
      colTempMinTitle: "Temperatur an der kälteren Oberfläche dieser Schicht unter Bemessungsbedingungen",
      colTempMax: "Temp. max",
      colTempMaxTitle: "Temperatur an der wärmeren Oberfläche dieser Schicht unter Bemessungsbedingungen",
      colSdTitle: "Wasserdampfdiffusionsäquivalente Luftschichtdicke (μ × Dicke)",
      colCondensate: "Tauwasser",
      colCondensateTitle: "Tauwasserrate im Bauteil, falls vorhanden, an der Innenseite dieser Schicht",
      colWeight: "Gewicht",
      colWeightTitle: "Flächenbezogene Masse",
      colHeatCapacity: "Wärmekapazität",
      colHeatCapacityTitle: "Spezifische Wärmekapazität",
      makeSideInside: "Klicken, um diese Seite zur Innenseite zu machen",
      makeSideOutside: "Klicken, um diese Seite zur Außenseite zu machen",
      sideSwitched: (top: boolean, outside: boolean) => `${top ? "Obere" : "Untere"} Seite: ${outside ? "AUSSEN" : "INNEN"}`,
      differsFromLibrary: "Weicht vom Bibliotheksmaterial ab",
      variable: "variabel",
      thicknessField: "Dicke",
      checkPositive: (fields: string) => `Bitte prüfen Sie ${fields}: Es ist eine positive Zahl erforderlich.`,
      stud: "Ständer",
      material: "Material",
      thermalHygrometric: "Thermisch & hygrisch",
      acoustic: "Akustisch",
      permeance: "Permeanz",
      membraneLocked: "Rothoblaas-Bahn: Werte aus dem Datenblatt, nicht bearbeitbar.",
      layerValuesHint: "Werte nur dieser Schicht, übernommen mit Enter oder beim Verlassen eines Felds.",
      studValuesHint: "Werte nur dieses Ständers, übernommen mit Enter oder beim Verlassen eines Felds.",
      checkedOn: (date: string) => `Geprüft am ${date}`,
      dataSheet: "Datenblatt",
      resetToLibrary: "Auf Bibliothekswerte zurücksetzen",
    outside: "AUSSEN",
    inside: "INNEN",
    noLayersDrawing: "Noch keine Schichten - fügen Sie eine Schicht hinzu oder laden Sie ein Beispiel.",
    noLayersTable: "Keine Schichten - ziehen Sie ein Material aus der Bibliothek auf den grafischen Schnitt oben.",
    moveUp: "Nach oben verschieben (nach außen)",
    moveDown: "Nach unten verschieben (nach innen)",
    excludedClickToInclude: "Von der Berechnung ausgeschlossen - klicken Sie, um sie einzubeziehen",
    includedClickToExclude: "In die Berechnung einbezogen - klicken Sie, um sie auszuschließen",
    exteriorResistance: "Äußerer Wärmeübergangswiderstand (Rse)",
    interiorResistance: "Innerer Wärmeübergangswiderstand (Rsi)",
    dragToReorder: "Zum Umordnen ziehen",
    viewEditMaterial: "Material anzeigen/bearbeiten",
    customMaterialRow: "Diese Schicht verwendet ein eigenes/bearbeitetes Material, keine Standardreferenz der Bibliothek",
    supersaturated: "RF>100%",
    editStud: "Ständer/Sparren bearbeiten",
    removeStud: "Ständer/Sparren entfernen",
    studSummary: (name: string, width: string, spacing: string, depth: string, unit: string) => `${name} · ${width}×${spacing}${unit}, Tiefe ${depth}${unit}`,
    wholeComponent: "Gesamtes Bauteil",
    wholeComponentMidBay: "Gesamtes Bauteil (Spalte in Feldmitte)",
    sdName: {
      sdValue: "sd-Wert",
      permeance: "Permeanz",
      vapourResistance: "Dampfwiderstand",
    },
    insertInterior: "↑ Schicht einfügen (Innenseite)",
    insertExterior: "↓ Schicht einfügen (Außenseite)",
    placeStuds: "Ständer oder Sparren einsetzen…",
    removeStuds: "Ständer oder Sparren entfernen",
    duplicateLayer: "Schicht duplizieren",
    disableLayer: "Schicht deaktivieren (von der Berechnung ausschließen)",
    enableLayer: "Schicht aktivieren",
    deleteLayer: "Schicht löschen",
    changeMaterialTitle: "Material ändern",
    clearAllTitle: "Alle Schichten löschen?",
    clearAllMessage: (n: number) => `${n === 1 ? "Die einzige Schicht" : `Alle ${n} Schichten`} dieses Schichtaufbaus ${n === 1 ? "wird" : "werden"} gelöscht. Mit Rückgängig (Strg+Z) können Sie sie wiederherstellen.`,
    clearAllConfirm: "Alle löschen",
    ashrae160Note: "ASHRAE 160 noch nicht verfügbar (die Werte werden geprüft): es wird das UNI / EN-Modell verwendet",
    },
    materialLibrary: {
      title: "Materialbibliothek",
      materials: "Materialien",
      collapseExpand: "Materialbibliothek ein-/ausklappen",
      openLibrary: "Materialbibliothek öffnen",
      searchPlaceholder: "Materialien filtern...",
      materialSingular: "Material",
      materialPlural: "Materialien",
      noMatches: "Keine passenden Materialien.",
      backTo: (name: string) => `Zurück zu ${name}`,
      backToCategories: "Zurück zu den Kategorien",
      dragToInsert: "Zum Einfügen in die Schichttabelle ziehen",
      dragHint: "Ziehen Sie ein Material auf den grafischen Schnitt, um es als neue Schicht einzufügen (Esc bricht ab).",
      chooseCategoryHint: "Wählen Sie eine Kategorie und ziehen Sie dann ein Material auf den grafischen Schnitt, um es als neue Schicht einzufügen.",
      generic: "Generisch",
      filtersLabel: "Materialfilter",
      min: "min",
      max: "max",
      minLambda: "Minimale Wärmeleitfähigkeit",
      maxLambda: "Maximale Wärmeleitfähigkeit",
      membranes: "Bahnen",
      vapourDiffusionAs: "Dampfdiffusion als",
      minimumOf: (name: string) => `${name}: Minimum`,
      maximumOf: (name: string) => `${name}: Maximum`,
      permeance: "Permeanz",
      vapourResistance: "Dampfdiffusionswiderstand",
      manufacturer: "Hersteller",
      resetFilters: "Filter zurücksetzen",
      thickness: "Dicke",
      variable: "variabel",
      subcategories: {
        custom: "Benutzerdefiniert",
      },
    },
    shell: {
      calculating: "Berechnung…",
      cancelCalculation: "Berechnung abbrechen",
    },
    shortcuts: {
      undo: "Rückgängig",
      redo: "Wiederholen",
      flip: "Innen/außen vertauschen",
      toggleMode: "Berechnungsmodus umschalten",
      changeMaterial: "Material der ausgewählten Schicht ändern",
      toggleLibrary: "Materialbibliothek öffnen/schließen",
      togglePanels: "Seitenleiste und Ergebnisleiste ein-/ausklappen",
      pressKeys: "Tasten drücken…",
      none: "(keine)",
    },
    history: {
      saved: (time: string) => `Gespeichert ${time}`,
      undoTitle: (label: string) => `Rückgängig: ${label}`,
      undoLabel: (label: string) => `Rückgängig ${label}`,
      nothingToUndo: "Nichts rückgängig zu machen",
      redoTitle: (label: string) => `Wiederholen: ${label}`,
      redoLabel: (label: string) => `Wiederholen ${label}`,
      nothingToRedo: "Nichts zu wiederholen",
      menuCount: (shown: number, available: number) => `${shown} von ${available}`,
      steps: (steps: number) => `${steps} ${steps === 1 ? "Schritt" : "Schritte"}`,
      actionTitle: (detail: string, steps: number) =>
        `${detail} Klicken Sie, um ${steps === 1 ? "1 Aktion" : `${steps} Aktionen`} rückgängig zu machen.`,
      initialState: "Ausgangszustand",
      initialStateDetail: "Zustand beim Start dieser Sitzung.",
      sessionStart: "Sitzungsbeginn",
      fieldLabels: {
        thickness: "Dicke",
        materialId: "Material",
        enabled: "Berechnungsstatus",
        widthMM: "Breite",
        spacingMM: "Abstand",
        depthMM: "Tiefe",
        mode: "Berechnungsmodus",
        stratFlipped: "Ausrichtung innen/außen",
        Rsi: "innerer Wärmeübergangswiderstand",
        Rse: "äußerer Wärmeübergangswiderstand",
        patm: "Luftdruck",
        meshRows: "Zeilen des vereinfachten Netzes",
        meshRowsHygro2D: "Zeilen des 2D-Netzes",
        maxIterHygro2D: "Iterationsgrenze der Kopplung",
        epsU2D: "Toleranz des Wärmedurchgangskoeffizienten",
        epsRH2D: "Toleranz Dampfdruck / rel. Feuchte",
        solverMethod2D: "2D-Löser",
        autoMesh: "automatisches Netz",
        meshTolerance: "Netzgenauigkeit",
        Ti: "Innentemperatur",
        Te: "Außentemperatur",
        RHi: "relative Luftfeuchte innen",
        RHe: "relative Luftfeuchte außen",
        name: "Name",
        notes: "Notizen",
      } as Record<string, string>,
      unknownMaterial: "Unbekanntes Material",
      layerName: (position: number, material: string) => `Schicht ${position} (${material})`,
      enabled: "aktiviert",
      disabled: "deaktiviert",
      notSet: "nicht festgelegt",
      scopeChanged: (scope: string) => `${scope} geändert.`,
      scopeDiff: (scope: string, changes: string) => `${scope}: ${changes}.`,
      layerAdded: "Schicht hinzugefügt",
      layerAddedDetail: (layer: string, position: number, thickness: string) =>
        `${layer} an Position ${position} hinzugefügt, Dicke ${thickness} mm.`,
      layerDeleted: "Schicht gelöscht",
      layerDeletedDetail: (layer: string, position: number, thickness: string) =>
        `${layer} an Position ${position} gelöscht, Dicke ${thickness} mm.`,
      stratigraphyReplaced: "Schichtaufbau ersetzt",
      stratigraphyReplacedDetail: (from: number, to: number, added: number, removed: number) =>
        `Schichtanzahl von ${from} auf ${to} geändert; ${added} hinzugefügt und ${removed} entfernt.`,
      layersReordered: "Schichten neu angeordnet",
      layersReorderedDetail: (layer: string, from: number, to: number) => `${layer} von Position ${from} nach ${to} verschoben.`,
      bridgeAdded: "Wärmebrücke hinzugefügt",
      bridgeAddedDetail: (material: string, layer: string, width: string, spacing: string, depth: string) =>
        `Element aus ${material} zu ${layer} hinzugefügt; Breite ${width} mm, Abstand ${spacing} mm, Tiefe ${depth} mm.`,
      bridgeDeleted: "Wärmebrücke gelöscht",
      bridgeDeletedDetail: (material: string, layer: string) => `Element aus ${material} aus ${layer} gelöscht.`,
      bridgeMoved: "Wärmebrücke verschoben",
      bridgeMovedDetail: (material: string, from: string, to: string) => `Element aus ${material} von ${from} nach ${to} verschoben.`,
      bridgeModified: "Wärmebrücke geändert",
      bridgeScope: (material: string, layer: string) => `Element aus ${material} in ${layer}`,
      layerMaterialChanged: "Material der Schicht geändert",
      layerMaterialChangedDetail: (position: number, from: string, to: string) =>
        `Material der Schicht ${position} von ${from} in ${to} geändert.`,
      layerThicknessChanged: "Dicke der Schicht geändert",
      layerThicknessChangedDetail: (layer: string, from: string, to: string) =>
        `Dicke von ${layer} von ${from} mm auf ${to} mm geändert.`,
      layerDisabled: "Schicht deaktiviert",
      layerDisabledDetail: (layer: string) => `${layer} von den Berechnungen ausgeschlossen.`,
      layerEnabled: "Schicht aktiviert",
      layerEnabledDetail: (layer: string) => `${layer} in die Berechnungen einbezogen.`,
      layerModified: "Schicht geändert",
      flipped: "Innen/außen vertauscht",
      flippedDetail: "Die angezeigte Ausrichtung innen/außen des Schichtaufbaus umgekehrt.",
      modeChanged: "Berechnungsmodus geändert",
      modeChangedDetail: (from: string, to: string) => `Berechnungsmodus: ${from} → ${to}.`,
      projectDetailsModified: "Projektangaben geändert",
      projectDetailsScope: "Projektangaben",
      customMaterialAdded: "Eigenes Material hinzugefügt",
      customMaterialAddedDetail: (name: string) => `Eigenes Material „${name}“ zur Materialbibliothek hinzugefügt.`,
      customMaterialDeleted: "Eigenes Material gelöscht",
      customMaterialDeletedDetail: (name: string) => `Eigenes Material „${name}“ aus der Materialbibliothek gelöscht.`,
      customMaterialModified: "Eigenes Material geändert",
      customMaterialModifiedDetail: (name: string) => `Eigenes Material „${name}“ geändert.`,
      materialLibraryModified: "Materialbibliothek geändert",
      materialLibraryModifiedDetail: "Mehrere eigene Materialien geändert.",
      climateModified: "Bemessungsklima geändert",
      climateScope: "Bemessungsklima",
      settingsModified: "Berechnungseinstellungen geändert",
      settingsScope: "Berechnungseinstellungen",
      projectModified: "Projekt geändert",
      projectModifiedDetail: "Mehrere Projektwerte in dieser gespeicherten Aktion geändert.",
    },
    materials: {
      panelDesc: "Integrierte und benutzerdefinierte Materialien. Benutzerdefinierte Materialien werden im lokalen Speicher dieses Browsers gespeichert.",
      newMaterial: "+ Neues Material",
      searchPlaceholder: "Nach Name suchen...",
      allCategories: "Alle Kategorien",
      allManufacturers: "Alle Hersteller",
      sortName: "Sortieren: Name",
      sortDensity: "Sortieren: Dichte ρ",
      sortLambda: "Sortieren: λ",
      sortMu: "Sortieren: μ",
      sortWcap: "Sortieren: wcap",
      sortDirectionTitle: "Auf-/absteigend umschalten",
      sortAsc: "▲ Aufst.",
      sortDesc: "▼ Abst.",
      colSymbol: "Symbol",
      colName: "Name",
      colManufacturer: "Hersteller",
      customBadge: "benutzerdefiniert",
      edit: "Bearbeiten",
      delete: "Löschen",
      deleteConfirm: "Dieses benutzerdefinierte Material löschen?",
      variableSdTitle: (min: number, max: number) => `Variabler Sd-Wert: ${min}–${max} m (Datenblatt)`,
      navHint: "Wählen Sie eine Kategorie oder suchen Sie oben nach Namen.",
      sourceAssumed: "Nicht im Datenblatt: angenommener Wert",
      sourceDerived: "Nicht im Datenblatt: aus seinen Werten abgeleitet",
      dataNotes: {
        lambdaBelowAir: (lambda: number) => `λ = ${lambda} W/(m·K) des Datenblatts liegt unter dem Wert ruhender Luft: nicht verwendet`,
        densityDerived: "Rohdichte aus den Werten des Datenblatts abgeleitet",
      },
    },
    materialModal: {
      titleMembrane: "Rothoblaas-Bahn",
      titleNew: "Neues Material",
      titleEditCopy: "Material bearbeiten (als benutzerdefinierte Kopie gespeichert)",
      titleEdit: "Material bearbeiten",
      unnamed: "Unbenanntes Material",
      membraneLockedHint: "Rothoblaas-Bahnen werden mit den Werten ihres Datenblatts angezeigt und können weder bearbeitet noch kopiert werden.",
      builtinHint: "Integrierte Materialien können nicht geändert werden: Beim Speichern wird eine benutzerdefinierte Kopie erstellt, und die Schichten dieses Aufbaus, die dieses Material verwenden, wechseln zur Kopie.",
      technicalDataSheet: "Technisches Datenblatt ↗",
      productPage: "Produktseite ↗",
      checked: (date: string) => `geprüft am ${date}`,
      laboratoryNotStated: "Labor nicht angegeben",
      propertiesLabel: "Materialeigenschaften",
      tabIdentity: "Identität",
      tabGraphics: "Grafik",
      tabThermal: "Wärme",
      tabMoisture: "Feuchte",
      tabAcoustic: "Akustik",
      name: "Name",
      manufacturer: "Hersteller",
      optional: "optional",
      category: "Kategorie",
      categoryPlaceholder: "z. B. insulation",
      thickness: "Dicke",
      thicknessVariable: "Variable Dicke",
      thicknessKnown: "Bekannte Dicken (vom Hersteller)",
      reference: "Referenz",
      referencePlaceholder: "z. B. 100",
      min: "Min.",
      max: "Max.",
      knownThicknesses: "Bekannte Dicken",
      knownThicknessesHint: ", durch Kommas getrennt - die erste ist die Referenz",
      knownThicknessesPlaceholder: "z. B. 40, 60, 80, 100, 120",
      source: "Quelle",
      laboratory: "Labor",
      date: "Datum",
      file: "Datei",
      sectionSymbolCaption: "Schnittsymbol (Schraffur):",
      automaticFromCategory: " - automatisch, aus der Kategorie",
      sectionSymbol: "Schnittsymbol",
      auto: "Auto",
      hatches: {
        "": "Auto (aus der Kategorie)",
        concrete: "Beton / Estrich (punktiert)",
        masonry: "Mauerwerk, massiv (Ziegelverband)",
        "masonry-hollow": "Mauerwerk, gelocht (Ziegelverband + Hohlräume)",
        render: "Putz / Mörtel (feine Punkte)",
        board: "Feste Platte, gipsartig (glatt)",
        wool: "Faserdämmstoff (Schlaufen)",
        foam: "Hartschaumdämmung (Kreise)",
        fiberboard: "Holzfaser- / Granulatplatte (gesprenkelt)",
        wood: "Holz (Maserung)",
        membrane: "Bahn / Folie (kräftige Linie)",
        stone: "Naturstein (unregelmäßige Risse)",
        metal: "Metall (feine Diagonale)",
        generic: "Allgemein (diagonale Schraffur)",
      },
      thermalHint: "Eine Wärmeleitfähigkeit pro Material (kein λ(w) in der Webversion).",
      sdValue: "Sd-Wert",
      vapourResistanceZ: "Dampfwiderstand Z",
      variableSd: "Variabler Sd-Wert",
      variableSdRange: (min: number, max: number) => `: ${min}–${max} m im Datenblatt`,
      variableSdCurve: " (gemessene Sd(RH)-Kurve: siehe Hygrothermische Eigenschaften)",
      admissibleCondensate: "Zulässiges Tauwasser",
      admissibleCondensateTitle: "Zulässiges Tauwasser der monatlichen Glaser-Analyse (Legacy-Tabelle frmInfoVAP)",
      condDefault: (label: string) => `Standard: ${label}`,
      vapourNote: (thickness: string, unit: string, mu: string) => `Bei ${thickness} ${unit}: μ = ${mu}`,
      vapourNoteNeedsThickness: "Benötigt eine Dicke (Referenz oder bekannte Dicken).",
      porosity: "Porosität",
      saturation: "Sättigung",
      capillary: "Kapillar",
      waterAbsorption: "Wasseraufnahme",
      airflow: "Strömungswiderstand",
      acousticHint: "Optional: von den Akustikwerkzeugen verwendet, nicht von den hygrothermischen Berechnungen.",
      errorMembrane: "Bahnen sind ausschließlich Rothoblaas-Produkte: Ein benutzerdefiniertes Material kann nicht zur Kategorie Bahn gehören.",
      errorSdThickness: (label: string) => `Um die Dampfdiffusion als ${label} einzugeben, geben Sie dem Material zuerst eine Dicke (Referenzdicke oder bekannte Dicken).`,
      errorSdPositive: (label: string) => `${label} muss eine Zahl größer als 0 sein.`,
      save: "Material speichern",
      cancel: "Abbrechen",
      close: "Schließen",
      density: "Rohdichte ρ",
      lambdaDry: "Wärmeleitfähigkeit trocken λ",
      cp: "Spezifische Wärmekapazität c",
      mu: "Wasserdampf-Diffusionswiderstandszahl μ",
      wCap: "Kapillare Wasseraufnahme wcap",
      emissivity: "Emissionsgrad ε",
    },
    materialHygro: {
      title: "Hygrothermische Eigenschaften",
      showTitle: "Hygrothermische Eigenschaften anzeigen",
      hideTitle: "Hygrothermische Eigenschaften ausblenden",
      tabSorption: "Sorption",
      tabSuction: "Saugspannung",
      thickness: "Dicke",
      sdNamePermeance: "Permeanz",
      sdNameVapourResistance: "Dampfwiderstand",
      dryCup: "Dry cup [US perm]",
      muDry: "μ trocken [-]",
      w80ClassAssumed: (cls: string) => `Keine gemessene Sorptionskurve: Klassenwert „${cls}“ des quasi-dynamischen Modells`,
      noSorptionCurve: "Keine gemessene Sorptionskurve",
      porosity: "Porosität [%]",
      sdChartTitle: (thickness: string, unit: string) => `Sd über der relativen Feuchte (gemessen, ${thickness} ${unit})`,
      sorptionMeasured: "Sorptionsisotherme (gemessen)",
      sorptionTable: "Sorptionsisotherme (Materialtabelle)",
      sorptionLinear: "Wassergehalt: Gerade des quasi-dynamischen Modells",
      suctionTitle: "Saugspannungskurve (Feuchtespeicherung)",
      klTitle: "Flüssigleitfähigkeit k(θ)",
      muwTitle: "Wasserdampf-Diffusionswiderstandszahl μ(w)",
      membraneVariableNote: "Variabler Sd-Wert im Datenblatt, aber keine gemessene Sd(RH)-Kurve: Die Berechnungen verwenden den dichtesten Zustand (fester Sd-Wert).",
      membraneFixedNote: "Bahn: nicht hygroskopische Folie (w = 0), Sd unabhängig von der Feuchte.",
      footSdCurve: "Sd(φ): gemessen nach EN ISO 12572, zwischen den Punkten log-linear gelesen (wie in der 1D-, der monatlichen und der quasi-dynamischen Analyse); die 2D-Analyse verwendet den dichtesten Zustand.",
      footTable: "w(φ) und μ(w), wie die 2D- und die quasi-dynamische Analyse sie verwenden.",
      footTableMeasured: "w(φ) und μ(w), wie die 2D- und die quasi-dynamische Analyse sie verwenden (Sorption aus der Speicherfunktion nach Kelvin bei 20 °C).",
      footNoCurve: (cls: string) => `Keine gemessene Sorptionskurve: Die 2D-Analyse verwendet keinen Wassergehalt; die quasi-dynamische Analyse speichert w = ξ·φ (Klasse „${cls}“).`,
      footLambda: "λ ist ein einziger Wert (kein λ(w)).",
      assumedNoData: "Angenommen: keine Messdaten für dieses Material",
      assumedTag: "angenommen",
      sorptionClasses: {
        membrane: "Bahn",
        woodBased: "Holz, Holzwerkstoffe und Naturfasern",
        gypsum: "Gipsplatten und Gipsputze",
        nonHygroInsulation: "Nicht hygroskopische Dämmstoffe",
        aac: "Porenbeton",
        lightweightConcrete: "Leichtbeton",
        concrete: "Beton",
        concreteBlock: "Betonsteine",
        calciumSilicate: "Calciumsilikat",
        firedClayBrick: "Gebrannter Ziegel",
        naturalStone: "Naturstein",
        metal: "Metall",
        mineralRender: "Mineralische Putze, Mörtel und Platten",
        unclassified: "Nicht klassifiziert",
      },
    },
    calculation: {
      panelDesc: "Einstellungen der statischen Analysen: Wärmeübergangswiderstände und Luftdruck. Die Wärmeübergangswiderstände werden auch in der Zeichnung des Aufbaus eingestellt.",
      advancedSettings: "Erweiterte Einstellungen",
      showHide: "ein- / ausblenden",
      atmosphericPressure: "Luftdruck [Pa]",
      modelsHint: "Modell des Sättigungsdrucks: Magnus-Tetens-Näherung (fest). Diffusionsmodell: stationäres Glaser-Verfahren / 2D-Dampfdiffusion beim Bemessungszustand (fest). Tauwassermodell: Unstetigkeit des Dampfstroms in der Tauwasserebene (1D) / lokale Markierung RH>100% (2D) (fest). Sie sind hier zur Transparenz angegeben; sie können noch nicht vom Benutzer ausgetauscht werden. Die Einstellungen für Netz, Iterationen und Toleranzen des hygrothermischen 2D-Lösers befinden sich jetzt unter Einstellungen (Zahnradsymbol oben rechts) → Registerkarte 2D-Löser.",
      diagnosticsTitle: "Löserdiagnose (2D-Netz)",
      diagnosticsIntro: "Alle Details dazu, wie das letzte hygrothermische 2D-Ergebnis tatsächlich berechnet wurde - Verfahren, Netzgröße, Konvergenz, Verlauf der Residuen - getrennt für die Lösung des Temperatur- und des Dampfdruckfelds (sie können sich stark unterscheiden: Das Dampffeld ist meist deutlich schlechter konditioniert). Die Konvergenz des Lösers (diese Karte) und die ingenieurmäßige Genauigkeit (die Netzkonvergenztabelle unten, bei automatischem Netz) sind zwei verschiedene Fragen - eine vollständig konvergierte Lösung auf einem groben Netz kann dennoch ungenau sein.",
      thermal: "Thermisch",
      vapourPressure: "Dampfdruck",
      vapour: "Dampf",
      addLayerForDiagnostics: "Fügen Sie mindestens eine Schicht hinzu, um hier die Löserdiagnose zu sehen.",
      noDiagnosticsYet: "Noch keine Diagnose.",
      methodUsed: "Verwendetes Verfahren",
      autoSuffix: " (auto)",
      solverAuto: "automatisch",
      meshCells: "Netzzellen",
      iterationsCycles: "Iterationen / Zyklen",
      residualInitial: "Residuum (anfangs)",
      residualFinal: "Residuum (final)",
      reductionFactor: "Reduktionsfaktor",
      solverFlux: "Wärmestrom des Lösers q",
      energyBalanceError: "Fehler der Energiebilanz",
      spdSystem: "SPD-System",
      conductivityContrast: "Leitfähigkeitskontrast",
      finalStatus: "Endstatus",
      yes: "Ja",
      no: "Nein",
      residualChartTitle: "Residuum je Iteration (logarithmisch)",
      compareSolvers: "CG und V-cycle an diesem Problem vergleichen",
      runCalculationFirst: "Führen Sie zuerst eine Berechnung durch.",
      solvingBothMethods: "Berechnung mit beiden Verfahren…",
      colMethod: "Verfahren",
      colQThermal: "q (thermisch)",
      colIterations: "Iterationen",
      colResidual: "Residuum",
      colStatus: "Status",
      colTime: "Zeit",
      meshLadder: "Netzkonvergenzstufen (diese Berechnung):",
      colRows: "Zeilen",
      colSolverConverged: "Löser konvergiert",
      colAccepted: "Akzeptiert",
    },
    charts: {
      noDataYet: "Noch keine Daten",
      legendToggle: (label: string) => `${label} (zum Aus- oder Einblenden klicken)`,
    },
    search: {
      clearSearch: "Suche löschen",
      filters: "Filter",
    },
    monthly: {
      title: "Monatliches Glaser-Verfahren",
      panelDesc: "Quasistationäre Monatsanalyse (1D), das Verfahren der bisherigen Thermal-Anwendung: Glaser an jeder Grenzfläche Monat für Monat, wobei sich Tauwasser von einem Monat zum nächsten ansammelt und verdunstet, gespeist von den Monatsmitteln des Standortklimas. Sie läuft bei jeder Berechnung mit, neben den Ergebnissen Vereinfacht und 2D-Netz.",
      inputsTitle: "Berechnungseingaben",
      summaryClass: (cls: number) => `Feuchteklasse ${cls}`,
      summaryConstant: (ti: string, unit: string, rhi: string) => `Ti ${ti} ${unit}, RHi ${rhi} %`,
      climateOfSite: "Klima des Standorts",
      summaryDrying: "Austrocknungszeit",
      outsideClimate: "Außenklima",
      outsideClimateTip: "Das im Tab Daten gewählte Standortklima (ein Standort auf der Karte oder eine EPW-Datei), dasselbe wie bei den anderen Analysen. Monatsmittel der Stundenwerte: Te = Mittel von T, pv,e = Mittel von RH·psat(T).",
      loadingClimate: "Klima wird geladen…",
      change: "ändern →",
      noClimate: "Noch kein Klima.",
      insideClimate: "Innenklima",
      tipIso13788: "Feuchteklassen, UNI EN ISO 13788: Δp = 270 · Klasse · clamp((20 − Te) / 20; 0; 1) Pa, pi = pe + 1.10 · Δp, Ti = 20 °C.",
      tipEn15026: "EN 15026 Annex D: Stunde für Stunde aus dem gleitenden 24-h-Mittel der Außentemperatur der EPW-Datei, dann je Monat gemittelt.",
      tipConstant: "Konstante Innentemperatur und relative Luftfeuchte in jedem Monat.",
      insideModel: "Innenklimamodell",
      ashraeOption: "ASHRAE 160 - noch nicht verfügbar: die Werte werden derzeit geprüft",
      modelIso13788: "Feuchteklassen (UNI EN ISO 13788)",
      untilAshrae: " - bis ASHRAE 160 verfügbar ist",
      modelConstant: "Konstante Ti und RHi",
      modelEn15026: "EN 15026 Annex D",
      humidityClass: "Feuchteklasse",
      classOption: (cls: number) => `Klasse ${cls}`,
      moistureLoad: "Feuchtelast",
      loads: {
        "normal+5": "Normale Feuchtelast + 5 % (Bemessungsfall)",
        normal: "Normale Feuchtelast",
        low: "Niedrige Feuchtelast",
        high: "Hohe Feuchtelast",
      },
      dryingTime: "Austrocknungszeit",
      dryingTip: "Monate, die nötig sind, um 1 kg/m² Wasser in der Mitte der gewählten Schicht ab Oktober mit dem Monatsklima auszutrocknen. Verfahren der gesamten Analyse: UNI EN ISO 13788, monatliches Glaser-Verfahren mit Akkumulation.",
      dryingLayer: "Schicht der Austrocknungsberechnung",
      chooseLayer: "Wählen Sie die Schicht…",
      monthlyClimate: "Monatsklima",
      monthlyClimateUsed: "Monatsklima und verwendetes Innenklima",
      monthlyClimateTip: (utcOffset: string) => `Monatsmittel der Stundenwerte in Ortszeit (UTC${utcOffset}): Te = Mittel von T; pe = Mittel von RH·psat(T); RHe = pe / psat(Te).`,
      statusEmpty: "Die Schichtenfolge ist leer: Fügen Sie zuerst im Bereich Schichtenfolge Schichten hinzu.",
      statusInternalWall: "Keine Außenseite (INNEN auf beiden Seiten: eine Innenwand): Die Monatsanalyse benötigt ein Außen- und ein Innenklima.",
      statusBothOutside: "Keine Außenseite (AUSSEN auf beiden Seiten): Die Monatsanalyse benötigt ein Außen- und ein Innenklima.",
      statusChooseClimate: "Wählen Sie das Standortklima im Tab Daten (ein Standort auf der Karte oder eine EPW-Datei).",
      failed: "Die Monatsanalyse ist fehlgeschlagen.",
      verified: "Nachgewiesen",
      notVerified: (n: number) => `Nicht nachgewiesen (${n} ${n > 1 ? "Nachweise" : "Nachweis"})`,
      checkResidual: "Jahresbilanz",
      checkAdmissible: "zulässige Tauwassermenge",
      checkMould: "Schimmel (f_Rsi)",
      allPass: "Kein Tauwasser am Ende des Zyklus, jede angesammelte Menge innerhalb ihres zulässigen Werts, f_{Rsi} über f_{Rsi,min}.",
      allPassBoth: "Kein Tauwasser am Ende des Zyklus, jede angesammelte Menge innerhalb ihres zulässigen Werts, f_{Rsi} über f_{Rsi,min}, in beiden Schnitten.",
      settingsUsed: "Verwendete Einstellungen",
      startOfCycle: "Beginn des Zyklus",
      subLayers: "Teilschichten",
      subLayersValue: (rMax: string) => `jede Schicht mit R > ${rMax} m²K/W in gleiche Teilschichten unterteilt`,
      humidityDependentSd: "Feuchteabhängiger Sd-Wert",
      condensationTest: "Tauwasserprüfung",
      surfaces: "Oberflächen",
      surfacesValue: (rsi: string, rse: string) => `R_{si} = ${rsi} m²K/W, R_{se} = ${rse} m²K/W (Luftschichten nach EN ISO 6946, wie bei den anderen Analysen)`,
      sections: "Schnitte",
      sectionsValue: (list: string) => `${list} - getrennte 1D-Schnitte, kein kombiniertes Verfahren`,
      admissibleCondensate: "Zulässige Tauwassermenge",
      mould: "Schimmel",
      uValue: (section: string) => `U-Wert (${section})`,
      interfaces: (n: number) => `${n} Grenzflächen`,
      maxAccumulated: "Max. angesammeltes Tauwasser",
      interfacesCondensing: (n: number) => `${n} ${n === 1 ? "Grenzfläche" : "Grenzflächen"}`,
      noCondensation: "kein Tauwasser",
      leftAtEnd: "Verbleibend am Ende des Zyklus",
      mustBeZero: "muss 0 sein",
      mouldStat: "Schimmel: f_{Rsi} / f_{Rsi,min}",
      criticalMonth: (month: string) => `kritischer Monat ${month}`,
      noMonthAtRisk: "kein Monat gefährdet",
      months: "Monate",
      dryingTag: (layer: string) => `1 kg/m² in ${layer}, ab Oktober`,
      monthByMonth: (section: string) => `Monat für Monat - ${section}`,
      month: "Monat",
      maTotal: "M_{a} gesamt",
      noInterfaceCondenses: "An keiner Grenzfläche fällt in irgendeinem Monat Tauwasser aus.",
      monthsNote: (unit: string) => `G_{c}: Tauwasserbildung (+) oder Verdunstung (−) des Monats; M_{a}: angesammelt am Monatsende [${unit}]. Grenzflächen von der Außenoberfläche aus nummeriert.`,
      admissibleTitle: (section: string) => `Zulässige Tauwassermenge - ${section}`,
      interface: "Grenzfläche",
      maxMa: (unit: string) => `Max. M_{a} [${unit}]`,
      admissibleColumn: (unit: string) => `Zulässig [${unit}]`,
      materialAndRule: "Material und Regel",
      notAssessed: "nicht bewertet",
      within: "eingehalten",
      exceeded: "⚠ überschritten",
      admissibleNote: "An einer Grenzfläche zwischen zwei Materialien gilt die kleinere ihrer zulässigen Mengen.",
      glaserDiagram: "Glaser-Diagramm",
      abscissa: "Abszisse",
      thickness: "Dicke",
      diagramMonth: "Monat des Diagramms",
      condensationOverCycle: "Tauwasser im Zyklus",
      condensationNote: (n: number, months: string, maxG: string, unit: string, at: string) => `⚠ Tauwasser bildet sich in ${n} ${n === 1 ? "Monat" : "Monaten"} (${months}): höchstens ${maxG} ${unit} an ${at};`,
      evaporatedBy: (month: string) => `es ist bis Ende ${month} verdunstet.`,
      evaporatesWithin: "es verdunstet innerhalb des Zyklus.",
      notAllEvaporates: "es verdunstet nicht vollständig innerhalb des Zyklus.",
      mouldTitle: (section: string) => `Schimmel - UNI EN ISO 13788 - ${section}`,
      mouldNote: (rh: string, rsi: string, fRsi: string, rMin: string) => `RH_{si} ≤ ${rh} %, R_{si} = ${rsi} m²K/W: f_{Rsi} des Schnitts = ${fRsi}${rMin}. Monate mit T_{i} ≤ T_{e}: nicht bewertet (—); f_{Rsi,min} ≤ 0: keine Anforderung in diesem Monat.`,
      mouldRmin: (value: string, unit: string) => `; minimaler Wärmedurchlasswiderstand der Schichten für den kritischen Monat: ${value} ${unit}`,
      curvesShown: "Angezeigte Kurven (zum Aus- oder Einblenden klicken)",
      legendP: "Dampfdruck p",
      legendPsat: "Sättigungsdampfdruck p_{sat} (● Schichtgrenze, ○ innerhalb einer Schicht)",
      legendWet: "Grenzfläche mit Tauwasser",
      glaserAria: (month: string) => `Glaser-Diagramm, ${month}`,
      depthAxis: (unit: string) => `Tiefe von innen [${unit}]`,
      sdAxisSi: "Sd von innen [m]",
      sdAxisUk: "Dampfdiffusionswiderstand von innen [MN·s/g]",
      sdAxisImperial: "Dampfdiffusionswiderstand von innen [rep = 1/perm]",
      inside: "innen",
      outside: "außen",
      pressureAxis: "Druck [Pa]",
      surface: "Oberfläche",
      node: (k: number) => `Knoten ${k}`,
      psatTip: (point: string, psat: number) => `${point}: p_sat ${psat} Pa`,
      legendCondensed: "im Monat ausgefallen",
      legendEvaporated: "im Monat verdunstet",
      legendAccumulated: "angesammelt am Monatsende",
      noCondensateAnyMonth: "in keinem Monat Tauwasser",
      accumulationAria: "Ausgefallenes, verdunstetes und angesammeltes Wasser je Monat",
      condensed: "ausgefallen",
      evaporated: "verdunstet",
      accumulated: "angesammelt",
      sectionLabels: {
        bay: "Gefach (zwischen den Ständern)",
        stud: "Ständer",
        whole: "Gesamte Wand",
      },
      interfaceOuter: "Außenoberfläche",
      interfaceInner: (material: string) => `${material} | Innenoberfläche`,
      interfaceWithin: (material: string) => `in ${material}`,
      insideIso13788: (cls: number, fs: string, ti: number) =>
        `UNI EN ISO 13788 Feuchteklasse ${cls}${cls === 3 ? " (DM 26/06/2015)" : ""}: Δp = 270·${cls}·clamp((20 − Te)/20; 0; 1) Pa, pi = pe + ${fs}·Δp (höchstens psat(Ti)), Ti = ${ti} °C (Te, wenn wärmer)`,
      insideConstant: (ti: number, rhi: number) => `Konstant: Ti = ${ti} °C, RHi = ${rhi} %`,
      insideEn15026: (load: string) => `EN 15026 Annex D, ${load}: Stundenwerte aus dem gleitenden 24-h-Mittel der Außentemperatur, je Monat gemittelt`,
      startNotes: {
        OCTOBER_LEGACY: "Oktober, wie beim bisherigen Verfahren",
        NONE_CONDENSES: "kein Monat mit Tauwasser bei trockenem Start: Zyklus ab Oktober",
        ALL_CONDENSE: "jeder Monat mit Tauwasser bei trockenem Start: Zyklus ab Oktober",
        FIRST_CONDENSING: (month: string) => `erster Monat mit Tauwasser nach einem Monat ohne Tauwasser: ${month}`,
      },
      failureMould: (fRsi: string, fRsiMin: string, month: string) => `f_Rsi = ${fRsi} unter f_Rsi,min = ${fRsiMin} (kritischer Monat: ${month})`,
      failureResidual: (g: string, at: string) => `Tauwasser am Ende des Zyklus verbleibend: ${g} g/m² bei ${at}`,
      failureAdmissible: (g: string, at: string, admissible: string, material: string, rule: string) =>
        `${g} g/m² bei ${at}, über den zulässigen ${admissible} g/m² (${material}: ${rule})`,
      warningPicardCapped: (section: string, passes: number, tolM: number) =>
        `${section}: Die Iteration des feuchteabhängigen Sd-Werts erreichte in mindestens einem Monat ${passes} Durchläufe, ohne sich innerhalb von ${tolM} m einzustellen; die Werte des letzten Durchlaufs werden angezeigt.`,
      warningOutsideRhAbove100: "In mindestens einem Monat liegt der mittlere Außendampfdruck über dem Sättigungsdruck der mittleren Temperatur (RHe > 100 %); er wird unverändert verwendet.",
      presetLabels: {
        legacy: "Thermal (bisher)",
        iso13788: "UNI EN ISO 13788 (Standard)",
      },
      startRules: {
        legacy: "Oktober (bisher)",
        iso13788: "erster Monat mit Tauwasser nach einem Monat ohne Tauwasser (UNI EN ISO 13788), je Schnitt",
      },
      sdRules: {
        legacy: "feuchteabhängiger Sd-Wert aus der RH der Innenseite seiner Teilschicht (bisherige Regel)",
        iso13788: "feuchteabhängiger Sd-Wert aus der mittleren RH der beiden Seiten seiner Teilschicht",
      },
      picardRule: (tolM: number, max: number) => `bis die Summe von |ΔSd| ≤ ${tolM} m, höchstens ${max} Durchläufe`,
      pTolExact: "exakt p ≥ psat (bisher)",
      pTolRule: (pTol: number) => `p ≥ psat·(1 − ${pTol})`,
      mouldRule: (rh: number, rsi: number) => `UNI EN ISO 13788: f_Rsi,min je Monat mit RH_si ≤ ${rh} %, R_si = ${rsi} m²K/W`,
      admissibleRule: "bisherige Tabelle frmInfoVAP je Material; an einer Grenzfläche die kleinere zulässige Menge der angrenzenden Materialien; \"30 d\" gelesen als 30·ρ·d",
    },
    dynamic: {
      // Inputs tab
      desc: "Stunde für Stunde über mehrere Jahre mit realen Klimadaten, im Rahmen der EN 15026: Temperaturen, relative Luftfeuchte, Tauwasserbildung und Austrocknung des aktuellen Schichtaufbaus.",
      preview: "Vorschau",
      why: "Warum \"quasi-dynamisch\"?",
      hide: "ausblenden",
      settingsTitle: "Berechnungseinstellungen (Einstellungen → Quasi-dynamisch)",
      whyDynamicTerm: "Dynamisch",
      whyDynamic: ": Die Zeit wird Stunde für Stunde simuliert, sodass die Schichten Wärme und Feuchte speichern und abgeben - Befeuchtung im Winter und Austrocknung im Sommer, Baufeuchte, die über die Jahre austrocknet. Genau das können die stationären Berechnungen (Glaser, 2D, monatlich) nicht zeigen.",
      whyQuasiTerm: "Quasi",
      whyQuasi: ": Die Materialeigenschaften bleiben konstant. λ, ρc und µ ändern sich nicht mit der Feuchte; die Feuchtespeicherung ist linear, w = ξ·φ mit ξ als Sekante der Sorptionskurve bei 80 % r. F.; Wasser bewegt sich nur als Dampf (kein Flüssig-/Kapillartransport). Latente Wärme und die Wärmekapazität des Wassergehalts sind optional (In das Modell einbezogene Effekte, unten). Feuchtevariable Bahnen (CLIMA CONTROL) ändern dagegen ihren Sd-Wert mit der r. F., und Tauwasser wird beim Entstehen und Austrocknen verfolgt.",
      whyFullBefore: "Das ",
      whyFullTerm: "vollständig dynamische",
      whyFull: " Modell der EN 15026 (wie in WUFI) ergänzt feuchteabhängige Speicherung und Durchlässigkeit, Flüssigleitung und λ(w) sowie die Enthalpie von Dampf und Flüssigkeit in der Energiebilanz; es erfordert vollständige hygrische Materialdaten und ist am Benchmark des Annex B der Norm validiert. Es ist der nächste Schritt. Das quasi-dynamische Modell ist zuverlässig für dampfdiffusionsbestimmte Aufbauten (leichte Holz- und gedämmte Aufbauten mit Bahnen) und ein Werkzeug zur Vorabprüfung, keine Zertifizierung.",
      tabsLabel: "Quasi-dynamische Analyse",
      tabInputs: "Eingaben",
      tabResults: "Ergebnisse",
      runFirst: "Führen Sie zuerst die quasi-dynamische Analyse durch",
      outerSurface: "Außenoberfläche",
      absorptance: "Solarer Absorptionsgrad",
      emissivity: "Emissionsgrad",
      insideLoad: "Innenklima (EN 15026 Annex D)",
      loads: {
        "normal+5": "Normale Feuchtelast + 5 % (Bemessungsfall)",
        normal: "Normale Feuchtelast",
        low: "Niedrige Feuchtelast",
        high: "Hohe Feuchtelast",
      },
      ashrae160Note: "ASHRAE 160 noch nicht verfügbar (die Werte werden geprüft): Das UNI / EN-Modell wird verwendet.",
      modelAndRun: "Modell und Berechnung",
      run: "Berechnung",
      years: "Jahre",
      woodCriterion: "Holzkriterium [M.-%]",
      woodCriterionTitle: "Fest: Feuchtegehalt der Holzwerkstoffe ≤ 16 M.-%",
      construction: "Baufeuchte (anfängliche r. F. 95 %, sonst 80 %)",
      stepAuto: (step: string) => `automatisch (${step})`,
      solver2d: (method: string, tolerance: string, rows: number) => `${method} · Toleranz ${tolerance} · ${rows} Zeilen`,
      direct1d: "direkter 1D-Löser",
      calcSummary: (step: string, solver: string) => `Berechnung: Schritt ${step} · ${solver} ·`,
      changeInSettings: "in den Einstellungen ändern ⚙",
      runButton: (mode: string) => `Quasi-dynamische Analyse starten (${mode})`,
      calculating: "Berechnung läuft…",
      hint2d: "2D-Netz-Modus (Kopfzeile): Ständer und Dämmung bilden ein gekoppeltes Raster. Etwa 10-20 s pro simuliertem Jahr bei 20-40 Zeilen; feinere Raster dauern länger, und der Server bricht eine Berechnung nach 300 s ab.",
      hint1d: "Vereinfachter Modus (Kopfzeile): eine 1D-Säule durch die Schichten; Ständer werden nicht berücksichtigt (stellen Sie die Kopfzeile dafür auf 2D-Netz um).",
      emptyStratigraphy: "Der Schichtaufbau ist leer: Fügen Sie zuerst Schichten im Bereich Schichtaufbau hinzu.",
      noClimate: "Wählen Sie zuerst das Klima: Registerkarte Daten, Klima des Standorts (ein Standort auf der Karte oder eine EPW-Datei).",
      noOutdoorSide: "Keine Außenseite: Die quasi-dynamische Analyse benötigt eine AUSSEN- und eine INNENSEITE in der Zeichnung (Registerkarte Schichtaufbau).",
      analysisFailed: "Die Analyse ist fehlgeschlagen.",
      cancelled: "Die Berechnung wurde abgebrochen; das vorherige Ergebnis (falls vorhanden) wird weiterhin angezeigt.",
      unreadable: "Die Antwort des Servers konnte nicht gelesen werden.",
      unreachable: "Der Server war nicht erreichbar.",
      timeout: "Die Berechnung hat länger gedauert, als der Server erlaubt (300 s). Simulieren Sie weniger Jahre, verwenden Sie ein gröberes 2D-Raster oder einen längeren Zeitschritt.",
      statusError: (status: number) => `Der Server hat mit dem Status ${status} geantwortet.`,
      stopped: "Die Berechnung wurde vor dem Ende beendet (der Server hat möglicherweise sein Zeitlimit von 300 s erreicht). Simulieren Sie weniger Jahre, verwenden Sie ein gröberes 2D-Raster oder einen längeren Zeitschritt.",
      lastResult: (mode: string, years: number, source: string) => `Letztes Ergebnis: ${mode}, ${years} Jahr(e), ${source} ·`,
      openResults: "Ergebnisse öffnen →",
      mode2d: "2D-Netz",
      mode1d: "1D",
      modeSimplified: "Vereinfacht",
      modeSimplified1d: "Vereinfacht (1D)",
      // effects included in the model
      effectsLegend: "In das Modell einbezogene Effekte",
      effects: {
        dynamic: {
          label: "Dynamisches System",
          hint: "Wärme- und Feuchtespeicherung (die ∂/∂t-Terme). Aus: Jeder Zeitschritt ist der stationäre Zustand seines Klimas (stündliches Glaser-Verfahren); nur Tauwasser sammelt sich an.",
        },
        waterHeatCapacity: {
          label: "Wärmekapazität des Wassergehalts",
          hint: "ρc + w·c_w, mit c_w = 4190 J/(kg·K) und dem Wassergehalt w des vorherigen Zeitschritts.",
        },
        latentHeat: {
          label: "Latente Wärme",
          hint: "Wärme, die dort frei wird, wo Dampf sorbiert wird oder kondensiert, und dort entzogen wird, wo er verdunstet: h_v · Netto-Dampfzufluss jeder Zelle, h_v = 2500 kJ/kg (EN 15026:2007), aus dem Dampffeld des vorherigen Zeitschritts.",
        },
        variableSd: {
          label: "Variabler Dampfdiffusionswiderstand µ(w), Sd(r. F.)",
          hint: "Sd(r. F.) der Bahnen mit gemessener Kurve (CLIMA CONTROL 80 und 160) und µ(w) der Materialien mit feuchteabhängiger µ-Kurve, in jedem Zeitschritt aktualisiert. Aus: ihre festen Werte.",
        },
        rain: {
          label: "Regen",
          hint: "Schlagregen auf die Außenoberfläche, aus Regen und Wind des Klimas; das eindringende Wasser entweicht durch Verdunstung (kein Kapillartransport).",
        },
        conductivity: {
          label: "Variable Wärmeleitfähigkeit λ(w)",
          hint: "Erfordert feuchteabhängige λ-Daten für jedes Material.",
        },
        capillarity: {
          label: "Kapillarität",
          hint: "Flüssigtransport: erfordert die vollständige Sorptionskurve und die Flüssigtransportkoeffizienten jedes Materials.",
        },
      },
      availableSoon: "bald verfügbar",
      needsDynamic: "Erfordert das dynamische System (Speicherung).",
      noRain: "Das gewählte Klima enthält keinen Regen (PVGIS liefert keinen): Wählen Sie ERA5 (Open-Meteo), eine TMYx-Station oder eine EPW-Datei mit Regen.",
      rainSettings: "Regeneinstellungen",
      waterEntering: "In die Wand eindringendes Wasser",
      rainModeTitle: "Durch die Normen der Menüs festgelegt (Einstellungen → Allgemein)",
      rainAbsorb: "Saugfähige Fassade (EN 15026): 70 % in die Außenschicht",
      rainDrain: "Bekleidung mit Entwässerung (ASHRAE 160): 1 % dahinter",
      rainAbsorbHint: "Putz, Sichtmauerwerk: 70 % des Schlagregens dringen als flüssiges Wasser in die äußersten Zellen der Außenschicht ein, bis zur kapillaren Sättigung des Materials (w_cap); der Rest läuft ab.",
      rainDrainHint: "Verkleidung oder Platten auf einem entwässerten Hohlraum: 1 % des Schlagregens lagert sich im äußeren Teil der ersten Schicht hinter der Bekleidung ab (die Bemessungsleckage nach ASHRAE 160).",
      exposure: "Exposition (Schlagregenkoeffizient R2)",
      exposures: {
        low: "Niedriges Gebäude (bis 10 m)",
        "tall-low": "Hohes Gebäude, unterer Teil",
        "tall-mid": "Hohes Gebäude, mittlerer Teil",
        "tall-top": "Hohes Gebäude, oberer Teil",
      },
      exposureOption: (label: string, r2: number) => `${label} — R2 = ${r2} s/m`,
      rainFormula: "Regen auf die Wand = R2 × Windgeschwindigkeit × horizontaler Regen × cos(Winkel zwischen Wind und Fassade), nur von der Luvseite; Dächer erhalten zusätzlich den horizontalen Regen × cos(Neigung).",
      // orientation of the outer surface
      directions: ["N", "NO", "O", "SO", "S", "SW", "W", "NW"],
      orientLabel: (tilt: number, direction: string, azimuth: number) => `Außenoberfläche: Neigung ${tilt}°, ausgerichtet nach ${direction} ${azimuth}°. Ziehen Sie oder verwenden Sie die Pfeiltasten, um sie zu drehen.`,
      orientValue: (tilt: number, azimuth: number) => `Neigung ${tilt}°, Ausrichtung ${azimuth}°`,
      outside: "außen",
      inside: "innen",
      turning: "↔ Drehen (Ausrichtung)",
      tilting: "↕ Neigen",
      tiltTitle: (now: string) => `Neigung: 0° Flachdach, 90° senkrechte Wand (jetzt: ${now})`,
      flatRoof: "Flachdach",
      wall: "Wand",
      roof: "Dach",
      azimuthTitle: (now: string) => `Ausrichtung: Richtung der Außenoberfläche, Grad ab Norden, im Uhrzeigersinn (jetzt: ${now})`,
      anyFlat: "beliebig, flach",
      orientHint: (wallMinTilt: number) => `Ziehen Sie die Wand: links/rechts dreht sie (Ausrichtung), nach unten richtet sie zur Senkrechten auf, nach oben senkt sie - jeweils nur eines, bestimmt durch die erste Bewegung jedes Ziehens. Pfeiltasten: Schritte von 1°, Umschalt 5°. Eine Neigung ≥ ${wallMinTilt}° gilt als Wand, darunter als Dach.`,
      // progress window
      running: "Quasi-dynamische Analyse läuft",
      cancelling: "Wird abgebrochen…",
      sending: "Schichtaufbau wird an den Server gesendet",
      comparison: " (Vergleich)",
      simulatedUpTo: (day: string, years: number) => `simuliert bis ${day} von ${years} Jahr(en)`,
      elapsed: (time: string) => `vergangen ${time}`,
      timeLeft: (time: string) => ` · noch etwa ${time}`,
      // Results tab
      resultsDesc: "Ergebnis der letzten Berechnung. Ziehen Sie über ein Zeitdiagramm, um einen Zeitraum auszuwählen; ein Doppelklick wählt die gesamte Berechnung.",
      noResult: "Noch kein Ergebnis: Führen Sie die Analyse unter Quasi-dynamische Analyse durch.",
      checksNotSatisfied: (failed: number, total: number) => `⚠ ${failed} von ${total} Nachweisen nicht erfüllt`,
      allSatisfied: "alle Nachweise erfüllt",
      noCheckApplicable: "kein Nachweis anwendbar",
      verdictLine: (mode: string, years: number, source: string) => `${mode} · ${years} Jahr(e) · ${source} · Vorabprüfung, keine Zertifizierung · klicken Sie auf einen Nachweis für die Details`,
      modeMismatch: (resultMode: string, headerMode: string) => `Dieses Ergebnis wurde im Modus ${resultMode} berechnet; die Kopfzeile steht jetzt auf ${headerMode}. Führen Sie die Berechnung erneut aus, um es zu aktualisieren.`,
      notApplicable: "n. a.",
      satisfied: "erfüllt",
      notSatisfied: "nicht erfüllt",
      notSatisfiedWarn: "⚠ nicht erfüllt",
      sectionsLabel: "Abschnitte",
      secRun: "Berechnung und Klimadaten",
      secPeriod: "Ergebnisse im Zeitraum",
      secPeriodTitle: "Ergebnisse im ausgewählten Zeitraum",
      secConv: "Konvergenz",
      secConvTitle: "Konvergenz im ausgewählten Zeitraum",
      secCharts: "Diagramme",
      secChartsTitle: "Diagramme im ausgewählten Zeitraum",
      secFields: "2D-Felder",
      secFieldsTitle: "2D-Felder zu bemerkenswerten Stunden",
      secHourly: "Stunde für Stunde",
      secHourlyTitle: "Stunde für Stunde (letztes simuliertes Jahr)",
      secLayers: "Schichten des Modells",
      resultBadge: (mode: string) => `Ergebnis ${mode}`,
      source: "Quelle",
      sourceDbs: (radiation: string, weather: string) => ` (Strahlung ${radiation}, Wetter ${weather})`,
      location: "Standort",
      period: "Zeitraum",
      insideClimate: "Innenklima",
      attribution: "Quellenangabe",
      runLine: (years: number, month: string, step: string, grid: string, cells: number, seconds: string, mass: string, energy: string) =>
        `Berechnung: ${years} Jahre ab 1. ${month}, Zeitschritt ${step}, ${grid}${cells} Zellen; berechnet in ${seconds} s; Bilanzresiduen: Masse ${mass}, Energie ${energy}.`,
      gridText: (rows: number, columns: number) => `2D-Raster ${rows} × ${columns} = `,
      rhInterfaces: "Relative Luftfeuchte an den Grenzflächen (Tagesmittel)",
      rhAxis: "r. F. [%]",
      tInterfaces: (unit: string) => `Temperatur an den Grenzflächen (Tagesmittel) [${unit}]`,
      outsideAir: "Außenluft",
      membraneSd: "Sd der Bahnen (Tagesmittel, Band = Tagesschwankung)",
      condensateTitle: "Tauwasser (Tagesmittel) [g/m²]",
      total: "gesamt",
      waterTitle: "Gesamtwasser im Bauteil [kg/m²]",
      totalWater: "Gesamtwasser",
      rainTitle: "Regen auf die Außenoberfläche und in die Wand eindringend (pro Tag) [kg/m²]",
      rainAxis: "kg/m² pro Tag",
      rainOnSurface: "Schlagregen auf die Oberfläche",
      rainEntering: "in die Wand eindringend",
      woodTitle: "Feuchtegehalt der Holzwerkstoffschichten [M.-%]",
      rhMapTitle: "Relative Luftfeuchte im Bauteil, Tag für Tag",
      fieldColdest: "In der kältesten Stunde der Berechnung",
      fieldPeak: "Beim Tauwasserhöchstwert",
      inPeriod: "im Zeitraum",
      outsidePeriod: "außerhalb des Zeitraums",
      fieldLine: (date: string, hour: string, te: string, unit: string) => `${date}, ${hour}:00 (Stunde der Berechnung) · außen ${te} ${unit}`,
      fieldCondensate: (value: string) => ` · Tauwasser ${value} g/m²`,
      noCondensateRun: "Kein Tauwasser in der gesamten Berechnung.",
      hourlyTitle: "Stunde für Stunde: r. F. an den 4 feuchtesten Grenzflächen des letzten Jahres",
      hourlyLastYearOnly: (from: string) => `Die stündlichen Reihen umfassen nur das letzte simulierte Jahr (ab ${from}).`,
      selectLastYear: "Letztes Jahr auswählen",
      membraneVariable: (min: number, max: number) => `Bahn, Sd ${min}–${max} m je nach r. F.`,
      membraneFixed: (sd: string) => `Bahn, Sd ${sd} m`,
      xiText: (xi: string, source: string) => `ξ = ${xi} kg/m³ (${source})`,
      // period filter
      dayLabel: (day: number, month: string, year: number) => `${day}. ${month}, Jahr ${year}`,
      yearN: (year: number) => `Jahr ${year}`,
      days: "Tage",
      presetPeriods: "Vordefinierte Zeiträume",
      wholeRun: "Gesamte Berechnung",
      season: (season: string, from: string, to: string, year: number) => `${season} (${from}–${to}), Jahr ${year}`,
      winter: "Winter",
      summer: "Sommer",
      from: "Von",
      to: "bis",
      periodHint: "Ziehen Sie über den Streifen oder ein Diagramm unten, um einen Zeitraum auszuwählen; ein Doppelklick auf ein Diagramm wählt die gesamte Berechnung.",
      day: "Tag",
      month: "Monat",
      simulatedYear: "Simuliertes Jahr",
      overviewLabel: "Ausgewählter Zeitraum innerhalb der gesamten Berechnung: zum Ändern ziehen",
      overviewTitle: "Außenlufttemperatur, gesamte Berechnung",
      // results in the period
      surfaceTmin: "Innenoberfläche, min. Temperatur",
      fRsiMin: "Temperaturfaktor f_Rsi, min.",
      fRsiTag: (day: string, limit: number) => `${day} · Grenzwert ${limit}`,
      noHour10K: "keine Stunde mit Ti − Te ≥ 10 K",
      condMax: "Tauwasser, max.",
      condTag: (day: string, days: number) => `${day} · ${days} Tag(e) mit Tauwasser`,
      noneInPeriod: "keines im Zeitraum",
      condLastDay: "Tauwasser am letzten Tag",
      driedOut: "ausgetrocknet",
      stillPresent: "noch vorhanden",
      woodMax: "Holzfeuchte, max.",
      rhMax: "r. F. an einer Grenzfläche, max. (stündlich)",
      rainIn: "In die Wand eindringender Regen",
      rainTag: (onSurface: string) => `von ${onSurface} kg/m² Schlagregen auf die Oberfläche`,
      waterChange: "Wasser im Bauteil, Änderung",
      waterTag: (from: string, to: string, trend: string) => `${from} → ${to} kg/m² · ${trend}`,
      netWetting: "Netto-Befeuchtung",
      netDrying: "Netto-Austrocknung",
      noNetChange: "keine Netto-Änderung",
      outsideMean: "Außenluft, Mittel",
      outsideTag: (rh: string, min: string, max: string, unit: string) => `r. F. ${rh} % · ${min} bis ${max} ${unit} (Tagesmittel)`,
      checksInPeriod: "Nachweise im Zeitraum",
      screening: "Vorabprüfung, keine Zertifizierung",
      checkCondensate: (limit: number) => `Tauwasser ≤ ${limit} g/m² im gesamten Zeitraum`,
      checkCondensateDetail: (max: string, day: string, days: number, end: string) => `max. ${max} g/m² am ${day}; ${days} Tag(e) mit Tauwasser; ${end} g/m² am letzten Tag`,
      noCondensatePeriod: "Kein Tauwasser im Zeitraum.",
      checkWood: (limit: number) => `Holzwerkstoffe: Feuchtegehalt ≤ ${limit} M.-%`,
      checkWoodDetail: (name: string, max: string, day: string, daysOver: number) => `${name}: max. ${max} M.-% am ${day}${daysOver ? `, ${daysOver} Tag(e) darüber` : ""}`,
      noWood: "Kein Holzwerkstoff.",
      checkFrsi: (limit: number) => `Temperaturfaktor f_Rsi ≥ ${limit} (Stunden mit Ti − Te ≥ 10 K)`,
      checkFrsiDetail: (value: string, day: string) => `minimaler f_Rsi ${value} am ${day}`,
      noHour10KPeriod: "Keine Stunde mit Ti − Te ≥ 10 K im Zeitraum.",
      accumulationNote: "Der Nachweis der Akkumulation von Jahr zu Jahr erfordert ganze Jahre: siehe die Nachweise am Ende der Berechnung oben.",
      membranesWood: "Bahnen und Holzwerkstoffe",
      colMembrane: "Bahn",
      colSdMean: "Sd Mittel [m]",
      colSdMin: "Sd min [m]",
      colSdMax: "Sd max [m]",
      byRh: "je nach r. F.",
      fixedSd: "fest",
      noMembrane: "Keine Bahn im Schichtaufbau.",
      colWood: "Holzwerkstoff",
      colUMean: "u Mittel [M.-%]",
      colUMax: "u max [M.-%]",
      colDaysOver: (limit: number) => `Tage > ${limit} M.-%`,
      woodNote: "u = täglicher Spitzenwert der feuchtesten Zelle des Materials.",
      layersInPeriod: "Schichten im Zeitraum",
      colLayer: "Schicht",
      colTmin: (unit: string) => `T min [${unit}]`,
      colTmean: (unit: string) => `T Mittel [${unit}]`,
      colTmax: (unit: string) => `T max [${unit}]`,
      colRhMean: "r. F. Mittel [%]",
      colRhMax: "r. F. max [%]",
      colWaterMean: "Wasser Mittel [kg/m²]",
      colCondMax: "Tauwasser max [g/m²]",
      membraneFace: "Bahn: in 1D eine Fläche zwischen den Zellen (siehe ihren Sd-Wert und die Grenzflächen auf beiden Seiten)",
      layersNote: "Schichten von innen nach außen. Die Werte jeder Schicht sind tägliche Volumenmittel; Minimum und Maximum beziehen sich auf diese Tagesmittel.",
      interfacesInPeriod: "Grenzflächen im Zeitraum",
      colInterface: "Grenzfläche",
      colRhMaxHourly: "r. F. max, stündlich [%]",
      colDays80: "Tage mit mittlerer r. F. > 80 %",
      interfacesNote: "T-Minimum und -Maximum der Tagesmittel; r. F.-Maximum über die Stunden des Zeitraums.",
      profileTitle: "Relative Luftfeuchte im Bauteil im Zeitraum",
      // convergence
      convSettings: (method: string, tolerance: string, maxIter: number, step: string) => `${method} · Toleranz ${tolerance} · max. ${maxIter} Iterationen pro Lösung · Zeitschritt ${step}`,
      hoursConverged: "Konvergierte Stunden",
      everyHour: "jede Stunde",
      hoursNotConverged: (hours: number) => `${hours} Stunde(n) nicht konvergiert`,
      maxIterations: "Lineare Lösung, max. Iterationen",
      limitN: (limit: number) => `Grenzwert ${limit}`,
      directSolver: " · direkter Löser",
      maxResidual: "Lineare Lösung, max. Residuum",
      toleranceTag: (tolerance: string) => `Toleranz ${tolerance}`,
      energyResidual: "Energiebilanz, max. Residuum",
      relativeLimit: (limit: string) => `relativ · Grenzwert ${limit}`,
      massResidual: "Massenbilanz, max. Residuum",
      massTag: (limit: string, max: string) => `relativ · Grenzwert ${limit} · Maximum der gesamten Berechnung ${max} kg/m² pro Schritt`,
      notConvergedTitle: "Stunde für Stunde: nicht konvergierte Stunden markiert",
      causes: ["Wärmelösung", "Dampflösung", "aktive Menge der Tauwasserbildung", "Sd der Bahnen"],
      causeHours: (cause: string, hours: number) => `${cause} ${hours} h`,
      notConvergedList: (list: string) => `Nicht konvergiert: ${list}.`,
      allConverged: "Jede Stunde des Zeitraums ist konvergiert: Alle linearen Lösungen haben die Toleranz erreicht, die aktive Menge der Tauwasserbildung hat sich stabilisiert und die Sd-Iteration der Bahnen ist konvergiert.",
      residualNote: "Die Bilanzresiduen werden getrennt angezeigt: Ein relatives Massenresiduum über 1e−6 in einer Stunde mit nahezu keinem Fluss durch die Oberflächen kann einer winzigen absoluten Menge entsprechen.",
      iterationsTitle: (perHour: boolean) => `Iterationen der linearen Lösung (${perHour ? "pro Stunde" : "ungünstigste Stunde jedes Tages"})`,
      residualsTitle: (perHour: boolean) => `Residuen, log10 (${perHour ? "pro Stunde" : "ungünstigste Stunde jedes Tages"})`,
      iterations: "Iterationen",
      maxIterSeries: "max. Iterationen (Wärme oder Dampf)",
      linearRelative: "lineare Lösung (relativ)",
      energyBalance: "Energiebilanz",
      massBalance: "Massenbilanz",
      failsTitle: "Nicht konvergierte Stunden (die ersten 30 im Zeitraum)",
      colHour: "Stunde",
      colNotConverged: "Nicht konvergiert",
      colIterations: "Iterationen",
      colLinearResidual: "Lineares Residuum",
      converged: "konvergiert",
      linearNotConverged: "lineare Lösung nicht konvergiert",
      activeSetNotConverged: "aktive Menge oder Sd der Bahnen nicht konvergiert",
      // charts
      curvesShown: "Angezeigte Kurven (zum Aus- oder Einblenden klicken)",
      rhCondensation: "100 % (Tauwasserbildung)",
      membrane: "Bahn",
      profileLabel: "Relative Luftfeuchte im Bauteil",
      outsideCaps: "AUSSEN",
      profileMean: "Mittel der Tagesmittel",
      profileMax: "höchstes Tagesmittel",
      profileMin: "niedrigstes Tagesmittel",
      insideAtBottom: "innen unten",
      quantity: "Größe",
      fieldRh: "r. F.",
      fieldT: "Temperatur",
      fieldWc: "Tauwasser",
      fieldHover: (layer: string, stud: boolean, x: string, y: string, temperature: string, unit: string, rh: string, condensate: string) =>
        `${layer}${stud ? " (Ständer)" : ""} · x ${x} mm, ${y} mm von innen · T ${temperature} ${unit} · r. F. ${rh} % · Tauwasser ${condensate} kg/m³`,
      noCondensateHour: "kein Tauwasser in dieser Stunde",
      fieldHint: "Bewegen Sie den Mauszeiger über eine Zelle, um ihre Werte zu sehen. Breite: ein halber Ständerabstand zwischen den Symmetrieebenen.",
    },
    results: {
      badge2D: "2D-Netz - letzte Berechnung",
      badgeSimplified: "vereinfacht - letzte Berechnung",
      badgeNone: "noch keine Ergebnisse",
      warnings: "Hinweise",
      stratigraphy: "Schichtaufbau",
      fieldTitle: "Feld durch den Schichtaufbau",
      zoomResetTitle: "Zoom zurücksetzen (scrollen Sie über der Zeichnung, um zu vergrößern/verkleinern)",
      standingNoteTitle: "Numerische Verfahren & Materialwahl",
      standingNoteDetail: "Wo anwendbar (2D-Netzmodus), werden numerische Verfahren eingesetzt. Die Prüfung ihrer Gültigkeit sowie der Eignung jedes im Schichtaufbau gewählten Materials und Produkts für den konkreten Fall liegt in der Verantwortung des zuständigen Planers.",
      inputErrorTitle: "Berechnung nicht ausgeführt - ungültige Eingabe",
      inputErrorTitleQuota: "Berechnung nicht ausgeführt - Rechenzeit des Servers aufgebraucht",
      inputErrorTitleRateLimit: "Berechnung nicht ausgeführt - Anfragelimit erreicht",
      cancelledTitle: "BERECHNUNG ABGEBROCHEN",
      cancelledDetail: "Berechnung vom Benutzer abgebrochen. Die folgenden Werte stammen aus der letzten tatsächlich abgeschlossenen Berechnung.",
      noWarnings: "Fügen Sie dem Schichtaufbau mindestens eine Schicht hinzu, um hier die technischen Hinweise zu sehen.",
      uLimitLabels: {
        wall: "Außenwand (DE Bestand, 0,24)",
        roof: "Dach / oberste Geschossdecke (DE Bestand, 0,24)",
        floor: "Boden gegen Erdreich/unbeheizt (DE Bestand, 0,30)",
        custom: "Eigener Grenzwert",
      },
      checks: {
        uValue: "U-Wert",
        uValue2D: "U-Wert (2D)",
        limit: (value: string, reference: string) => `Grenzwert ${value} (${reference})`,
        interstitial: "Tauwasser im Bauteil und Schimmel",
        interstitial2D: "Tauwasser im Bauteil und Schimmel (2D)",
        interstitialCondensation: "Tauwasser im Bauteil",
        possibleMould: "mögliche Schimmelbildung",
        none: "keine",
        wet1D: (rate: string, node: string) => `${rate} g/(m²·h) an Knoten ${node} (EN ISO 13788, Bemessungsmonat)`,
        wet2D: (cells: number, share: string) => `${cells} ${cells > 1 ? "Zellen" : "Zelle"} mit RH > 100 % · ${share} % des Querschnitts (Bemessungsbedingung)`,
        maxRh: (where: string, rh: string) => `max. RH ${where} ${rh} % (Bemessungsbedingung)`,
        betweenLayers: "zwischen den Schichten",
        inTheField: "im Feld",
        noFace: "keine Grenzfläche zwischen Schichten",
        surface1D: "RH der Innenoberfläche (Schimmel)",
        surfaceDetail1D: "Grenzwert 80 % · klicken Sie für das psychrometrische Diagramm",
        surface2D: "Innenoberfläche (Schimmel)",
        surfaceDetail2D: "max. RH, Grenzwert 80 % · min. Temperatur · klicken Sie für das psychrometrische Diagramm",
        solver: "Solver",
        notConverged: "nicht konvergiert",
        solverDetail: "Wärme- und Dampffeld",
        monthly: "Monatliches Glaser-Verfahren",
        notCalculated: "nicht berechnet",
        checksNotSatisfied: (n: number) => `${n} ${n > 1 ? "Nachweise" : "Nachweis"} nicht erfüllt`,
        verified: "nachgewiesen",
        monthlyOk: "Jahresbilanz, zulässige Tauwassermenge, Schimmel",
        monthlyFailure: {
          residual: "Tauwasser am Ende des Zyklus verbleibend",
          admissible: "zulässige Tauwassermenge überschritten",
          mould: "Schimmelrisiko (f_Rsi)",
        },
        satisfied: "Erfüllt",
        notSatisfied: "Nicht erfüllt",
        ofLimit: (percent: number) => `${percent} % des Grenzwerts`,
        notSatisfiedCount: (bad: number, total: number) => `⚠ ${bad} von ${total} Nachweisen nicht erfüllt`,
        allSatisfied: (total: number) => `alle ${total} Nachweise erfüllt`,
        mode2D: "2D-Netz (stationär, finite Volumen)",
        mode1D: "Vereinfacht 1D (EN ISO 6946 / EN ISO 13788)",
        clickForDetails: "klicken Sie auf einen Nachweis für die Details",
      },
      temperature: "Temperatur",
      humidity: "Feuchte",
      humidityRh: "Feuchte (RH)",
      vapourPressure: "Dampfdruck",
      vapourPressurePa: "Dampfdruck [Pa]",
      moistureContent: "Feuchtegehalt",
      secondProfileChart: "Zweites Profildiagramm",
      field2DDesc: "Ein symmetrischer halber Ständerabstand (oder das ganze Feld ohne tragendes Element), Zeilenhöhen maßstabsgetreu. Klicken Sie auf eine Zelle für ihre genauen Werte. Ziehen Sie die senkrechte Linie, um die Diagramme des vertikalen Profils daneben an diese Stelle zu verschieben",
      moistureNote: "Rot = übersättigt (RH>100%) - das Sorptionsgleichgewicht w(RH) beschreibt diese Zelle nicht mehr zutreffend, unabhängig davon, ob ein Feuchtemodell verfügbar ist.",
      midBay: "Feldmitte",
      midBayColumn: "Spalte in Feldmitte",
      studCentreline: "Ständerachse",
      fromStud: (distance: string) => `${distance} von der Ständerachse`,
      verticalProfile: (where: string) => `Vertikales Profil (${where})`,
      temperatureThroughThickness: "Temperatur über die Dicke",
      rhThroughThickness: "RH über die Dicke",
      psatChartLabel: "Sättigungs- und Dampfdruck über die Dicke",
      rhChartLabel: "Relative Feuchte über die Dicke",
      cell: {
        hint: "Klicken Sie auf eine Zelle der Karte oben, um hier ihre Werte zu sehen.",
        interiorFilm: "Innerer Wärmeübergang",
        exteriorFilm: "Äußerer Wärmeübergang",
        layer: (n: number) => `Schicht ${n}`,
        material: "Material",
        noMoistureModel: "nicht verfügbar (kein Feuchtemodell)",
        dryNoModel: "(trocken, kein Modell)",
        saturated: "Status: GESÄTTIGT / TAUWASSERGEFAHR",
        rawNote: ">100% - technischer Wert, nicht die oben angezeigte RH",
      },
      field1DDesc: "Der Schnitt zeigt die eine feste Bemessungsbedingung, für die die Berechnung gelöst wird (bearbeiten Sie Te/RHe/Ti/RHi direkt am grafischen Schnitt im Bereich Schichtaufbau), keinen Jahresmittelwert, im gleichen Maßstab wie der Bereich Schichtaufbau. Ohne 2D-Wärmebrückenberechnung ist das Farbfeld 1D: es ändert sich nur über die Dicke, und die Umrisse der tragenden Elemente dienen nur zur Orientierung (die Farbe ändert sich dort seitlich nicht). Liegt eine 2D-Netzberechnung der Wärmebrücke vor, zeigt die Ansicht Temperatur dieses tatsächlich gelöste Feld und die Ansicht Feuchte die lokale RH desselben Feldes, berechnet zusammen mit dem 1D-Dampfdruckprofil nach Glaser (der Dampfdiffusionswiderstand wird seitlich nicht aufgelöst; es handelt sich also um eine Näherung und keine echte 2D-Dampfberechnung - wählen Sie dafür den Berechnungsmodus 2D-Netz).",
      sectionTitle: (field: string) => `${field} durch den Schichtaufbau - Bemessungsbedingung`,
      sectionTitleShort: (field: string) => `${field} durch den Schichtaufbau`,
      noResultsYet: "Noch keine Ergebnisse.",
      diagramNote2D: (humidity: boolean): string =>
        humidity
          ? "Temperaturfeld des 2D-Netzes + 1D-Dampfprofil (pv seitlich nicht aufgelöst), an jeder Ständerachse gespiegelt"
          : "2D-Netz, an jeder Ständerachse gespiegelt (2 Achsabstände dargestellt)",
      profilesTitle: "Profile über die Dicke (Bemessungsbedingung)",
      glaserSubtitleBefore: "Glaser-Diagramm — Tauwasser, wo ",
      glaserSubtitleAfter: ", bei der Bemessungsbedingung",
      info: {
        uvalue: "Wärmedurchgangskoeffizient, geprüft gegen den gewählten Referenzgrenzwert (oder einen eigenen Wert).",
        rw: "Vereinfachte Schätzung nach dem Massegesetz (in Anlehnung an EN 12354-1, Rw ≈ 37,5·log10(m′) − 42 dB) nur aus der gesamten flächenbezogenen Masse - nicht gültig für mehrschalige Aufbauten/Aufbauten mit Hohlraum (Resonanz, Koinzidenz nicht modelliert). Kein Ersatz für ein gemessenes oder zertifiziertes Rw.",
        condensationGlaser: "Glaser 1D: Dampfdruck über dem Sättigungsdruck an einer inneren Grenzfläche, bei der festen Bemessungsbedingung (Bereich Schichtaufbau). Ein stationärer Nachweis, keine Jahresbilanz.",
        surfaceCondensation: "Temperatur/RH direkt an der Innenoberfläche, bei der festen Bemessungsbedingung. Über ca. 80% RH wird Schimmelbildung auch ohne eigentliches Tauwasser begünstigt; bei 100% RH bildet sich an der Oberfläche Tauwasser.",
        interstitialCondensation2d: "Höchste lokale RH im konvergierten 2D-Feld. Über ca. 80% wird Schimmelbildung begünstigt; bei 100% ist die Zelle übersättigt (Tauwasser).",
      },
      bar: {
        empty: "Fügen Sie mindestens eine Schicht hinzu, um hier Ergebnisse zu sehen.",
        moreInfo: "Weitere Informationen",
        previousSection: "Vorheriger Abschnitt",
        nextSection: "Nächster Abschnitt",
        sectionOf: (i: number, n: number) => `Abschnitt ${i} von ${n}`,
        collapseToggle: "Ergebnisleiste ein-/ausklappen",
        thermal: "Wärme",
        thermal2D: "Wärme (2D)",
        hygrometric: "Feuchte",
        hygrometric2D: "Feuchte (2D)",
        acoustic: "Akustik",
        uValue: "U-Wert",
        uValue2D: "U-Wert (2D)",
        transmittanceConvergence: "Konvergenz Wärmedurchgang",
        vapourConvergence: "Konvergenz Dampfdruck / RH",
        converged: "Konvergiert",
        notConverged: "Nicht konvergiert",
        solveTime: (time: string) => `Rechenzeit: ${time}`,
        surfaceCondensation: "Oberflächentauwasser",
        condensing: "Tauwasser",
        mouldRisk: "Schimmelrisiko",
        ok: "OK",
        noSurfaceCondensation: "Kein Oberflächentauwasser",
        condensationGlaser: "Tauwasser (Glaser)",
        interstitialCondensation: "Tauwasser im Bauteil",
        noCondensation: "Kein Tauwasser",
        suggestion: "Empfehlung",
        leverIterations: "die maximalen Iterationen erhöhen",
        leverMesh: "das Netz verfeinern (mehr Zeilen)",
        leverTolerance: "die Toleranz lockern (εU/εRH)",
        leverOr: " oder ",
        suggestTry: (levers: string) => `Versuchen Sie: ${levers} (Einstellungen → 2D-Solver).`,
        suggestAllTried: "Iterationen/Netz bereits erhöht und Toleranz gegenüber den Standardwerten gelockert, dennoch keine Konvergenz - prüfen Sie den Schichtaufbau auf ein extremes Verhältnis der Wärmeleitfähigkeiten zwischen den Materialien.",
        weight: "Gewicht",
        rw: "Rw (Schätzung Massegesetz)",
      },
      psychro: {
        title: "Psychrometrisches Diagramm",
        hint: "die Innenluft bis 80 % RH",
        hintValue: (temperature: string) => `die Innenluft bis 80 % RH: ${temperature}`,
        unavailable: "Verfügbar, sobald der Schichtaufbau berechnet ist.",
        insideAir: "Innenluft",
        humidityRatio: "Wasserdampfgehalt W",
        theta80: "θ bei 80 % RH",
        insideSurface: "Innenoberfläche",
        insideSurfaceMin: "Innenoberfläche, min.",
        noMouldRisk: "Oberfläche über θ80%: kein Schimmelrisiko",
        mouldRisk: "Oberfläche unter θ80%: Schimmelrisiko",
        chartLabel: "Psychrometrisches Diagramm: die Innenluft bei konstantem Wasserdampfgehalt bis 80 % RH abgekühlt",
        dryBulb: (unit: string) => `Trockenkugeltemperatur [${unit}]`,
        humidityRatioAxis: "Wasserdampfgehalt W [g/kg trockene Luft]",
        enthalpy: "Enthalpie h [kJ/kg]",
      },
    },
    tutorial: {
      startTitle: "Tutorial starten?",
      startMessage: "Eine kurze Vorführung der wichtigsten Funktionen an der mit Ubakus validierten Holzrahmenwand, bereits berechnet: Jeder Schritt wird für Sie ausgeführt, Sie fahren mit Weiter fort. Ihr Projekt wird beiseitegelegt und kommt am Ende unverändert zurück.",
      start: "Tutorial starten",
      preparing: "Tutorial wird vorbereitet…",
      running: "Das Tutorial läuft: Drücken Sie Weiter, oder Tutorial beenden, um es zu verlassen.",
      stepOf: (step: number, total: number) => `Schritt ${step} von ${total}`,
      showing: "Wird gezeigt…",
      stepDone: "✓ Erledigt",
      skip: "Tutorial beenden",
      next: "Weiter",
      startButton: "Starten",
      finish: "Fertig",
      demoName: "Demo-Wand",
      steps: {
        intro: {
          title: "Tutorial",
          text: "Eine kurze Vorführung der wichtigsten Funktionen an einem realen Beispiel - der mit Ubakus validierten Holzrahmenwand mit Installationsebene, bereits berechnet. Jeder Schritt wird für Sie ausgeführt und auf der Seite gezeigt: Drücken Sie Weiter, um fortzufahren, oder Tutorial beenden, um es zu verlassen. Ihr eigenes Projekt wird beiseitegelegt und kommt am Ende unverändert zurück.",
        },
        name: {
          title: "Projektname",
          text: "Das Projekt erhält einen Namen (optional): Er erscheint im Bericht.",
        },
        site: {
          title: "Standort und Klima",
          text: "Der Standort bestimmt das Klima der Tauwassernachweise: ein Klick auf die Karte, ein nach Namen gesuchter Ort oder einer der Orte auf der Karte. Hier wird der Standort des Beispiels beibehalten.",
        },
        element: {
          title: "Bauteil",
          text: "Wand: Das Beispiel ist eine Außenwand. Dach und erdberührtes Bauteil setzen andere Wärmeübergangswiderstände.",
        },
        analysis: {
          title: "Analyse",
          text: "Stationär: U-Wert, Tauwasser nach Glaser im Bemessungszustand und das monatliche Glaser-Verfahren mit dem Klima des Standorts. Quasi-dynamisch: Wärme und Feuchte Stunde für Stunde über mehrere Jahre des Standortklimas, in eigenen Registerkarten. Das Beispiel verwendet die stationäre Analyse.",
        },
        stratigraphy: {
          title: "Schichtaufbau",
          text: "Die Registerkarte Schichtaufbau: die Schichten der Wand, von außen nach innen, mit ihren Ständern.",
        },
        addMaterial: {
          title: "Ein Material hinzufügen",
          text: "Ein Material wird aus der Bibliothek auf die Zeichnung gezogen: Eine Lücke zeigt, wohin es kommt, und es wird dort eingefügt, wo es losgelassen wird.",
        },
        thickness: {
          title: "Eine Dicke ändern",
          text: "Ein Klick auf eine Schicht öffnet ihre Eigenschaften: Die Dicke wird eingegeben und mit Enter bestätigt.",
        },
        layerValues: {
          title: "Werte einer einzelnen Schicht",
          text: "Im selben Bereich wird das λ dieser Schicht geändert: Nur diese Schicht ändert sich, das Material in der Bibliothek bleibt unverändert.",
        },
        undo: {
          title: "Rückgängig",
          text: "Rückgängig (oder Strg+Z) nimmt die Änderungen eine nach der anderen zurück, bis das Beispiel wieder wie zuvor ist; ▾ listet die letzten Änderungen auf.",
        },
        results: {
          title: "Analyse und Ergebnisse",
          text: "Analyse und Ergebnisse: Das Beispiel ist bereits berechnet.",
        },
        checks: {
          title: "Die Nachweise",
          text: "U-Wert gegenüber dem Grenzwert, Tauwasser im Bauteil (Glaser), Oberflächenfeuchte innen (Schimmel) und das Ergebnis des monatlichen Glaser-Verfahrens; darunter Temperatur und Dampfdruck durch die Wand.",
        },
        monthly: {
          title: "Monatliches Glaser-Verfahren",
          text: "Ein Klick auf den Nachweis des monatlichen Glaser-Verfahrens: Tauwasser und Austrocknung Monat für Monat mit dem Klima des Standorts.",
        },
        report: {
          title: "Bericht",
          text: "Der Bericht: alles in einem Dokument, zum Drucken oder Speichern als PDF.",
        },
        done: {
          title: "Fertig",
          text: "Das ist der Hauptweg: Projekt und Standort, Schichtaufbau, Ergebnisse, Bericht. Drücken Sie Fertig: Ihr eigenes Projekt kommt so zurück, wie Sie es verlassen haben. Tutorial in der Seitenleiste zeigt es jederzeit erneut.",
        },
      },
    },
    report: {
      printAndContents: "Druck und Inhalt des Berichts",
      print: "Drucken…",
      printInfo: (pages: number, bw: boolean) => `${pages} ${pages > 1 ? "Seiten" : "Seite"} · A4 · ${bw ? "schwarz-weiß" : "farbig"}`,
      contents: "Inhalt",
      groups: {
        inputs: "Eingaben",
        results: "Ergebnisse",
      },
      all: "alle",
      none: "keine",
      notAvailable: "Für die aktuelle Berechnung nicht verfügbar",
      fieldShown: "Angezeigtes Feld",
      sections: {
        project: "Projekt",
        site: "Standort und Klima",
        conditions: "Bemessungsbedingungen",
        drawing: "Zeichnung des Schichtaufbaus",
        layers: "Schichtentabelle",
        checks: "Nachweise",
        uvalue: "Berechnung des U-Werts",
        nodes: "Werte an den Grenzflächen",
        temp: "Temperaturverlauf",
        profiles2d: "Vertikale 2D-Profile",
        compare: "Vergleich 1D / 2D",
        climate: "Referenzjahr des Standorts",
      },
      relativeHumidity: "Relative Luftfeuchte",
      untitledProject: "Unbenanntes Projekt",
      notes: "Notizen",
      date: "Datum",
      calculation2D: "2D-Netz (stationär, Finite Volumen)",
      location: "Ort",
      climateData: "Klimadaten",
      elevation: "Höhe",
      topSide: (side: string) => `Obere Seite (${side}): Temperatur / RH / Wärmeübergangswiderstand`,
      bottomSide: (side: string) => `Untere Seite (${side}): Temperatur / RH / Wärmeübergangswiderstand`,
      materialInsideOutside: "Material (innen → außen)",
      check: "Nachweis",
      value: "Wert",
      reference: "Referenz",
      outcome: "Ergebnis",
      satisfied: "ERFÜLLT",
      notSatisfied: "NICHT ERFÜLLT",
      overallOutcome: "Gesamtergebnis",
      allSatisfied: "ALLE NACHWEISE ERFÜLLT",
      notAllSatisfied: "NICHT ALLE NACHWEISE ERFÜLLT",
      uFraming: "U mit den Ständern (kombiniertes Verfahren EN ISO 6946)",
      interface: "Grenzfläche",
      insideNode: (n: number) => `${n} (innen)`,
      outsideNode: (n: number) => `${n} (außen)`,
      verdict: "Ergebnis",
      notVerified: (failures: string) => `nicht erfüllt: ${failures}`,
      method: "Verfahren",
      methodValue: (preset: string, start: string) => `${preset}; Beginn des Zyklus: ${start}`,
      monthlySection: (u: string, maxAccumulated: string, left: string, fRsi: string, fRsiMin: string) => `U ${u} W/(m²K) · max. angesammelt ${maxAccumulated} · am Ende verbleibend ${left} · f_Rsi ${fRsi} / ${fRsiMin}`,
      glaserMonthShown: "Glaser-Diagramm (auf der Seite gezeigter Monat)",
      quantity: "Größe",
      simplified1D: "Vereinfacht 1D",
      mesh2D: "2D-Netz",
      insideSurfaceTemperature: "Innere Oberflächentemperatur",
      insideSurfaceRh: "RH an der inneren Oberfläche",
      min: "(min.)",
      max: "(max.)",
      empty: "Wählen Sie in der Inhaltsliste die Abschnitte aus, die aufgenommen werden sollen.",
      disclaimer1: "Alle Berechnungen müssen vor der Verwendung vom verantwortlichen Planer geprüft und freigegeben werden.",
      disclaimer2: "Die Materialwerte stammen aus den Produktzertifikaten oder technischen Datenblättern; die Verwendung numerischer Verfahren (2D-Netz-Modus) und die Wahl der Materialien und Produkte des Schichtaufbaus liegen in der Verantwortung des Planers.",
      pageOf: (page: number, pages: number) => `Seite ${page} von ${pages}`,
      reportPages: "Berichtsseiten",
      viewer: "Berichtsanzeige",
      previousPage: "Vorherige Seite",
      nextPage: "Nächste Seite",
      page: "Seite",
      zoomOut: "Verkleinern",
      zoomIn: "Vergrößern",
      zoom: "Zoom",
      fitWidthPct: (percent: string) => `Seitenbreite (${percent} %)`,
      fitPagePct: (percent: string) => `Ganze Seite (${percent} %)`,
      fitWidth: "Seitenbreite",
      fitPage: "Ganze Seite",
      pageLayout: "Seitenlayout",
      layouts: {
        continuous: "Fortlaufende Seiten",
        single: "Einzelseite",
        two: "Zwei Seiten nebeneinander",
      },
      blackAndWhite: "Schwarz-Weiß",
      blackAndWhiteOn: "Schwarz-Weiß (aktiv: auch beim Drucken)",
      bwShort: "S/W",
    },
  },

  // Texts of the Design System V2 components (src/components/ds2).
  designSystem: {
    openInNewLabel: "Öffnet in einem neuen Tab",
    breadcrumbLabel: "Brotkrümelnavigation",
    openMenuLabel: "Menü öffnen",
    closeMenuLabel: "Menü schließen",
    closeLabel: "Schließen",
    accountMenuLabel: "Kontomenü",
    paginationOfLabel: "von",
    previousPageLabel: "Vorherige Seite",
    nextPageLabel: "Nächste Seite",
    selectPlaceholder: "Auswählen...",
    dropFilesLabel: "Hochladen!",
    dropZoneLabel: "Ablagebereich für Dateien",
    dropRejectedLabel: "Dieser Dateityp wird nicht unterstützt",
    searchPlaceholder: "Suchen",
    clearSearchLabel: "Suche löschen",
    toggleSubmenuLabel: (text) => `${text} umschalten`,
    contextMenuTriggerLabel: "Aktionsmenü öffnen",
    increaseLabel: (value: number) => `${value} Erhöhen`,
    decreaseLabel: (value: number) => `${value} verringern`,
    datePicker: {
      clearButton: "Datum löschen",
      nextMonth: "Nächster Monat",
      placeholder: "Datum wählen",
      previousMonth: "Vorheriger Monat",
      selectMonth: "Monat wählen",
      selectYear: "Jahr wählen",
      calendarLabel: "Datum auswählen",
    },
    cancelLabel: "Abbrechen",
    okLabel: "OK",
    removeLabel: "Entfernen",
    requiredFieldLabel: "Dieses Feld ist erforderlich.",
    fieldTooSmallLabel: "Der Wert dieses Felds ist zu niedrig.",
    fieldTooLargeLabel: "Der Wert dieses Felds ist zu hoch.",
    fieldInvalidFormatLabel: "Der Wert dieses Felds hat ein ungültiges Format.",
    fieldInvalidValueLabel: "Der Wert dieses Felds ist ungültig.",
    browseOrDropFileLabel: "Datei hierher ziehen oder klicken, um sie auszuwählen",
    browseOrDropImageLabel: "Bild hierher ziehen oder klicken, um es hochzuladen",
    removeImageLabel: "Bild entfernen",
    resetImageLabel: "Bild zurücksetzen",
    translationsLabel: "Übersetzungen",
    increaseOrderLabel: "Reihenfolge erhöhen",
    decreaseOrderLabel: "Reihenfolge verringern",
    stagingLabel: "Testumgebung",
    unexpectedErrorLabel: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    saveLabel: "Speichern",
    loadingLabel: "Wird geladen",
    browseFilesLabel: "Dateien durchsuchen",
    noOptionsLabel: "Keine Optionen",
    quantityOfLabel: (label: string) => `Menge von ${label}`,
    chooseOrDropFileLabel: "Wählen Sie eine Datei oder ziehen Sie sie hierher, um sie hochzuladen.",
    stepOfLabel: (current: number, total: number) => `Schritt ${current} von ${total}`,
    stepCompletedLabel: "abgeschlossen",
    backToStepLabel: (step: number) => `Zurück zu Schritt ${step}`,
  },
};

export default de;
